import {createClient} from '@supabase/supabase-js'
import { getCurrentYear, createDate, dateToISOString, getYearStartFromDate, getISOTimestamp, toLocalDateString, parseDateString } from './utils/dateUtils.js'
import { generateRecordFingerprint, saveImportHistory } from './utils/recordFingerprintManager.js'

// Support both Vite (import.meta.env) and Node.js (process.env) contexts
const supabaseUrl = typeof import.meta.env !== 'undefined'
  ? import.meta.env.VITE_SUPABASE_URL
  : process.env.VITE_SUPABASE_URL

const supabaseKey = typeof import.meta.env !== 'undefined'
  ? import.meta.env.VITE_SUPABASE_ANON_KEY
  : process.env.VITE_SUPABASE_ANON_KEY

export const supabase = createClient(supabaseUrl, supabaseKey)

/**
 * UUID validation regex for filtering legacy data
 * @constant {RegExp}
 */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Price Multiplier Utilities
 * Converts between price_modifier_percent (legacy) and price_multiplier (current)
 */

/**
 * Convert price_multiplier to discount percentage (for display/backward compatibility)
 * @param {number} multiplier - Price multiplier (e.g., 1.60, 1.00, 0.75, 0.00)
 * @returns {number} Discount percentage (e.g., 0 for base, 25 for 25% off, 100 for free)
 *
 * @example
 * multiplierToDiscountPercent(1.60) // 0 (premium, no discount)
 * multiplierToDiscountPercent(1.00) // 0 (base price, no discount)
 * multiplierToDiscountPercent(0.75) // 25 (25% discount)
 * multiplierToDiscountPercent(0.00) // 100 (free, 100% discount)
 */
export const multiplierToDiscountPercent = (multiplier) => {
  if (multiplier === 0) return 100        // Free = 100% discount
  if (multiplier >= 1) return 0           // Premium/Base = no discount
  return Math.round((1 - multiplier) * 100) // Discount = (1 - multiplier) × 100
}

/**
 * Convert discount percentage to price_multiplier
 * @param {number} discountPercent - Discount percentage (0-100)
 * @returns {number} Price multiplier
 *
 * @example
 * discountPercentToMultiplier(0)   // 1.00 (no discount = base price)
 * discountPercentToMultiplier(25)  // 0.75 (25% off)
 * discountPercentToMultiplier(100) // 0.00 (free)
 */
export const discountPercentToMultiplier = (discountPercent) => {
  if (discountPercent === 100) return 0
  return 1 - (discountPercent / 100)
}

/**
 * Unified pagination utility for Supabase queries
 * Handles paginated fetching of large result sets to bypass the 1000-record limit
 *
 * @param {Object} queryBuilder - Supabase query builder with filters already applied
 * @param {Object} options - Pagination configuration
 * @param {number} options.pageSize - Records per page (default: 1000, max supported by Supabase)
 * @param {number} options.maxPages - Safety limit to prevent infinite loops (default: 100)
 * @param {string} options.orderBy - Column to order by (default: null, uses existing order)
 * @param {boolean} options.ascending - Sort direction (default: true)
 * @returns {Promise<Array>} All records across all pages
 *
 * @example
 * // Basic usage with existing query
 * const query = supabase.from('sales').select('*').eq('status', 'delivered')
 * const allSales = await paginateQuery(query, { orderBy: 'date' })
 *
 * @example
 * // With custom page size and ordering
 * const query = supabase.from('import_staging_sales').select('*').eq('session_id', sessionId)
 * const records = await paginateQuery(query, { pageSize: 500, orderBy: 'row_number' })
 */
export const paginateQuery = async (queryBuilder, options = {}) => {
  const {
    pageSize = 1000,
    maxPages = 100,
    orderBy = null,
    ascending = true
  } = options

  // Validate page size
  if (pageSize > 1000) {
    console.warn('[paginateQuery] Page size exceeds Supabase limit of 1000, using 1000')
  }
  const effectivePageSize = Math.min(pageSize, 1000)

  const allRecords = []
  let page = 0
  let hasMore = true

  while (hasMore && page < maxPages) {
    // Clone the query builder and apply ordering if specified
    let query = queryBuilder
    if (orderBy) {
      query = query.order(orderBy, { ascending })
    }

    // Apply range for pagination
    const from = page * effectivePageSize
    const to = from + effectivePageSize - 1
    query = query.range(from, to)

    const { data, error } = await query

    if (error) {
      console.error(`[paginateQuery] Error on page ${page + 1}:`, error)
      throw error
    }

    if (!data || data.length === 0) {
      hasMore = false
      break
    }

    allRecords.push(...data)
    hasMore = data.length === effectivePageSize
    page++
  }

  // Log warning if we hit the safety limit
  if (page >= maxPages && hasMore) {
    console.warn(`[paginateQuery] Hit safety limit of ${maxPages} pages (${allRecords.length} records). There may be more records available.`)
  }

  return allRecords
}

// Customer CRUD operations

/**
 * Helper function to determine the default branch name based on customer type
 * @param {string} typeId - The customer type ID
 * @returns {Promise<string>} - The default branch name ('Personal' for direct, 'Main' for others)
 */
const determineDefaultBranchName = async (typeId) => {
  const { data, error } = await supabase
    .from('customer_types')
    .select('name')
    .eq('id', typeId)
    .single()

  if (error) throw error

  // Direct customers get 'Personal', all others get 'Main'
  return data.name.toLowerCase() === 'direct' ? 'Personal' : 'Main'
}

/**
 * Helper function to determine the default branch type based on customer type
 * @param {string} typeId - The customer type ID
 * @returns {Promise<string>} - The default branch type ('personal' for direct, 'branch' for others)
 */
const determineDefaultBranchType = async (typeId) => {
  const { data, error } = await supabase
    .from('customer_types')
    .select('name')
    .eq('id', typeId)
    .single()

  if (error) throw error

  // Direct customers get 'personal', all others get 'branch'
  return data.name.toLowerCase() === 'direct' ? 'personal' : 'branch'
}

export const createCustomer = async (customerData) => {
  const insertData = {
    name: customerData.companyName,
    email: customerData.email,
    phone: customerData.phone || null,
    payment_terms: customerData.paymentTerms,
    payment_method: customerData.paymentMethod || null,
    price_tier_id: customerData.priceTierId || null, // US-014: Customer pricing tier
    employee_discount_eligible: customerData.employeeDiscountEligible || false, // US-014: Employee discount
    fruiting_blocks_eligible: customerData.fruitingBlocksEligible || false,
    growing_materials_eligible: customerData.growingMaterialsEligible || false,
    transmission: customerData.transmission || 'delivery',
    reference_code: customerData.referenceCode || null
  }

  // Note: 'type' field was removed from customers table in migration 20250114000000
  // Customer type is now stored at the branch level in the branches table

  // Step 1: Create customer record
  const { data: customer, error: customerError } = await supabase
    .from('customers')
    .insert([insertData])
    .select()
    .single()

  if (customerError) {
    console.error('[createCustomer] Insert failed:', customerError.message, customerError.details)
    throw customerError
  }

  // Validate customer object and ID
  if (!customer || !customer.id) {
    throw new Error('Failed to create customer: Invalid customer data returned')
  }

  // Note: Branches are now created separately by CustomerForm after customer creation
  // This allows for proper error handling and user control over branch data
  // No automatic default branch is created here

  return customer
}

export const fetchCustomers = async () => {
  const { data, error } = await supabase
    .from('customers')
    .select(`
      *,
      branches:branches(
        *,
        customer_type:customer_types!branches_type_id_fkey(id, name, icon_name, default_price_tier_id)
      )
    `)
    .eq('active', true)
    .order('name')

  if (error) throw error
  return (data || []).filter(Boolean)
}

/**
 * Fetch sale counts per customer for sorting by volume.
 * Returns a map of customer_id -> number of sales.
 */
export const fetchCustomerSaleCounts = async () => {
  const { data, error } = await supabase
    .from('sales')
    .select('customer_id')

  if (error) throw error

  const counts = {}
  data.forEach(sale => {
    counts[sale.customer_id] = (counts[sale.customer_id] || 0) + 1
  })
  return counts
}

export const fetchCustomer = async (customerId) => {
  const { data, error } = await supabase
    .from('customers')
    .select(`
      *,
      branches:branches(*)
    `)
    .eq('id', customerId)
    .single()

  if (error) throw error
  return data
}

export const fetchArchivedCustomers = async () => {
  const { data, error } = await supabase
    .from('customers')
    .select(`
      *,
      branches:branches(*)
    `)
    .eq('active', false)
    .order('name')

  if (error) throw error
  return data
}

export const updateCustomer = async (id, updates) => {
  const updateData = {}

  // Only include fields that are actually provided
  if (updates.companyName !== undefined) {
    updateData.name = updates.companyName
  }
  if (updates.email !== undefined) {
    updateData.email = updates.email
  }
  if (updates.phone !== undefined) {
    updateData.phone = updates.phone || null
  }
  if (updates.paymentTerms !== undefined) {
    updateData.payment_terms = updates.paymentTerms
  }
  if (updates.paymentMethod !== undefined) {
    updateData.payment_method = updates.paymentMethod || null
  }
  // US-014: Customer pricing tier
  if (updates.priceTierId !== undefined) {
    updateData.price_tier_id = updates.priceTierId
  }
  // US-014: Employee discount eligibility
  if (updates.employeeDiscountEligible !== undefined) {
    updateData.employee_discount_eligible = updates.employeeDiscountEligible
  }

  if (updates.fruitingBlocksEligible !== undefined) {
    updateData.fruiting_blocks_eligible = updates.fruitingBlocksEligible
  }

  if (updates.growingMaterialsEligible !== undefined) {
    updateData.growing_materials_eligible = updates.growingMaterialsEligible
  }

  if (updates.transmission !== undefined) {
    updateData.transmission = updates.transmission
  }

  if (updates.referenceCode !== undefined) {
    updateData.reference_code = updates.referenceCode
  }

  // Customer logo fields
  if (updates.logoFilePath !== undefined) {
    updateData.logo_file_path = updates.logoFilePath
  }
  if (updates.logoFileName !== undefined) {
    updateData.logo_file_name = updates.logoFileName
  }
  if (updates.logoUploadedAt !== undefined) {
    updateData.logo_uploaded_at = updates.logoUploadedAt
  }

  // Support both legacy 'type' field and new 'type_id' field
  if (updates.type_id !== undefined) {
    updateData.type_id = updates.type_id
  } else if (updates.customerType !== undefined) {
    updateData.type = updates.customerType
  }

  const { data, error } = await supabase
    .from('customers')
    .update(updateData)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return data
}

export const archiveCustomer = async (id) => {
  const { data, error } = await supabase
    .from('customers')
    .update({ active: false })
    .eq('id', id)
    .select()
    .single()
  
  if (error) throw error
  return data
}

export const unarchiveCustomer = async (id) => {
  const { data, error } = await supabase
    .from('customers')
    .update({ active: true })
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return data
}

export const deleteCustomer = async (id) => {
  const { error } = await supabase
    .from('customers')
    .delete()
    .eq('id', id)

  if (error) throw error
  return true
}

// Branch management
export const createCustomerBranch = async (branchData) => {
  const { data, error} = await supabase
    .from('branches')
    .insert([{
      customer_id: branchData.customer_id,
      name: branchData.branchName,
      address: branchData.address,
      address_line_1: branchData.address_line_1 || null,
      address_line_2: branchData.address_line_2 || null,
      address_line_3: branchData.address_line_3 || null,
      city: branchData.city || null,
      postcode: branchData.postcode || null,
      country: branchData.country || 'United Kingdom',
      phone: branchData.phone || null,
      email: branchData.email || null,
      branch_type: branchData.branch_type || 'branch',
      type_id: branchData.type_id,  // Required: branches.type_id has NOT NULL constraint
      reference_code: branchData.reference_code || null
    }])
    .select()
    .single()

  if (error) throw error
  return data
}

export const fetchCustomerBranches = async (customerId) => {
  const { data, error } = await supabase
    .from('branches')
    .select('*')
    .eq('customer_id', customerId)
    .neq('branch_type', 'company')  // Filter out only company information branches
    .order('name')

  if (error) throw error
  return data
}

/**
 * Get the default branch for a customer (oldest branch by creation date)
 * @param {string} customerId - The customer ID
 * @returns {Promise<Object>} - The default branch object
 */
export const getCustomerDefaultBranch = async (customerId) => {
  const { data, error } = await supabase
    .from('branches')
    .select('*')
    .eq('customer_id', customerId)
    .order('name')
    .limit(1)
    .single()

  if (error) throw error
  return data
}

export const updateCustomerBranch = async (id, updates) => {
  const updateData = {
    name: updates.branchName,
    address: updates.address,
    address_line_1: updates.address_line_1 || null,
    address_line_2: updates.address_line_2 || null,
    address_line_3: updates.address_line_3 || null,
    city: updates.city || null,
    postcode: updates.postcode || null,
    country: updates.country || 'United Kingdom',
    phone: updates.phone || null,
    email: updates.email || null,
    branch_type: updates.branch_type || 'branch'
  }

  // Only include type_id if provided (optional for updates)
  if (updates.type_id) {
    updateData.type_id = updates.type_id
  }

  if (updates.reference_code !== undefined) {
    updateData.reference_code = updates.reference_code
  }

  const { data, error } = await supabase
    .from('branches')
    .update(updateData)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return data
}

/**
 * Delete a customer branch with validation
 * Prevents deletion if:
 * - Branch has associated sales or invoices
 * - Branch is the last branch for the customer
 * @param {string} id - Branch ID to delete
 * @throws {Error} If validation fails or deletion not allowed
 */
export const deleteCustomerBranch = async (id) => {
  // Step 1: Get branch details to find customer_id
  const { data: branch, error: branchFetchError } = await supabase
    .from('branches')
    .select('id, customer_id, name')
    .eq('id', id)
    .single()

  if (branchFetchError) throw branchFetchError
  if (!branch) throw new Error('Branch not found')

  // Step 2: Check for related sales
  const { count: salesCount, error: salesError } = await supabase
    .from('sales')
    .select('id', { count: 'exact', head: true })
    .eq('branch_id', id)

  if (salesError) throw salesError

  // Step 3: Check for related invoices
  const { count: invoicesCount, error: invoicesError } = await supabase
    .from('invoices')
    .select('id', { count: 'exact', head: true })
    .eq('branch_id', id)

  if (invoicesError) throw invoicesError

  // Step 4: Prevent deletion if branch has historical data
  if (salesCount > 0 || invoicesCount > 0) {
    throw new Error(
      `Cannot delete branch: ${salesCount} sale${salesCount !== 1 ? 's' : ''} and ${invoicesCount} invoice${invoicesCount !== 1 ? 's' : ''} are linked to this branch. Consider archiving the customer instead.`
    )
  }

  // Step 5: Check if this is the last branch for the customer
  const { count: branchCount, error: branchCountError } = await supabase
    .from('branches')
    .select('id', { count: 'exact', head: true })
    .eq('customer_id', branch.customer_id)

  if (branchCountError) throw branchCountError

  if (branchCount <= 1) {
    throw new Error('Cannot delete the last branch. Every customer must have at least one branch.')
  }

  // Step 6: Safe to delete
  const { error: deleteError } = await supabase
    .from('branches')
    .delete()
    .eq('id', id)

  if (deleteError) throw deleteError
}

/**
 * Upgrade a customer to multi-location type by renaming default "Main" branch to "Head Office"
 * Used when customer type changes to multi_location or enterprise
 * @param {string} customerId - Customer ID to upgrade
 * @returns {Promise<Object>} Updated branch object or null if no changes needed
 */
export const upgradeCustomerToMultiLocation = async (customerId) => {
  // Step 1: Fetch all branches for the customer
  const { data: branches, error: fetchError } = await supabase
    .from('branches')
    .select('*')
    .eq('customer_id', customerId)

  if (fetchError) throw fetchError

  // Step 2: Check if customer only has a single "Main" branch
  if (branches.length === 1 && branches[0].name === 'Main') {
    // Step 3: Rename to "Head Office"
    const { data: updatedBranch, error: updateError } = await supabase
      .from('branches')
      .update({
        name: 'Head Office',
        branch_type: 'branch'
      })
      .eq('id', branches[0].id)
      .select()
      .single()

    if (updateError) throw updateError

    return updatedBranch
  }

  // No changes needed (customer already has multiple branches or no "Main" branch)
  return null
}

// Customer Type Management
export const fetchCustomerTypes = async (activeOnly = true) => {
  let query = supabase
    .from('customer_types')
    .select('*')
    .order('display_order', { ascending: true })

  if (activeOnly) {
    query = query.eq('active', true)
  }

  const { data, error } = await query
  if (error) throw error
  return data
}

export const createCustomerType = async (typeData) => {
  // Validate name is not empty
  if (!typeData.name || typeData.name.trim() === '') {
    throw new Error('Customer type name cannot be empty')
  }

  const { data, error } = await supabase
    .from('customer_types')
    .insert([{
      name: typeData.name.trim(),
      description: typeData.description || null,
      icon_name: typeData.icon_name || null,
      display_order: typeData.display_order || 999
    }])
    .select()
    .single()

  if (error) {
    // Handle duplicate name error
    if (error.code === '23505') {
      throw new Error('A customer type with this name already exists')
    }
    throw error
  }
  return data
}

export const updateCustomerType = async (id, updates) => {
  const updateData = {}

  if (updates.name !== undefined) {
    if (updates.name.trim() === '') {
      throw new Error('Customer type name cannot be empty')
    }
    updateData.name = updates.name.trim()
  }

  if (updates.description !== undefined) {
    updateData.description = updates.description
  }

  if (updates.icon_name !== undefined) {
    updateData.icon_name = updates.icon_name
  }

  if (updates.display_order !== undefined) {
    updateData.display_order = updates.display_order
  }

  if (updates.default_price_tier_id !== undefined) {
    updateData.default_price_tier_id = updates.default_price_tier_id
  }

  const { data, error } = await supabase
    .from('customer_types')
    .update(updateData)
    .eq('id', id)
    .select()
    .single()

  if (error) {
    // Handle duplicate name error
    if (error.code === '23505') {
      throw new Error('A customer type with this name already exists')
    }
    throw error
  }
  return data
}

export const softDeleteCustomerType = async (id) => {
  // Check if type is in use by customers
  const customerCount = await getBranchCountByType(id)

  // Soft delete by setting active=false
  const { data, error } = await supabase
    .from('customer_types')
    .update({ active: false })
    .eq('id', id)
    .select()
    .single()

  if (error) throw error

  return {
    success: true,
    customerCount: customerCount,
    data: data
  }
}

export const reactivateCustomerType = async (id) => {
  const { data, error } = await supabase
    .from('customer_types')
    .update({ active: true })
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return data
}

export const getBranchCountByType = async (typeId) => {
  const { count, error } = await supabase
    .from('branches')
    .select('*', { count: 'exact', head: true })
    .eq('type_id', typeId)

  if (error) throw error
  return count || 0
}

// ============================================
// STRAIN FAMILIES
// ============================================

export const fetchStrainFamilies = async (activeOnly = true) => {
  let query = supabase
    .from('strain_families')
    .select('id, name, code, display_order, active, product_family_id, product_families(id, name, reference_code)')
    .order('display_order', { ascending: true })

  if (activeOnly) {
    query = query.eq('active', true)
  }

  const { data, error } = await query
  if (error) throw error
  return data
}

export const createStrainFamily = async ({ name, code, display_order }) => {
  if (!name || name.trim() === '') {
    throw new Error('Strain family name cannot be empty')
  }
  if (!code || code.trim() === '') {
    throw new Error('Strain family code cannot be empty')
  }

  const trimmedName = name.trim()
  const trimmedCode = code.trim()

  // Check for soft-deleted entry with same name or code and reactivate it
  const { data: existing } = await supabase
    .from('strain_families')
    .select('id')
    .eq('active', false)
    .or(`name.eq.${trimmedName},code.eq.${trimmedCode}`)
    .limit(1)
    .single()

  if (existing) {
    const { data, error } = await supabase
      .from('strain_families')
      .update({ name: trimmedName, code: trimmedCode, active: true, display_order: display_order || 999 })
      .eq('id', existing.id)
      .select()
      .single()

    if (error) throw error
    return data
  }

  const { data, error } = await supabase
    .from('strain_families')
    .insert([{
      name: trimmedName,
      code: trimmedCode,
      display_order: display_order || 999
    }])
    .select()
    .single()

  if (error) {
    if (error.code === '23505') {
      throw new Error('A strain family with this name or code already exists')
    }
    throw error
  }
  return data
}

export const updateStrainFamily = async (id, updates) => {
  const updateData = {}

  if (updates.name !== undefined) {
    if (updates.name.trim() === '') {
      throw new Error('Strain family name cannot be empty')
    }
    updateData.name = updates.name.trim()
  }

  if (updates.code !== undefined) {
    if (updates.code.trim() === '') {
      throw new Error('Strain family code cannot be empty')
    }
    updateData.code = updates.code.trim()
  }

  if (updates.display_order !== undefined) {
    updateData.display_order = updates.display_order
  }

  const { data, error } = await supabase
    .from('strain_families')
    .update(updateData)
    .eq('id', id)
    .select()
    .single()

  if (error) {
    if (error.code === '23505') {
      throw new Error('A strain family with this name or code already exists')
    }
    throw error
  }
  return data
}

export const softDeleteStrainFamily = async (id) => {
  const { data, error } = await supabase
    .from('strain_families')
    .update({ active: false })
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return { success: true, data }
}

export const getProductCountByStrainFamily = async (familyId) => {
  const { count, error } = await supabase
    .from('products')
    .select('*', { count: 'exact', head: true })
    .eq('strain_family_id', familyId)

  if (error) throw error
  return count || 0
}

// ============================================
// PRICING TIERS (US-014)
// ============================================

/**
 * Fetch all price tiers
 * @returns {Promise<Array>} Array of price tier objects
 */
export const fetchPriceTiers = async () => {
  const { data, error } = await supabase
    .from('price_tiers')
    .select('*')
    .order('sort_order', { ascending: true })

  if (error) throw error
  return data
}

/**
 * Get a single price tier by ID
 * @param {string} tierId - UUID of the price tier
 * @returns {Promise<Object>} Price tier object
 */
export const fetchPriceTier = async (tierId) => {
  const { data, error } = await supabase
    .from('price_tiers')
    .select('*')
    .eq('id', tierId)
    .single()

  if (error) throw error
  return data
}

/**
 * Get a price tier by name (retail, commercial, wholesale)
 * @param {string} tierName - Name of the tier
 * @returns {Promise<Object>} Price tier object
 */
export const fetchPriceTierByName = async (tierName) => {
  const { data, error } = await supabase
    .from('price_tiers')
    .select('*')
    .eq('name', tierName.toLowerCase())
    .single()

  if (error) throw error
  return data
}

/**
 * Update a price tier's multiplier or display properties
 * @param {string} tierId - UUID of the price tier
 * @param {Object} updates - Fields to update (multiplier, display_name, description)
 * @returns {Promise<Object>} Updated price tier
 */
export const updatePriceTier = async (tierId, updates) => {
  const updateData = {}

  if (updates.multiplier !== undefined) {
    updateData.multiplier = updates.multiplier
  }
  if (updates.display_name !== undefined) {
    updateData.display_name = updates.display_name
  }
  if (updates.description !== undefined) {
    updateData.description = updates.description
  }

  updateData.updated_at = new Date().toISOString()

  const { data, error } = await supabase
    .from('price_tiers')
    .update(updateData)
    .eq('id', tierId)
    .select()
    .single()

  if (error) throw error
  return data
}

// ============================================
// WHOLESALE THRESHOLDS (US-014)
// ============================================

/**
 * Fetch all wholesale thresholds
 * @returns {Promise<Array>} Array of threshold objects with product details
 */
/**
 * Read the singleton pricing_settings row (creates it on first call if missing).
 */
export const fetchPricingSettings = async () => {
  const { data, error } = await supabase
    .from('pricing_settings')
    .select('*')
    .eq('id', true)
    .maybeSingle()
  if (error) throw error
  return data || { id: true, wholesale_upgrade_customer_type_ids: [] }
}

/**
 * Update which customer types qualify for wholesale-threshold upgrade.
 * @param {string[]} customerTypeIds
 */
export const updateWholesaleUpgradeCustomerTypes = async (customerTypeIds) => {
  const { data, error } = await supabase
    .from('pricing_settings')
    .update({
      wholesale_upgrade_customer_type_ids: customerTypeIds,
      updated_at: new Date().toISOString(),
    })
    .eq('id', true)
    .select()
    .single()
  if (error) throw error
  return data
}

export const fetchWholesaleThresholds = async () => {
  const { data, error } = await supabase
    .from('wholesale_thresholds')
    .select(`
      *,
      products (id, strain)
    `)
    .order('created_at', { ascending: true })

  if (error) throw error
  return data
}

/**
 * Get wholesale threshold for a specific product
 * @param {string} productId - UUID of the product
 * @returns {Promise<Object|null>} Threshold object or null if none exists
 */
export const fetchWholesaleThresholdByProduct = async (productId) => {
  const { data, error } = await supabase
    .from('wholesale_thresholds')
    .select('*')
    .eq('product_id', productId)
    .maybeSingle()

  if (error) throw error
  return data
}

/**
 * Create a wholesale threshold for a product
 * @param {Object} thresholdData - { product_id, min_quantity_kg }
 * @returns {Promise<Object>} Created threshold
 */
export const createWholesaleThreshold = async (thresholdData) => {
  if (!thresholdData.product_id) {
    throw new Error('Product ID is required')
  }
  if (!thresholdData.min_quantity_kg || thresholdData.min_quantity_kg <= 0) {
    throw new Error('Minimum quantity must be greater than 0')
  }

  const { data, error } = await supabase
    .from('wholesale_thresholds')
    .insert([{
      product_id: thresholdData.product_id,
      min_quantity_kg: thresholdData.min_quantity_kg
    }])
    .select()
    .single()

  if (error) {
    if (error.code === '23505') {
      throw new Error('A wholesale threshold already exists for this product')
    }
    throw error
  }
  return data
}

/**
 * Update a wholesale threshold
 * @param {string} thresholdId - UUID of the threshold
 * @param {Object} updates - { min_quantity_kg }
 * @returns {Promise<Object>} Updated threshold
 */
export const updateWholesaleThreshold = async (thresholdId, updates) => {
  if (updates.min_quantity_kg !== undefined && updates.min_quantity_kg <= 0) {
    throw new Error('Minimum quantity must be greater than 0')
  }

  const updateData = {
    ...updates,
    updated_at: new Date().toISOString()
  }

  const { data, error } = await supabase
    .from('wholesale_thresholds')
    .update(updateData)
    .eq('id', thresholdId)
    .select()
    .single()

  if (error) throw error
  return data
}

/**
 * Delete a wholesale threshold
 * @param {string} thresholdId - UUID of the threshold
 * @returns {Promise<void>}
 */
export const deleteWholesaleThreshold = async (thresholdId) => {
  const { error } = await supabase
    .from('wholesale_thresholds')
    .delete()
    .eq('id', thresholdId)

  if (error) throw error
}

// ============================================
// PRODUCT PRICES CATALOGUE (US-014)
// ============================================

/**
 * Fetch all product prices from the catalogue
 * @param {Object} options - Filter options
 * @param {string} options.productId - Filter by product ID
 * @param {string} options.productTypeId - Filter by product type (grade) ID
 * @param {string} options.priceTierId - Filter by price tier ID
 * @returns {Promise<Array>} Array of product prices with joined product, type, and tier details
 */
export const fetchProductPrices = async (options = {}) => {
  let query = supabase
    .from('product_prices')
    .select(`
      *,
      product:products (id, strain, base_price_per_kg, active, limited_availability),
      product_type:product_types (id, name, price_multiplier, active),
      price_tier:price_tiers (id, name, display_name, multiplier, sort_order)
    `)
    .order('product_id')
    .order('product_type_id')
    .order('price_tier_id')

  if (options.productId) {
    query = query.eq('product_id', options.productId)
  }
  if (options.productTypeId) {
    query = query.eq('product_type_id', options.productTypeId)
  }
  if (options.priceTierId) {
    query = query.eq('price_tier_id', options.priceTierId)
  }

  const { data, error } = await query

  if (error) throw error
  return data
}

/**
 * Fetch a single catalogue price for a specific product × grade × tier combination
 * @param {string} productId - Product UUID
 * @param {string} productTypeId - Product type (grade) UUID
 * @param {string} priceTierId - Price tier UUID
 * @returns {Promise<Object|null>} Price entry or null if not found
 */
export const fetchProductPrice = async (productId, productTypeId, priceTierId) => {
  let query = supabase
    .from('product_prices')
    .select('*')
    .eq('product_id', productId)
    .eq('price_tier_id', priceTierId)

  if (productTypeId) {
    query = query.eq('product_type_id', productTypeId)
  } else {
    query = query.is('product_type_id', null)
  }

  const { data, error } = await query.maybeSingle()

  if (error) throw error
  return data
}

/**
 * Update a product price in the catalogue
 * @param {string} priceId - UUID of the product_prices entry
 * @param {Object} updates - { price_per_kg }
 * @returns {Promise<Object>} Updated price entry
 */
export const updateProductPrice = async (priceId, updates) => {
  if (updates.price_per_kg !== undefined && updates.price_per_kg < 0) {
    throw new Error('Price must be 0 or greater')
  }

  const updateData = {
    price_per_kg: updates.price_per_kg,
    updated_at: new Date().toISOString()
  }

  const { data, error } = await supabase
    .from('product_prices')
    .update(updateData)
    .eq('id', priceId)
    .select()
    .single()

  if (error) throw error
  return data
}

/**
 * Bulk update multiple product prices
 * @param {Array} updates - Array of { id, price_per_kg }
 * @returns {Promise<Array>} Updated price entries
 */
export const bulkUpdateProductPrices = async (updates) => {
  const results = []
  for (const update of updates) {
    const result = await updateProductPrice(update.id, { price_per_kg: update.price_per_kg })
    results.push(result)
  }
  return results
}

/**
 * Recalculate catalogue prices from base price × grade multiplier × tier multiplier
 * Useful for resetting prices after multiplier changes
 * @param {Object} options - Filter options (productId, productTypeId, priceTierId)
 * @returns {Promise<number>} Number of prices recalculated
 */
export const recalculateCataloguePrices = async (options = {}) => {
  // Fetch prices with all related data
  const prices = await fetchProductPrices(options)

  let updated = 0
  for (const price of prices) {
    const basePrice = price.product?.base_price_per_kg || 0
    const gradeMultiplier = price.product_type?.price_multiplier || 1.0
    const tierMultiplier = price.price_tier?.multiplier || 1.0

    const calculatedPrice = Math.round(basePrice * gradeMultiplier * tierMultiplier * 100) / 100

    if (calculatedPrice !== parseFloat(price.price_per_kg)) {
      await updateProductPrice(price.id, { price_per_kg: calculatedPrice })
      updated++
    }
  }

  return updated
}

// ============================================
// PRICING CALCULATION UTILITIES (US-014)
// ============================================
// NOTE: Core pricing logic now lives in the resolve_price() Postgres function.
// Client-side pricing functions (determinePriceTier, determineVolumeDiscount,
// calculateUnitPrice, calculateSaleItemPrice) have been removed.
// Use resolveSalePricing() or supabase.rpc('resolve_price', ...) instead.

/**
 * Get customer with their effective price tier information
 * Includes the default tier from customer type and employee discount status
 *
 * @param {string} customerId - Customer UUID
 * @returns {Promise<Object>} Customer with pricing context
 */
export const fetchCustomerWithPricingContext = async (customerId) => {
  const { data, error } = await supabase
    .from('customers')
    .select(`
      *,
      branches (
        id,
        name,
        type_id,
        customer_types (
          id,
          name,
          default_price_tier_id,
          price_tiers (
            id,
            name,
            multiplier,
            display_name
          )
        )
      )
    `)
    .eq('id', customerId)
    .single()

  if (error) throw error
  return data
}

/**
 * Recalculate all sale item prices against the current pricing catalogue.
 * Read-only - does not write to the database.
 *
 * @param {string} saleId - UUID of the sale to recalculate
 * @returns {Promise<Object>} Price comparison object
 */
export const recalculateSaleItemPrices = async (saleId) => {
  // Fetch sale with all items and related data
  const sale = await fetchSaleById(saleId)
  if (!sale) throw new Error('Sale not found')

  const items = await Promise.all(
    (sale.sale_items || []).map(async (item) => {
      const oldPrice = parseFloat(item.price)
      const oldDiscount = parseFloat(item.discount_percent || 0)
      const qty = parseFloat(item.quantity)
      const oldSubtotal = parseFloat(item.sub_total || (oldPrice * qty * (1 - oldDiscount / 100)))

      // Substrate items use fixed block pricing — skip recalculation
      if (item.block_format_id) {
        return {
          saleItemId: item.id,
          strain: item.product?.strain || 'Unknown',
          productTypeName: item.block_format?.name || 'Block',
          quantity: qty,
          oldPrice, newPrice: oldPrice,
          oldDiscountPercent: oldDiscount, newDiscountPercent: oldDiscount,
          oldSubtotal, newSubtotal: oldSubtotal,
          newTierId: null, newTierName: null,
          changed: false
        }
      }

      // Growing materials use fixed flat unit pricing — skip recalculation
      if (item.growing_material_id) {
        return {
          saleItemId: item.id,
          strain: item.growing_material?.name || 'Growing Material',
          productTypeName: item.growing_material?.unit_label || 'unit',
          quantity: qty,
          oldPrice, newPrice: oldPrice,
          oldDiscountPercent: oldDiscount, newDiscountPercent: oldDiscount,
          oldSubtotal, newSubtotal: oldSubtotal,
          newTierId: null, newTierName: null,
          changed: false
        }
      }

      // Use unified pricing engine
      const { data: priceResult, error: priceError } = await supabase.rpc('resolve_price', {
        p_customer_id: sale.customer_id,
        p_product_id: item.product_id,
        p_product_type_id: item.product_type_id,
        p_quantity: item.quantity
      })

      if (priceError || !priceResult?.[0]) {
        console.error('[recalculateSaleItemPrices] resolve_price failed:', priceError?.message)
        return {
          saleItemId: item.id,
          strain: item.product?.strain || 'Unknown',
          productTypeName: item.product_type?.name || 'Standard',
          quantity: qty,
          oldPrice, newPrice: oldPrice,
          oldDiscountPercent: oldDiscount, newDiscountPercent: oldDiscount,
          oldSubtotal, newSubtotal: oldSubtotal,
          newTierId: item.applied_price_tier_id, newTierName: null,
          changed: false
        }
      }

      const pricing = priceResult[0]
      const basePrice = pricing.catalogue_price
      const discountPercent = pricing.discount_percent || 0
      const newSubtotal = Math.round(basePrice * qty * (1 - discountPercent / 100) * 100) / 100

      return {
        saleItemId: item.id,
        strain: item.product?.strain || 'Unknown',
        productTypeName: item.product_type?.name || 'Standard',
        quantity: qty,
        oldPrice,
        newPrice: basePrice,
        oldDiscountPercent: oldDiscount,
        newDiscountPercent: discountPercent,
        oldSubtotal,
        newSubtotal,
        newTierId: pricing.applied_price_tier_id,
        newTierName: null,
        changed: Math.abs(oldPrice - basePrice) > 0.001 ||
                 Math.abs(oldDiscount - discountPercent) > 0.001
      }
    })
  )

  const oldTotal = items.reduce((sum, i) => sum + i.oldSubtotal, 0)
  const newTotal = items.reduce((sum, i) => sum + i.newSubtotal, 0)

  return {
    saleId,
    customerId: sale.customer_id,
    customerName: sale.customer?.name || 'Unknown',
    items,
    oldTotal: Math.round(oldTotal * 100) / 100,
    newTotal: Math.round(newTotal * 100) / 100,
    hasChanges: items.some(i => i.changed)
  }
}

/**
 * Apply recalculated prices to sale items and update sale status.
 *
 * @param {Object} priceComparison - Result from recalculateSaleItemPrices()
 * @param {string} newStatus - Status to transition to (e.g. 'packed')
 * @returns {Promise<Object>} Updated sale record
 */
export const applySaleItemPriceUpdates = async (priceComparison, newStatus) => {
  // Update each changed sale item
  const updatePromises = priceComparison.items
    .filter(item => item.changed)
    .map(item =>
      supabase
        .from('sale_items')
        .update({
          price: item.newPrice,
          discount_percent: item.newDiscountPercent,
          applied_price_tier_id: item.newTierId
        })
        .eq('id', item.saleItemId)
    )

  const results = await Promise.all(updatePromises)
  const errors = results.filter(r => r.error)
  if (errors.length > 0) {
    throw new Error(`Failed to update ${errors.length} sale item(s): ${errors[0].error.message}`)
  }

  // Update sale status
  return updateSaleStatus(priceComparison.saleId, newStatus)
}

// ── Sale Item Adjustments ──────────────────────────────────────────────────────
// Post-delivery modifications to sale line items (returns, class downgrades).
// See: vibe_coding/proposed_features/sale-adjustments-recourse.md

/**
 * Preview the financial impact of adjusting a sale item.
 * Read-only — does not write to the database.
 *
 * @param {string} saleItemId - UUID of the sale_item to adjust
 * @param {Object} changes - { quantity?: number, productTypeId?: string }
 * @returns {Promise<Object>} Preview with original/adjusted values and financial delta
 */
export const calculateAdjustmentPreview = async (saleItemId, { quantity, productTypeId }) => {
  // Fetch the sale item with full context
  const { data: saleItem, error: itemError } = await supabase
    .from('sale_items')
    .select(`
      *,
      product:products(id, strain, base_price_per_kg),
      product_type:product_types(id, name, price_multiplier),
      block_format:block_formats(id, name, weight_kg),
      growing_material:growing_materials(id, name, category, unit_label, reference_code),
      sale:sales(
        id, status, customer_id, branch_id, invoice_id,
        customer:customers(id, name, employee_discount_eligible),
        branch:branches(id, name, customer_type:customer_types(id, name, default_price_tier_id)),
        invoice:invoices(id, invoice_no, paid, payment_source, xero_invoice_id)
      )
    `)
    .eq('id', saleItemId)
    .single()

  if (itemError) throw itemError
  if (!saleItem) throw new Error('Sale item not found')

  const sale = saleItem.sale
  const originalQty = parseFloat(saleItem.quantity)
  const originalPrice = parseFloat(saleItem.price)
  const originalDiscount = parseFloat(saleItem.discount_percent || 0)
  const originalSubtotal = parseFloat(saleItem.sub_total)

  const adjustedQty = quantity !== undefined ? quantity : originalQty
  const adjustedProductTypeId = productTypeId !== undefined ? productTypeId : saleItem.product_type_id

  // Determine adjusted price
  let adjustedPrice = originalPrice
  let adjustedDiscount = originalDiscount
  let adjustedTierId = saleItem.applied_price_tier_id

  // Block items: keep existing price (fixed block pricing)
  if (saleItem.block_format_id) {
    // Price stays the same, only quantity can change
  } else if (productTypeId && productTypeId !== saleItem.product_type_id) {
    // Class changed — use unified pricing engine to recalculate
    const { data: priceResult, error: priceError } = await supabase.rpc('resolve_price', {
      p_customer_id: sale.customer_id,
      p_product_id: saleItem.product_id,
      p_product_type_id: adjustedProductTypeId,
      p_quantity: adjustedQty
    })

    if (!priceError && priceResult?.[0]) {
      const pricing = priceResult[0]
      adjustedPrice = pricing.catalogue_price
      adjustedDiscount = pricing.discount_percent || 0
      adjustedTierId = pricing.applied_price_tier_id
    }
  }

  const adjustedSubtotal = Math.round(adjustedPrice * adjustedQty * (1 - adjustedDiscount / 100) * 100) / 100

  // Determine invoice state
  const invoice = sale.invoice
  let invoiceState = 'none'
  if (invoice && sale.status === 'paid') invoiceState = 'paid'
  else if (invoice) invoiceState = 'invoiced'

  // Get product type name for the adjusted type
  let adjustedProductTypeName = saleItem.product_type?.name
  if (productTypeId && productTypeId !== saleItem.product_type_id) {
    const { data: newType } = await supabase
      .from('product_types')
      .select('name')
      .eq('id', productTypeId)
      .single()
    adjustedProductTypeName = newType?.name || 'Unknown'
  }

  return {
    saleItemId,
    saleId: sale.id,
    customerId: sale.customer_id,
    strain: saleItem.product?.strain || 'Unknown',
    trayNumber: saleItem.item_number,
    original: {
      quantity: originalQty,
      productTypeId: saleItem.product_type_id,
      productTypeName: saleItem.product_type?.name || (saleItem.block_format?.name ? `Block (${saleItem.block_format.name})` : 'Unknown'),
      price: originalPrice,
      discountPercent: originalDiscount,
      subtotal: originalSubtotal
    },
    adjusted: {
      quantity: adjustedQty,
      productTypeId: adjustedProductTypeId,
      productTypeName: adjustedProductTypeName || (saleItem.block_format?.name ? `Block (${saleItem.block_format.name})` : 'Unknown'),
      price: adjustedPrice,
      discountPercent: adjustedDiscount,
      subtotal: adjustedSubtotal,
      tierId: adjustedTierId
    },
    delta: Math.round((originalSubtotal - adjustedSubtotal) * 100) / 100,
    invoiceState,
    invoiceId: invoice?.id || null,
    invoiceNo: invoice?.invoice_no || null,
    xeroInvoiceId: invoice?.xero_invoice_id || null
  }
}

/**
 * Apply an adjustment to a sale item: update in place, create audit record,
 * and optionally create a credit note for post-invoice adjustments.
 *
 * @param {string} saleItemId - UUID of the sale_item to adjust
 * @param {Object} params - { quantity?: number, productTypeId?: string, reason: string }
 * @returns {Promise<Object>} { adjustment, creditNote }
 */
export const applySaleItemAdjustment = async (saleItemId, { quantity, productTypeId, reason }) => {
  // Get the full preview (before/after + financial delta)
  const preview = await calculateAdjustmentPreview(saleItemId, { quantity, productTypeId })

  // Update sale_item in place
  const updateData = {
    quantity: preview.adjusted.quantity,
    price: preview.adjusted.price,
    discount_percent: preview.adjusted.discountPercent
  }

  // Encode which fields changed into discount_note for asterisk placement
  const changedFields = []
  if (preview.original.quantity !== preview.adjusted.quantity) changedFields.push('qty')
  if (preview.original.productTypeId !== preview.adjusted.productTypeId) changedFields.push('class')
  updateData.discount_note = `Adjusted[${changedFields.join(',')}]: ${reason}`

  if (preview.adjusted.tierId) {
    updateData.applied_price_tier_id = preview.adjusted.tierId
  }
  // Only update product_type_id for fresh mushrooms (not blocks)
  if (productTypeId !== undefined) {
    updateData.product_type_id = preview.adjusted.productTypeId
  }

  const { error: updateError } = await supabase
    .from('sale_items')
    .update(updateData)
    .eq('id', saleItemId)

  if (updateError) throw updateError

  // Get current user for audit
  const { data: { user } } = await supabase.auth.getUser()

  // Create adjustment audit record
  const { data: adjustment, error: adjError } = await supabase
    .from('sale_item_adjustments')
    .insert({
      sale_item_id: saleItemId,
      sale_id: preview.saleId,
      original_quantity: preview.original.quantity,
      original_product_type_id: preview.original.productTypeId,
      original_price: preview.original.price,
      original_sub_total: preview.original.subtotal,
      adjusted_quantity: preview.adjusted.quantity,
      adjusted_product_type_id: preview.adjusted.productTypeId,
      adjusted_price: preview.adjusted.price,
      adjusted_sub_total: preview.adjusted.subtotal,
      reason,
      adjusted_by: user.id
    })
    .select()
    .single()

  if (adjError) throw adjError

  // Post-invoice: create credit note for the financial delta
  let creditNote = null
  if (preview.invoiceState !== 'none' && preview.delta > 0) {
    const response = await fetch(
      `${supabaseUrl}/functions/v1/credit-notes`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
          'apikey': supabaseKey
        },
        body: JSON.stringify({
          action: 'create',
          customer_id: preview.customerId,
          invoice_id: preview.invoiceId,
          amount: preview.delta,
          reason: reason,
          type: 'adjustment',
          sale_item_id: saleItemId
        })
      }
    )

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error('[applySaleItemAdjustment] Credit note creation failed:', errorData)
      // Don't throw — the adjustment itself succeeded; credit note is secondary
    } else {
      const result = await response.json()
      creditNote = result.credit_note || null

      // Link credit note to adjustment record
      if (creditNote?.id) {
        await supabase
          .from('sale_item_adjustments')
          .update({ credit_note_id: creditNote.id })
          .eq('id', adjustment.id)
      }
    }
  }

  return { adjustment, creditNote, preview }
}

/**
 * Fetch all adjustments for a sale, ordered chronologically.
 * Used to populate visual indicators on the timeline.
 *
 * @param {string} saleId - UUID of the sale
 * @returns {Promise<Array>} Array of adjustment records
 */
export const fetchSaleItemAdjustments = async (saleId) => {
  const { data, error } = await supabase
    .from('sale_item_adjustments')
    .select(`
      *,
      original_product_type:product_types!sale_item_adjustments_original_product_type_id_fkey(name),
      adjusted_product_type:product_types!sale_item_adjustments_adjusted_product_type_id_fkey(name)
    `)
    .eq('sale_id', saleId)
    .order('adjusted_at', { ascending: true })

  if (error) throw error
  return data
}

/**
 * Get the default price tier for a branch (via customer type)
 *
 * @param {string} branchId - Branch UUID
 * @returns {Promise<Object>} Price tier object
 */
export const fetchBranchDefaultPriceTier = async (branchId) => {
  const { data, error } = await supabase
    .from('branches')
    .select(`
      id,
      customer_types (
        id,
        name,
        default_price_tier_id,
        price_tiers (
          id,
          name,
          multiplier,
          display_name,
          description
        )
      )
    `)
    .eq('id', branchId)
    .single()

  if (error) throw error
  return data?.customer_types?.price_tiers || null
}

// Find existing mergeable sale for same customer + branch + date (pending or packed, not disregarded)
export const findExistingSale = async (customerId, branchId, date) => {
  const dateStr = typeof date === 'string' ? date.slice(0, 10) : toLocalDateString(date)

  let query
  if (branchId) {
    query = supabase
      .from('sales')
      .select('*')
      .eq('customer_id', customerId)
      .eq('branch_id', branchId)
      .eq('date', dateStr)
      .in('status', ['pending', 'packed'])
      .eq('disregarded', false)
      .limit(1)
      .single()
  } else {
    query = supabase
      .from('sales')
      .select('*')
      .eq('customer_id', customerId)
      .is('branch_id', null)
      .eq('date', dateStr)
      .in('status', ['pending', 'packed'])
      .eq('disregarded', false)
      .limit(1)
      .single()
  }

  const { data, error } = await query

  if (error && error.code === 'PGRST116') return null // No rows found
  if (error) throw error
  return data
}

// Backwards-compatible alias
export const findExistingPendingSale = findExistingSale

// Sales with customer/branch support
export const createSale = async (saleData) => {
  const insertData = {
    date: saleData.date,
    customer_id: saleData.customer_id,
    branch_id: saleData.branch_id,
    consignment_no: saleData.consignment_no,
    status: saleData.status || 'pending'  // Default to pending status
  }

  // Support recurring order fields (US-013E)
  if (saleData.is_auto_committed !== undefined) {
    insertData.is_auto_committed = saleData.is_auto_committed
  }
  if (saleData.recurring_order_id) {
    insertData.recurring_order_id = saleData.recurring_order_id
  }

  const { data, error } = await supabase
    .from('sales')
    .insert([insertData])
    .select()
    .single()

  if (error) throw error
  return data
}

export const createSaleWithItems = async (saleData) => {
  try {
    // Create the sale first
    const sale = await createSale({
      date: saleData.date,
      customer_id: saleData.customer_id,
      branch_id: saleData.branch_id,
      consignment_no: saleData.consignment_no,
      status: saleData.status || 'pending',
      // Support recurring order fields (US-013E)
      is_auto_committed: saleData.is_auto_committed,
      recurring_order_id: saleData.recurring_order_id
    })

    // Create sale items if provided
    if (saleData.items && saleData.items.length > 0) {
      // Fetch product details to get product_type_id for each item
      const productIds = saleData.items.map(item => item.product_id)
      const { data: products, error: productError } = await supabase
        .from('products')
        .select('id')
        .in('id', productIds)

      if (productError) throw productError

      // Note: product_type_id must be provided in saleData.items
      // Products table no longer stores type information

      const saleItems = saleData.items.map(item => ({
        sale_id: sale.id,
        product_id: item.product_id,
        product_type_id: item.product_type_id,  // Required: product_type_id from sale item
        quantity: item.quantity,
        price: item.price,
        packed: item.packed || false,
        discount_percent: item.discount_percent || null,
        discount_note: item.discount_note || null
      }))

      await createSaleItems(saleItems)
    }

    // Return the created sale
    return sale
  } catch (error) {
    console.error('Error creating sale with items:', error)
    throw error
  }
}

// DEPRECATED: This function loads ALL sales with nested data and should not be used
// Use fetchSalesWithDateRange() with appropriate date limits instead
// Only kept for backwards compatibility with tests
export const fetchSales = async () => {
  console.warn('[DEPRECATED] fetchSales() loads all sales and may cause performance issues. Use fetchSalesWithDateRange() instead.')
  const { data, error } = await supabase
    .from('sales')
    .select(`
      *,
      customer:customers(name),
      branch:branches(name, address, customer_type:customer_types(id, name)),
      sale_items:sale_items(
        *,
        product:products(strain),
        product_type:product_types(name),
        block_format:block_formats(id, name, weight_kg),
        growing_material:growing_materials(id, name, category, unit_label, reference_code),
        price_tier:price_tiers!sale_items_applied_price_tier_id_fkey(id, name, display_name, multiplier)
      )
    `)
    .order('date', { ascending: false })

  if (error) throw error
  return data
}

// Fetch only consignment numbers (lightweight query)
// Used for generating new consignment numbers
export const fetchConsignmentNumbers = async () => {
  const { data, error } = await supabase
    .from('sales')
    .select('consignment_no')
    .order('consignment_no', { ascending: false })

  if (error) throw error
  return data.map(sale => sale.consignment_no)
}

// Fetch a single sale by ID with all related data
export const fetchSaleById = async (saleId) => {
  const { data, error } = await supabase
    .from('sales')
    .select(`
      *,
      customer:customers(name),
      branch:branches(name, address, customer_type:customer_types(id, name)),
      sale_items:sale_items(
        *,
        product:products(strain),
        product_type:product_types(name),
        block_format:block_formats(id, name, weight_kg),
        growing_material:growing_materials(id, name, category, unit_label, reference_code, product:products(id, strain)),
        price_tier:price_tiers!sale_items_applied_price_tier_id_fkey(id, name, display_name, multiplier)
      ),
      invoices(id, invoice_no, paid, payment_source)
    `)
    .eq('id', saleId)
    .single()

  if (error) throw error
  return data
}

// Fetch sales within a specific date range
export const fetchSalesWithDateRange = async (startDate, endDate, limit = null) => {
  let query = supabase
    .from('sales')
    .select(`
      *,
      customer:customers(name, transmission),
      branch:branches(name, address, customer_type:customer_types(id, name)),
      sale_items:sale_items(
        *,
        product:products(strain),
        product_type:product_types(name),
        block_format:block_formats(id, name, weight_kg),
        growing_material:growing_materials(id, name, category, unit_label, reference_code),
        price_tier:price_tiers!sale_items_applied_price_tier_id_fkey(id, name, display_name, multiplier)
      ),
      invoices(id, invoice_no, paid, payment_source)
    `)
    .gte('date', startDate)
    .lte('date', endDate)
    .order('date', { ascending: false })

  if (limit) {
    query = query.limit(limit)
  }

  const { data, error } = await query
  if (error) throw error

  return data
}

/**
 * Lightweight sales query for timeline list view.
 * Fetches only the columns needed for TimelineRow rendering — skips
 * deep joins on product_types, price_tiers, and invoices.
 * Client-side enrichment from DataCacheContext snapshot fills in
 * strain/productTypeName before render.
 */
export const fetchSalesLightweight = async (startDate, endDate) => {
  const { data, error } = await supabase
    .from('sales')
    .select(`
      id, date, status, consignment_no, customer_id, branch_id, recurring_order_id, market_event_id,
      customer:customers(name),
      branch:branches(name),
      sale_items(id, product_id, product_type_id, block_format_id, block_format:block_formats(id, name, weight_kg), growing_material_id, growing_material:growing_materials(id, name, category, unit_label, reference_code, product:products(id, strain)), quantity, price, discount_percent, discount_note, sub_total, item_number)
    `)
    .gte('date', startDate)
    .lte('date', endDate)
    .order('date', { ascending: false })

  if (error) throw error
  return data
}

/**
 * Transform a lightweight sale (from fetchSalesLightweight) to TimelineItem format.
 * strain and productTypeName are set to null — caller enriches from cache.
 */
export const transformLightweightToTimelineItem = (sale) => {
  const type = sale.recurring_order_id ? 'committed-recurring' : 'confirmed'
  return {
    id: `sale-${sale.id}`,
    type,
    sourceId: sale.id,
    date: sale.date,
    customer: {
      id: sale.customer_id,
      name: sale.customer?.name || 'Unknown Customer'
    },
    branch: {
      id: sale.branch_id,
      name: sale.branch?.name || 'Unknown Branch'
    },
    items: (sale.sale_items || []).map(item => ({
      id: item.id,
      productId: item.product_id,
      productTypeId: item.product_type_id,
      blockFormatId: item.block_format_id || null,
      blockFormat: item.block_format || null,
      growingMaterialId: item.growing_material_id || null,
      growingMaterial: item.growing_material || null,
      strain: null,           // enriched client-side
      productTypeName: null,  // enriched client-side
      quantity: parseFloat(item.quantity),
      price: parseFloat(item.price),
      discountPercent: parseFloat(item.discount_percent || 0),
      discountNote: item.discount_note || null,
      subtotal: parseFloat(item.sub_total),
      itemNumber: item.item_number || null
    })),
    total: (sale.sale_items || []).reduce((sum, item) => sum + parseFloat(item.sub_total), 0),
    status: sale.status,
    consignmentNo: sale.consignment_no,
    invoiceNo: null,  // not fetched in lightweight query
    recurringOrderId: sale.recurring_order_id || null,
    marketEventId: sale.market_event_id || null
  }
}

// Fetch only the most recent sale date (lightweight query without nested data)
// Used to determine the starting point for progressive loading
export const fetchMostRecentSaleDate = async () => {
  const { data, error } = await supabase
    .from('sales')
    .select('date')
    .order('date', { ascending: false })
    .limit(1)

  if (error) throw error
  return data.length > 0 ? data[0].date : null
}

// Fetch the latest REAL sale date (with consignment number, excluding test data)
// Used for Google Sheets import to determine the cutoff date
export const fetchLatestRealSaleDate = async () => {
  const { data, error } = await supabase
    .from('sales')
    .select('date')
    .not('consignment_no', 'is', null)
    .order('date', { ascending: false })
    .limit(1)

  if (error) throw error
  return data.length > 0 ? data[0].date : null
}

// Check if there are any sales before a given date (lightweight query)
// Used to determine if "Show More" button should be displayed
export const hasSalesBeforeDate = async (beforeDate) => {
  const { data, error } = await supabase
    .from('sales')
    .select('id')
    .lt('date', beforeDate)
    .limit(1)

  if (error) throw error
  return data.length > 0
}

// Get total count of sales records
export const fetchSalesCount = async () => {
  const { count, error } = await supabase
    .from('sales')
    .select('*', { count: 'exact', head: true })
  
  if (error) throw error
  return count
}

// Get count of sales within date range
export const fetchSalesCountInDateRange = async (startDate, endDate) => {
  const { count, error } = await supabase
    .from('sales')
    .select('*', { count: 'exact', head: true })
    .gte('date', startDate)
    .lte('date', endDate)
  
  if (error) throw error
  return count
}

// Fetch sales in batches with progress callback (high-performance version)
export const fetchSalesInBatches = async (batchSize = null, onProgress = null) => {
  const totalCount = await fetchSalesCount()
  
  // Smart loading strategy based on dataset size
  if (totalCount <= 1000) {
    // For small datasets, load everything at once
    console.log(`[PERFORMANCE] Loading all ${totalCount} records at once (small dataset)`)
    const { data, error } = await supabase
      .from('sales')
      .select(`
        *,
        customer:customers(name),
        branch:branches(name, address, customer_type:customer_types(id, name)),
        sale_items:sale_items(
          *,
          product:products(strain),
          product_type:product_types(name),
        block_format:block_formats(id, name, weight_kg),
        growing_material:growing_materials(id, name, category, unit_label, reference_code),
          price_tier:price_tiers!sale_items_applied_price_tier_id_fkey(id, name, display_name, multiplier)
        ),
        invoices(id, invoice_no, paid, payment_source)
      `)
      .order('date', { ascending: false })

    if (error) throw error

    // Single progress update for completion
    if (onProgress) {
      onProgress({
        progress: 100,
        loadedCount: data.length,
        totalCount,
        batchNumber: 1,
        currentBatch: data
      })
    }
    
    return data
  }
  
  // For large datasets, use high-performance batching
  const optimalBatchSize = batchSize || Math.min(3000, Math.max(1000, Math.ceil(totalCount / 3)))
  console.log(`[PERFORMANCE] Loading ${totalCount} records in batches of ${optimalBatchSize}`)
  
  let allSales = []
  let offset = 0
  let batchNumber = 0
  let lastProgressUpdate = Date.now()
  
  while (offset < totalCount) {
    const startTime = Date.now()
    
    const { data, error } = await supabase
      .from('sales')
      .select(`
        *,
        customer:customers(name),
        branch:branches(name, address, customer_type:customer_types(id, name)),
        sale_items:sale_items(
          *,
          product:products(strain),
          product_type:product_types(name),
        block_format:block_formats(id, name, weight_kg),
        growing_material:growing_materials(id, name, category, unit_label, reference_code),
          price_tier:price_tiers!sale_items_applied_price_tier_id_fkey(id, name, display_name, multiplier)
        ),
        invoices(id, invoice_no, paid, payment_source)
      `)
      .order('date', { ascending: false })
      .range(offset, offset + optimalBatchSize - 1)
    
    if (error) throw error
    
    allSales = [...allSales, ...data]
    offset += optimalBatchSize
    batchNumber++
    
    const batchTime = Date.now() - startTime
    console.log(`[PERFORMANCE] Batch ${batchNumber}: loaded ${data.length} records in ${batchTime}ms`)
    
    // Time-based progress updates (max once per 200ms) instead of every batch
    const now = Date.now()
    if (onProgress && (now - lastProgressUpdate > 200 || offset >= totalCount)) {
      const progress = Math.min(100, (allSales.length / totalCount) * 100)
      onProgress({
        progress,
        loadedCount: allSales.length,
        totalCount,
        batchNumber,
        currentBatch: data
      })
      lastProgressUpdate = now
    }
    
    // No artificial delays - let it rip!
  }
  
  console.log(`[PERFORMANCE] Completed loading ${allSales.length} records in ${batchNumber} batches`)
  return allSales
}

export const updateSale = async (id, updates) => {
  const { data, error } = await supabase
    .from('sales')
    .update(updates)
    .eq('id', id)
    .select()
    .single()
  
  if (error) throw error
  return data
}

export const deleteSale = async (id) => {
  // Unlink any recurring_order_instances that reference this sale
  // (reset to pending so the instance can be re-confirmed)
  const { error: unlinkError } = await supabase
    .from('recurring_order_instances')
    .update({ sale_id: null, status: 'pending', actioned_at: null, actioned_by: null })
    .eq('sale_id', id)

  if (unlinkError) throw unlinkError

  const { error } = await supabase
    .from('sales')
    .delete()
    .eq('id', id)

  if (error) throw error
}

// Sale items management
/**
 * Creates a single sale item
 * @param {Object} saleItemData - Sale item data object
 * @param {uuid} saleItemData.sale_id - FK to sales table (required)
 * @param {uuid} saleItemData.product_id - FK to products table (required)
 * @param {uuid} saleItemData.product_type_id - FK to product_types table (required for new items)
 * @param {number} saleItemData.quantity - Quantity sold (required)
 * @param {number} saleItemData.price_per_unit - Price per unit (required)
 * @param {number} [saleItemData.discount_percent] - Discount percentage (optional)
 * @param {string} [saleItemData.discount_note] - Discount reason (optional)
 * @param {boolean} [saleItemData.packed=false] - Packing status (optional)
 * @returns {Promise<Object>} Created sale item record
 * @throws {Error} If database operation fails
 */
export const createSaleItem = async (saleItemData) => {
  const { data, error } = await supabase
    .from('sale_items')
    .insert([saleItemData])
    .select()
    .single()

  if (error) throw error
  return data
}

/**
 * Creates multiple sale items in a batch
 * @param {Array<Object>} saleItems - Array of sale item data objects
 * @param {uuid} saleItems[].sale_id - FK to sales table (required)
 * @param {uuid} saleItems[].product_id - FK to products table (required)
 * @param {uuid} saleItems[].product_type_id - FK to product_types table (required for new items)
 * @param {number} saleItems[].quantity - Quantity sold (required)
 * @param {number} saleItems[].price_per_unit - Price per unit (required)
 * @returns {Promise<Array>} Created sale item records
 * @throws {Error} If database operation fails
 */
export const createSaleItems = async (saleItems) => {
  const { data, error } = await supabase
    .from('sale_items')
    .insert(saleItems)
    .select()

  if (error) throw error
  return data
}

export const fetchSaleItems = async (saleId) => {
  const { data, error } = await supabase
    .from('sale_items')
    .select(`
      *,
      product:products(id, strain, sku, active),
      product_type:product_types(id, name),
      block_format:block_formats(id, name, weight_kg),
      growing_material:growing_materials(id, name, category, unit_label, reference_code)
    `)
    .eq('sale_id', saleId)

  if (error) throw error
  return data
}

// Delete all sale items for a given sale
export const deleteSaleItemsBySaleId = async (saleId) => {
  const { error } = await supabase
    .from('sale_items')
    .delete()
    .eq('sale_id', saleId)

  if (error) throw error
}

// Products
export const fetchProducts = async () => {
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .eq('active', true)
    .order('strain')

  if (error) throw error
  return data
}

// Price lists
export const fetchPriceLists = async (customerType = null) => {
  let query = supabase
    .from('price_lists')
    .select(`
      *,
      product:products(id, strain, sku, active)
    `)
  
  if (customerType) {
    query = query.eq('customer_type', customerType)
  }
  
  const { data, error } = await query.order('valid_from', { ascending: false })
  
  if (error) throw error
  return data
}

// Product management functions for US-008

// Products CRUD
/**
 * Creates a new product (strain) in the database
 * Products are strains only. Types are managed separately in the product_types table.
 * @param {Object} productData - Product data object
 * @param {string} productData.strain - Product strain/variety name (required)
 * @param {string} [productData.sku] - Product SKU (optional)
 * @param {number} [productData.base_price_per_kg] - Base price per kg (optional)
 * @param {boolean} [productData.active=true] - Product active status (optional)
 * @returns {Promise<Object>} Created product record
 * @throws {Error} If database operation fails
 *
 * @example
 * const product = await createProduct({
 *   strain: "Lions Mane",
 *   sku: "LM-001",
 *   base_price_per_kg: 20.00,
 *   active: true
 * })
 */
export const createProduct = async (productData) => {
  // Check for inactive product with same SKU and reactivate it
  if (productData.sku) {
    const { data: existing } = await supabase
      .from('products')
      .select('id')
      .eq('sku', productData.sku)
      .eq('active', false)
      .limit(1)
      .single()

    if (existing) {
      const { data, error } = await supabase
        .from('products')
        .update({ ...productData, active: true })
        .eq('id', existing.id)
        .select()

      if (error) throw error
      return data[0]
    }
  }

  const { data, error } = await supabase
    .from('products')
    .insert([productData])
    .select()

  if (error) {
    if (error.code === '23505') {
      throw new Error('A product with this SKU already exists')
    }
    throw error
  }
  return data[0]
}

export const updateProduct = async (id, updates) => {
  const { data, error } = await supabase
    .from('products')
    .update(updates)
    .eq('id', id)
    .select()
  
  if (error) throw error
  return data[0]
}

export const deleteProduct = async (id) => {
  const { error } = await supabase
    .from('products')
    .delete()
    .eq('id', id)
  
  if (error) throw error
}

// Product Types CRUD
export const fetchProductTypes = async () => {
  const { data, error } = await supabase
    .from('product_types')
    .select('*')
    .order('price_multiplier', { ascending: false }) // Order by multiplier descending (premium first)

  if (error) throw error
  return data
}

export const createProductType = async (typeData) => {
  const { data, error } = await supabase
    .from('product_types')
    .insert([typeData])
    .select()
  
  if (error) throw error
  return data[0]
}

export const updateProductType = async (id, updates) => {
  const { data, error } = await supabase
    .from('product_types')
    .update(updates)
    .eq('id', id)
    .select()
  
  if (error) throw error
  return data[0]
}

export const deleteProductType = async (id) => {
  const { error } = await supabase
    .from('product_types')
    .delete()
    .eq('id', id)
  
  if (error) throw error
}

// Block Formats CRUD
export const fetchBlockFormats = async () => {
  const { data, error } = await supabase
    .from('block_formats')
    .select('*, supplier:suppliers(id, name), block_format_strains(strain_id, strain:strains(id, name))')
    .order('name', { ascending: true })
  if (error) throw error
  return data
}

export const createBlockFormat = async (formatData) => {
  const { strain_ids, ...rest } = formatData
  const { data, error } = await supabase
    .from('block_formats')
    .insert([rest])
    .select()
  if (error) throw error
  const created = data[0]

  if (strain_ids && strain_ids.length > 0) {
    const rows = strain_ids.map(strain_id => ({ block_format_id: created.id, strain_id }))
    const { error: junctionError } = await supabase.from('block_format_strains').insert(rows)
    if (junctionError) throw junctionError
  }

  return created
}

export const updateBlockFormat = async (id, updates) => {
  const { strain_ids, ...rest } = updates
  const { data, error } = await supabase
    .from('block_formats')
    .update({ ...rest, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
  if (error) throw error

  // Replace junction rows: delete all, then insert new ones if specific strains selected
  const { error: deleteError } = await supabase.from('block_format_strains').delete().eq('block_format_id', id)
  if (deleteError) throw deleteError

  if (strain_ids && strain_ids.length > 0) {
    const rows = strain_ids.map(strain_id => ({ block_format_id: id, strain_id }))
    const { error: junctionError } = await supabase.from('block_format_strains').insert(rows)
    if (junctionError) throw junctionError
  }

  return data[0]
}

export const deleteBlockFormat = async (id) => {
  const { error } = await supabase
    .from('block_formats')
    .delete()
    .eq('id', id)
  if (error) throw error
}

// Block Prices CRUD
export const fetchBlockPrices = async () => {
  const { data, error } = await supabase
    .from('block_prices')
    .select(`
      *,
      product:products(id, strain, active),
      price_tier:price_tiers(id, name, display_name)
    `)
    .order('created_at', { ascending: true })
  if (error) throw error
  return data
}

export const upsertBlockPrice = async (productId, priceTierId, pricePerKg) => {
  const { data, error } = await supabase
    .from('block_prices')
    .upsert({
      product_id: productId,
      price_tier_id: priceTierId,
      price_per_kg: pricePerKg,
      updated_at: new Date().toISOString()
    }, { onConflict: 'product_id,price_tier_id' })
    .select()
  if (error) throw error
  return data[0]
}

// Growing Materials CRUD
export const fetchGrowingMaterials = async () => {
  const { data, error } = await supabase
    .from('growing_materials')
    .select('*, product:products(id, strain), supplier:suppliers(id, name), growing_material_prices(id, price_tier_id, unit_price)')
    .order('sort_order', { ascending: true })
    .order('name', { ascending: true })
  if (error) throw error
  return data
}

export const createGrowingMaterial = async (materialData) => {
  const { data, error } = await supabase
    .from('growing_materials')
    .insert([materialData])
    .select()
  if (error) throw error
  return data[0]
}

export const updateGrowingMaterial = async (id, updates) => {
  const { data, error } = await supabase
    .from('growing_materials')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
  if (error) throw error
  return data[0]
}

export const deleteGrowingMaterial = async (id) => {
  const { error } = await supabase
    .from('growing_materials')
    .delete()
    .eq('id', id)
  if (error) throw error
}

// Growing Material Prices CRUD
export const fetchGrowingMaterialPrices = async () => {
  const { data, error } = await supabase
    .from('growing_material_prices')
    .select(`
      *,
      growing_material:growing_materials(id, name, category, unit_label, active),
      price_tier:price_tiers(id, name, display_name)
    `)
    .order('created_at', { ascending: true })
  if (error) throw error
  return data
}

export const upsertGrowingMaterialPrice = async (growingMaterialId, priceTierId, unitPrice) => {
  const { data, error } = await supabase
    .from('growing_material_prices')
    .upsert({
      growing_material_id: growingMaterialId,
      price_tier_id: priceTierId,
      unit_price: unitPrice,
      updated_at: new Date().toISOString()
    }, { onConflict: 'growing_material_id,price_tier_id' })
    .select()
  if (error) throw error
  return data[0]
}

// Volume Discounts CRUD
export const fetchVolumeDiscounts = async () => {
  const { data, error} = await supabase
    .from('volume_discounts')
    .select(`
      *,
      product:products(id, strain, sku, active, base_price_per_kg),
      price_tier:price_tiers(id, name, display_name, multiplier)
    `)
    .order('price_tier_id')

  if (error) throw error
  return data
}

export const createVolumeDiscount = async (discountData) => {
  const { data, error } = await supabase
    .from('volume_discounts')
    .insert([discountData])
    .select()
  
  if (error) throw error
  return data[0]
}

export const updateVolumeDiscount = async (id, updates) => {
  const { data, error } = await supabase
    .from('volume_discounts')
    .update(updates)
    .eq('id', id)
    .select()
  
  if (error) throw error
  return data[0]
}

export const deleteVolumeDiscount = async (id) => {
  const { error } = await supabase
    .from('volume_discounts')
    .delete()
    .eq('id', id)
  
  if (error) throw error
}

// ============================================
// PROMOTIONS (Time-bound discounts)
// ============================================

export const fetchPromotions = async () => {
  const { data, error } = await supabase
    .from('promotions')
    .select(`
      *,
      product:products(id, strain),
      product_type:product_types(id, name),
      price_tier:price_tiers(id, name, display_name),
      customer_type:customer_types(id, name)
    `)
    .order('start_date', { ascending: false })

  if (error) throw error
  return data
}

export const fetchActivePromotions = async () => {
  const today = toLocalDateString(new Date())
  const { data, error } = await supabase
    .from('promotions')
    .select(`
      *,
      product:products(id, strain),
      product_type:product_types(id, name),
      price_tier:price_tiers(id, name, display_name),
      customer_type:customer_types(id, name)
    `)
    .eq('active', true)
    .lte('start_date', today)
    .gte('end_date', today)

  if (error) throw error
  return data
}

export const createPromotion = async (promotionData) => {
  const { data, error } = await supabase
    .from('promotions')
    .insert([promotionData])
    .select()

  if (error) throw error
  return data[0]
}

export const updatePromotion = async (id, updates) => {
  const { data, error } = await supabase
    .from('promotions')
    .update(updates)
    .eq('id', id)
    .select()

  if (error) throw error
  return data[0]
}

export const deletePromotion = async (id) => {
  const { error } = await supabase
    .from('promotions')
    .delete()
    .eq('id', id)

  if (error) throw error
}

// NOTE: getApplicablePromotion() removed — promotion matching now lives
// in the resolve_price() Postgres function.

// Product catalog view (computed products)
export const fetchProductCatalog = async () => {
  const { data, error } = await supabase
    .from('product_catalog')
    .select('*')
    .order('strain_name', { ascending: true })
    .order('price_modifier_percent', { ascending: true })
  
  if (error) throw error
  return data
}

// NOTE: calculateFinalPrice() removed — use resolve_price() Postgres function instead.

/**
 * Unified Pricing Engine — resolve pricing for an entire sale via Edge Function.
 * Single source of truth: all pricing logic lives in the resolve_price() Postgres function.
 * This replaces all client-side pricing calculations.
 */
export const resolveSalePricing = async ({ customerId, items, blockItems, growingMaterialItems, date }) => {
  const { data, error } = await supabase.functions.invoke('resolve-sale-pricing', {
    body: {
      customer_id: customerId,
      items: items.map(i => ({
        product_id: i.product_id,
        product_type_id: i.product_type_id,
        quantity: i.quantity
      })),
      block_items: blockItems?.map(i => ({
        product_id: i.product_id,
        block_format_id: i.block_format_id,
        quantity: i.quantity
      })),
      growing_material_items: growingMaterialItems?.map(i => ({
        growing_material_id: i.growing_material_id,
        quantity: i.quantity
      })),
      date
    }
  })

  if (error) throw error
  if (!data?.success) throw new Error(data?.error || 'Pricing resolution failed')
  return data.data
}

// Payment CRUD operations
export const createPayment = async (paymentData) => {
  const { data, error } = await supabase
    .from('payments')
    .insert([paymentData])
    .select()
    .single()
  
  if (error) throw error
  return data
}

export const fetchCustomerPayments = async (customerId) => {
  const { data, error } = await supabase
    .from('payments')
    .select('*')
    .eq('customer_id', customerId)
    .order('date', { ascending: false })
  
  if (error) throw error
  return data
}

export const fetchAllPayments = async () => {
  const { data, error } = await supabase
    .from('payments')
    .select(`
      *,
      customer:customer_id (
        id,
        name,
        type,
        email
      )
    `)
    .order('date', { ascending: false })
  
  if (error) throw error
  return data
}

export const fetchCustomerInvoices = async (customerId) => {
  const { data, error } = await supabase
    .from('invoices')
    .select('*')
    .eq('customer_id', customerId)
    .order('date', { ascending: false })
  
  if (error) throw error
  return data
}

export const updateInvoice = async (invoiceId, updateData) => {
  const { data, error } = await supabase
    .from('invoices')
    .update(updateData)
    .eq('id', invoiceId)
    .select()
    .single()
  
  if (error) throw error
  return data
}

// Farm Information functions
export const fetchFarmInformation = async () => {
  const { data, error } = await supabase
    .from('farm_information')
    .select('*')
    .single()
  
  if (error) throw error
  return data
}

export const updateFarmInformation = async (farmData) => {
  const { data, error } = await supabase
    .from('farm_information')
    .upsert(farmData, { onConflict: 'id' })
    .select()
    .single()
  
  if (error) throw error
  return data
}

// Farm Logo Upload functions
export const uploadFarmLogo = async (logoFile) => {
  if (!logoFile) {
    throw new Error('Logo file is required')
  }

  // Validate file type
  const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml']
  if (!allowedTypes.includes(logoFile.type)) {
    throw new Error('Invalid file type. Please upload a JPG, PNG, GIF, or SVG file.')
  }

  // Validate file size (2MB limit)
  const maxSize = 2 * 1024 * 1024 // 2MB in bytes
  if (logoFile.size > maxSize) {
    throw new Error('File too large. Please upload a file smaller than 2MB.')
  }

  // Generate unique filename
  const fileExt = logoFile.name.split('.').pop()
  const fileName = `logo-${Date.now()}.${fileExt}`
  const filePath = fileName

  try {
    // Upload to Supabase Storage
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('farm-logos')
      .upload(filePath, logoFile, {
        cacheControl: '3600',
        upsert: true
      })

    if (uploadError) {
      throw new Error(`Failed to upload logo: ${uploadError.message}`)
    }

    return {
      filePath: uploadData.path,
      fileName: logoFile.name,
      uploadedAt: (() => getISOTimestamp())()
    }
  } catch (error) {
    console.error('Error uploading logo:', error)
    throw error
  }
}

export const deleteFarmLogo = async (filePath) => {
  if (!filePath) {
    return true // Nothing to delete
  }

  try {
    const { error } = await supabase.storage
      .from('farm-logos')
      .remove([filePath])

    if (error) {
      console.warn('Warning: Could not delete old logo file:', error.message)
      // Don't throw error - we'll continue with the database update
    }

    return true
  } catch (error) {
    console.warn('Warning: Could not delete old logo file:', error)
    return true // Continue even if file deletion fails
  }
}

export const getFarmLogoUrl = (filePath) => {
  if (!filePath) {
    return null
  }

  const { data } = supabase.storage
    .from('farm-logos')
    .getPublicUrl(filePath)

  return data.publicUrl
}

// Customer logo functions (mirrors farm logo pattern)
export const uploadCustomerLogo = async (logoFile, customerId) => {
  if (!logoFile) {
    throw new Error('Logo file is required')
  }

  const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml']
  if (!allowedTypes.includes(logoFile.type)) {
    throw new Error('Invalid file type. Please upload a JPG, PNG, GIF, or SVG file.')
  }

  const maxSize = 2 * 1024 * 1024
  if (logoFile.size > maxSize) {
    throw new Error('File too large. Please upload a file smaller than 2MB.')
  }

  const fileExt = logoFile.name.split('.').pop()
  const fileName = `logo-${customerId}-${Date.now()}.${fileExt}`

  try {
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('customer-logos')
      .upload(fileName, logoFile, {
        cacheControl: '3600',
        upsert: true
      })

    if (uploadError) {
      throw new Error(`Failed to upload logo: ${uploadError.message}`)
    }

    return {
      filePath: uploadData.path,
      fileName: logoFile.name,
      uploadedAt: (() => getISOTimestamp())()
    }
  } catch (error) {
    console.error('Error uploading customer logo:', error)
    throw error
  }
}

export const deleteCustomerLogo = async (filePath) => {
  if (!filePath) {
    return true
  }

  try {
    const { error } = await supabase.storage
      .from('customer-logos')
      .remove([filePath])

    if (error) {
      console.warn('Warning: Could not delete customer logo file:', error.message)
    }

    return true
  } catch (error) {
    console.warn('Warning: Could not delete customer logo file:', error)
    return true
  }
}

export const getCustomerLogoUrl = (filePath) => {
  if (!filePath) {
    return null
  }

  const { data } = supabase.storage
    .from('customer-logos')
    .getPublicUrl(filePath)

  return data.publicUrl
}

// Partner logo functions (GMC processing pipeline)
export const uploadPartnerLogo = async (file, customerId) => {
  const session = await supabase.auth.getSession()
  const accessToken = session?.data?.session?.access_token
  if (!accessToken) {
    throw new Error('Not authenticated — please sign in again')
  }

  const formData = new FormData()
  formData.append('file', file)
  formData.append('customer_id', customerId)

  const response = await fetch('https://glasgowmushroomcompany.co.uk/api/partner-logos', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    },
    body: formData
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `Logo upload failed (${response.status})`)
  }

  return response.json()
}

export const fetchPartnerLogo = async (customerId) => {
  if (!customerId) return null

  const { data, error } = await supabase
    .from('partner_logos')
    .select('id, logo_url, logo_url_dark')
    .eq('customer_id', customerId)
    .eq('active', true)
    .limit(1)
    .maybeSingle()

  if (error) {
    console.warn('Error fetching partner logo:', error.message)
    return null
  }

  return data
}

export const deletePartnerLogo = async (partnerLogoId) => {
  if (!partnerLogoId) return true

  const { error } = await supabase
    .from('partner_logos')
    .update({ active: false })
    .eq('id', partnerLogoId)

  if (error) {
    console.warn('Error deactivating partner logo:', error.message)
    throw new Error('Failed to remove logo')
  }

  return true
}

export const updateFarmInformationWithLogo = async (farmData, logoFile = null) => {
  try {
    let logoUpdateData = {}

    if (logoFile) {
      // Upload new logo
      const logoData = await uploadFarmLogo(logoFile)
      
      // Delete old logo if it exists
      if (farmData.logo_file_path) {
        await deleteFarmLogo(farmData.logo_file_path)
      }

      logoUpdateData = {
        logo_file_path: logoData.filePath,
        logo_file_name: logoData.fileName,
        logo_uploaded_at: logoData.uploadedAt
      }
    }

    // Update farm information with logo data
    const updatedFarmData = {
      ...farmData,
      ...logoUpdateData
    }

    return await updateFarmInformation(updatedFarmData)
  } catch (error) {
    console.error('Error updating farm information with logo:', error)
    throw error
  }
}

// Invoice generation functions
export const createInvoice = async (invoiceData) => {
  // Validate branch_id is provided
  if (!invoiceData.branch_id) {
    throw new Error('branch_id is required for invoice creation. Each invoice must be associated with a specific customer branch.')
  }

  const { data, error } = await supabase
    .from('invoices')
    .insert([invoiceData])
    .select()
    .single()

  if (error) throw error
  return data
}

export const fetchUninvoicedSales = async (customerId, branchId = null) => {
  let query = supabase
    .from('sales')
    .select(`
      *,
      customer:customers(name, payment_terms),
      branch:branches(name, address, customer_type:customer_types(id, name)),
      sale_items:sale_items(
        *,
        product:products(strain, reference_code),
        product_type:product_types(name, reference_code),
        block_format:block_formats(id, name, weight_kg, reference_code),
        growing_material:growing_materials(id, name, category, unit_label, reference_code),
        price_tier:price_tiers!sale_items_applied_price_tier_id_fkey(id, name, display_name, multiplier)
      )
    `)
    .eq('customer_id', customerId)
    .eq('invoiced', false)
    .order('date', { ascending: true })
  
  // Handle both new sales with status='delivered' AND legacy sales with null status
  // This allows existing sales to be invoiced while maintaining the new workflow
  query = query.or('status.eq.delivered,status.eq.collected,status.is.null')

  if (branchId) {
    query = query.eq('branch_id', branchId)
  }

  const { data, error } = await query
  
  if (error) throw error
  return data
}

export const markSalesAsInvoiced = async (salesIds, invoiceId) => {
  const { data, error } = await supabase
    .from('sales')
    .update({ 
      invoiced: true, 
      invoice_id: invoiceId,
      status: 'invoiced'  // Update status to invoiced
    })
    .in('id', salesIds)
    .select()
  
  if (error) throw error
  return data
}

/**
 * Mark sales as paid without creating a Xero invoice.
 * Used for sales already paid outside Odin (cash, bank transfer).
 * Clears them from accruing invoices list.
 */
export const markSalesAsPaidWithoutInvoice = async (salesIds) => {
  const { data, error } = await supabase
    .from('sales')
    .update({
      invoiced: true,
      status: 'paid',
      invoice_id: null
    })
    .in('id', salesIds)
    .select()

  if (error) throw error
  return data
}

/**
 * Update sale status
 * When status changes to 'packed', 'delivered', or 'collected', the database trigger will auto-assign consignment_no
 * The updated record with the new consignment_no is returned
 *
 * @param {string} saleId - UUID of the sale to update
 * @param {string} status - New status value
 * @returns {Promise<Object>} Updated sale record (includes consignment_no if status='packed', 'delivered', or 'collected')
 */
/**
 * Pack all items in a sale: mark as packed and assign tray numbers.
 * Shared by updateSaleStatus (bulk) and used as the single source of truth
 * for packing logic. Returns updated items with their tray numbers.
 */
export const packSaleItems = async (saleId) => {
  // Mark all unpacked items as packed
  await supabase
    .from('sale_items')
    .update({ packed: true })
    .eq('sale_id', saleId)
    .eq('packed', false)

  // Assign tray numbers to items that don't have one yet
  const { data: unnumberedItems } = await supabase
    .from('sale_items')
    .select('id')
    .eq('sale_id', saleId)
    .is('item_number', null)
    .order('id')

  if (unnumberedItems?.length) {
    for (const item of unnumberedItems) {
      const itemNumber = await generateNextItemNumber()
      await supabase
        .from('sale_items')
        .update({ item_number: itemNumber })
        .eq('id', item.id)
    }
  }

  // Return updated items so callers can merge tray numbers into state
  const { data: updatedItems, error } = await supabase
    .from('sale_items')
    .select('id, item_number, packed')
    .eq('sale_id', saleId)
    .order('id')

  if (error) throw error
  return updatedItems
}

export const updateSaleStatus = async (saleId, status) => {
  // Guard: prevent status regression on invoice-linked sales
  if (status !== 'invoiced' && status !== 'paid') {
    const { data: currentSale } = await supabase
      .from('sales')
      .select('invoiced, invoice_id, status')
      .eq('id', saleId)
      .single()

    if (currentSale?.invoiced && currentSale?.invoice_id) {
      throw new Error(`Cannot change status: this sale is linked to an invoice. Remove the invoice first.`)
    }
    if (currentSale?.status === 'paid') {
      throw new Error(`Cannot change status: this sale is marked as paid.`)
    }
  }

  // Database trigger (BEFORE UPDATE) assigns consignment_no when status becomes packed/delivered/collected/sent
  const packingStatuses = ['packed', 'delivered', 'collected', 'sent']

  const { error } = await supabase
    .from('sales')
    .update({ status })
    .eq('id', saleId)

  if (error) throw error

  let updatedItems = null

  if (packingStatuses.includes(status)) {
    updatedItems = await packSaleItems(saleId)
  }

  // When reverting to pending, unpack all items
  if (status === 'pending') {
    await supabase
      .from('sale_items')
      .update({ packed: false })
      .eq('sale_id', saleId)
      .eq('packed', true)
  }

  // Re-fetch sale row to reliably get trigger-assigned consignment_no
  const { data: saleData, error: fetchError } = await supabase
    .from('sales')
    .select('id, status, consignment_no')
    .eq('id', saleId)
    .single()

  if (fetchError) throw fetchError

  return { ...saleData, updatedItems }
}

// Generate the next consignment number
export const generateConsignmentNumber = async () => {
  try {
    // Get the highest existing consignment number
    const { data: sales, error } = await supabase
      .from('sales')
      .select('consignment_no')
      .not('consignment_no', 'is', null)
      .order('consignment_no', { ascending: false })
      .limit(100)

    if (error) throw error

    // Extract numbers and find the max
    const existingNumbers = sales
      .map(sale => sale.consignment_no)
      .filter(no => no && no.startsWith('CONS-'))
      .map(no => parseInt(no.split('-')[1]))
      .filter(num => !isNaN(num))

    const nextNumber = existingNumbers.length > 0
      ? Math.max(...existingNumbers) + 1
      : 1

    return `CONS-${nextNumber.toString().padStart(4, '0')}`
  } catch (error) {
    console.error('Error generating consignment number:', error)
    throw new Error('Failed to generate consignment number: ' + error.message)
  }
}

/**
 * Manually update or clear consignment_no on a sale record
 * Used for manual override/correction of consignment numbers
 *
 * @param {string} saleId - UUID of the sale to update
 * @param {string|null} consignmentNo - New consignment number (CONS-NNNN format) or null to clear
 * @returns {Promise<Object>} Updated sale record
 * @throws {Error} If consignmentNo format is invalid
 */
export const updateConsignmentNumber = async (saleId, consignmentNo) => {
  // Validate format if not null
  if (consignmentNo !== null && !/^CONS-\d{4}$/.test(consignmentNo)) {
    throw new Error('Invalid consignment number format. Must be CONS-NNNN (e.g., CONS-0001)')
  }

  const { data, error } = await supabase
    .from('sales')
    .update({ consignment_no: consignmentNo })
    .eq('id', saleId)
    .select()

  if (error) throw error
  return data[0]
}

export const disregardSale = async (saleId, disregarded = true) => {
  const { data, error } = await supabase
    .from('sales')
    .update({ disregarded })
    .eq('id', saleId)
    .select()

  if (error) throw error
  return data[0]
}

export const updateSaleItemQuantity = async (saleItemId, quantity) => {
  const { data, error } = await supabase
    .from('sale_items')
    .update({ quantity })
    .eq('id', saleItemId)
    .select()

  if (error) throw error
  return data[0]
}

export const updateSaleItemPacked = async (saleItemId, packed) => {
  let updateData = { packed }

  // If marking as packed and item doesn't have an item_number, assign one
  if (packed) {
    // Check if item already has an item_number
    const { data: existingItem, error: fetchError } = await supabase
      .from('sale_items')
      .select('item_number')
      .eq('id', saleItemId)
      .single()

    if (fetchError) throw fetchError

    // Only generate item_number if one doesn't exist
    if (!existingItem.item_number) {
      const itemNumber = await generateNextItemNumber()
      updateData.item_number = itemNumber
    }
  }

  const { data, error } = await supabase
    .from('sale_items')
    .update(updateData)
    .eq('id', saleItemId)
    .select()

  if (error) throw error
  return data[0]
}

// Generate the next item_number (tray number)
export const generateNextItemNumber = async () => {
  try {
    // Get the highest existing item_number
    const { data, error } = await supabase
      .from('sale_items')
      .select('item_number')
      .not('item_number', 'is', null)
      .order('item_number', { ascending: false })
      .limit(1)

    if (error) throw error

    const nextNumber = data && data.length > 0 && data[0].item_number
      ? data[0].item_number + 1
      : 1

    return nextNumber
  } catch (error) {
    console.error('Error generating item number:', error)
    throw new Error('Failed to generate item number: ' + error.message)
  }
}

/**
 * Determine the product category reference code for a set of sale IDs.
 * Checks whether the sale items are fresh mushrooms (FM), fruiting blocks (B), or both (FM+B).
 * Fresh items have product_type_id set; block items have block_format_id set.
 */
const getProductCategoryRef = async (saleIDs) => {
  try {
    const { data: items } = await supabase
      .from('sale_items')
      .select('product_type_id, block_format_id')
      .in('sale_id', saleIDs)

    if (!items || items.length === 0) return 'FM'

    const hasFresh = items.some(i => i.product_type_id != null)
    const hasBlocks = items.some(i => i.block_format_id != null)

    if (hasFresh && hasBlocks) return 'FM+B'
    if (hasBlocks) return 'B'
    return 'FM'
  } catch (err) {
    console.warn('[getProductCategoryRef] Falling back to FM:', err.message)
    return 'FM'
  }
}

/**
 * Xero-First Invoice Creation (Task 5 v2)
 * Creates invoice directly in Xero, then stores minimal reference in Odin
 */

/**
 * Create invoice in Xero via Edge Function
 * Returns Xero invoice ID, invoice number, and other details
 */
export const createInvoiceInXero = async (customerID, branchID, saleIDs) => {
  try {
    // 1. Fetch sales with product details
    const { data: sales, error: salesError } = await supabase
      .from('sales')
      .select(`
        *,
        sale_items (
          product_id,
          product_type_id,
          quantity,
          price,
          tray_number,
          products (strain),
          product_types (name)
        )
      `)
      .in('id', saleIDs)
      .eq('invoiced', false)

    if (salesError) throw salesError
    if (!sales || sales.length === 0) {
      throw new Error('No uninvoiced sales found')
    }

    // 2. Get customer Xero contact ID and reference code (customer-level mapping)
    const { data: customerData } = await supabase
      .from('customers')
      .select('name, xero_contact_id, reference_code')
      .eq('id', customerID)
      .single()

    const contactID = customerData?.xero_contact_id

    if (!contactID) {
      throw new Error('Customer not mapped to Xero contact. Please map the customer in Settings > Integrations.')
    }

    // 3. Generate invoice reference: {family_ref} {cust_ref}-{branch_ref} {YYMMDD}
    let branchReference = null
    if (branchID) {
      const { data: branch } = await supabase
        .from('branches')
        .select('name, customer_id, reference_code')
        .eq('id', branchID)
        .single()

      if (branch) {
        const { count } = await supabase
          .from('branches')
          .select('id', { count: 'exact', head: true })
          .eq('customer_id', branch.customer_id)

        // Determine dominant product family from sale items
        const familyRef = await getProductCategoryRef(saleIDs)
        const custCode = customerData.reference_code || (customerData.name || '').substring(0, 4).toUpperCase()
        const today = toLocalDateString(new Date()).replace(/-/g, '').substring(2) // YYMMDD
        if (count > 1) {
          const branchCode = branch.reference_code || branch.name.substring(0, 3).toUpperCase()
          branchReference = `${familyRef} ${custCode}-${branchCode} ${today}`
        } else {
          branchReference = `${familyRef} ${custCode} ${today}`
        }
      }
    }

    // 4. Build raw line items — formatting happens server-side in Edge Function
    // Apply discount_percent to get effective price (promotional/volume discounts)
    const rawLineItems = sales.flatMap(sale =>
      sale.sale_items.map(item => {
        const discount = parseFloat(item.discount_percent || 0)
        const effectivePrice = item.price * (1 - discount / 100)
        return {
          product_name: `${item.products?.strain || ''} ${item.product_types?.name || ''}`.trim() || 'Unknown Product',
          quantity: item.quantity,
          price: effectivePrice,
          tray_number: item.tray_number || null,
        }
      })
    )

    // 5. Generate idempotency key from branch + sale IDs (prevents duplicate invoices on retry)
    const idempotencyKey = `create_${branchID}_${[...saleIDs].sort().join('_')}`

    // 6. Call xero-invoices Edge Function with 'create' action
    // Invoice number is assigned by Xero's auto-sequencing (configured in Xero Default Settings)
    // DB writes (invoice record + sales marking) are handled server-side for atomicity
    const { data, error } = await supabase.functions.invoke('xero-invoices', {
      body: {
        action: 'create',
        contactID: contactID,
        rawLineItems: rawLineItems,
        reference: branchReference,
        date: toLocalDateString(new Date()),
        idempotencyKey,
        customerId: customerID,
        branchId: branchID,
        saleIds: saleIDs
      }
    })

    if (error) throw error
    if (!data.success) {
      throw new Error(data.error || 'Failed to create invoice in Xero')
    }

    // Warn if Xero succeeded but DB writes failed (orphaned Xero invoice)
    if (data.warning) {
      console.warn('[createInvoiceInXero] Server-side DB warning:', data.warning)
    }

    const invoice = data.invoice

    // 7. Fetch and store PDF from Xero (non-blocking)
    if (invoice?.id) {
      supabase.functions.invoke('xero-invoices', {
        body: {
          action: 'fetch-pdf',
          invoiceId: invoice.id,
          xeroInvoiceId: data.invoiceID
        }
      }).then(({ data: pdfData, error: pdfError }) => {
        if (pdfError) {
          console.error('[createInvoiceInXero] Failed to fetch PDF:', pdfError)
        } else if (pdfData && pdfData.success) {
          console.log('[createInvoiceInXero] PDF fetched and stored:', pdfData.pdfUrl)
        }
      }).catch(err => {
        console.error('[createInvoiceInXero] PDF fetch error:', err)
      })
    }

    return {
      success: true,
      invoice: invoice,
      xeroInvoiceID: data.invoiceID,
      xeroInvoiceNumber: data.invoiceNumber,
      onlinePaymentUrl: data.onlinePaymentUrl,
      warning: data.warning,
      creditApplied: data.creditApplied,
      creditTotalApplied: data.creditTotalApplied,
      creditRemainingDue: data.creditRemainingDue,
      creditAllocations: data.creditAllocations
    }
  } catch (error) {
    console.error('[createInvoiceInXero] Error:', error)
    throw error
  }
}

/**
 * Batch send transactions to Xero
 * Groups transactions by branch and creates one invoice per branch
 */
export const sendInvoicesToXero = async (selectedTransactions) => {
  try {
    // Group transactions by branch
    const branchMap = {}
    selectedTransactions.forEach(transaction => {
      const branchKey = transaction.branch_id || 'no-branch'
      if (!branchMap[branchKey]) {
        branchMap[branchKey] = {
          branch_id: transaction.branch_id,
          customer_id: transaction.customer_id,
          saleIDs: []
        }
      }
      branchMap[branchKey].saleIDs.push(transaction.sale_id)
    })

    // For each branch, create invoice directly in Xero
    const results = []
    for (const [branchKey, groupData] of Object.entries(branchMap)) {
      try {
        const result = await createInvoiceInXero(
          groupData.customer_id,
          groupData.branch_id,
          groupData.saleIDs
        )

        results.push({
          success: true,
          invoice_id: result.invoice.id,
          xero_invoice_id: result.xeroInvoiceID,
          xero_invoice_number: result.xeroInvoiceNumber,
          branch_id: groupData.branch_id,
          branch_key: branchKey
        })
      } catch (error) {
        results.push({
          success: false,
          branch_id: groupData.branch_id,
          branch_key: branchKey,
          error: error.message
        })
      }
    }

    return results
  } catch (error) {
    console.error('[sendInvoicesToXero] Error:', error)
    throw error
  }
}

/**
 * Fetch customer statement data from Xero via the xero-invoices Edge Function.
 * Returns structured statement data for PDF rendering.
 */
export const fetchXeroStatement = async (customerId, fromDate, toDate) => {
  const { data, error } = await supabase.functions.invoke('xero-invoices', {
    body: {
      action: 'xero-statement',
      customer_id: customerId,
      from_date: fromDate,
      to_date: toDate
    }
  })
  if (error) throw error
  if (!data?.success) throw new Error(data?.error || 'Failed to fetch statement')
  return data.data
}

/**
 * DEPRECATED: Local invoice number generation
 * Kept for backward compatibility with historical invoices
 * New invoices use Xero invoice numbers
 */
export const generateInvoiceNumber = async () => {
  try {
    // Get the farm information to get the starting number
    const farmInfo = await fetchFarmInformation()
    
    if (!farmInfo) {
      throw new Error('Farm information not found. Please set up farm information in Settings.')
    }
    
    // Get the highest existing invoice number
    const { data: lastInvoice, error } = await supabase
      .from('invoices')
      .select('invoice_no')
      .order('invoice_no', { ascending: false })
      .limit(1)
    
    if (error) throw error
    
    let nextNumber = farmInfo.invoice_number_start || 1
    
    if (lastInvoice && lastInvoice.length > 0) {
      const lastNumber = parseInt(lastInvoice[0].invoice_no.split('-')[1])
      if (!isNaN(lastNumber) && lastNumber >= nextNumber) {
        nextNumber = lastNumber + 1
      }
    }
    
    const numberString = nextNumber.toString();
    const paddedNumber = numberString.padStart(3, '0');
    return `INV-${paddedNumber}`
  } catch (error) {
    console.error('Error generating invoice number:', error)
    throw new Error('Failed to generate invoice number: ' + error.message)
  }
}

// New functions for customer-grouped invoice dashboard
export const fetchCustomersWithUninvoicedSales = async () => {
  const { data, error } = await supabase
    .from('sales')
    .select(`
      *,
      customer:customers(id, name, email, phone, xero_contact_id, transmission),
      branch:branches(id, name, address, address_line_1, email, phone, customer_type:customer_types(id, name)),
      sale_items:sale_items(
        *,
        product:products(strain),
        product_type:product_types(name),
        block_format:block_formats(id, name, weight_kg),
        growing_material:growing_materials(id, name, category, unit_label, reference_code),
        price_tier:price_tiers!sale_items_applied_price_tier_id_fkey(id, name, display_name, multiplier)
      )
    `)
    .or('status.eq.delivered,status.eq.collected,status.eq.sent,status.is.null') // Include delivered, collected, sent or legacy null status sales
    .eq('invoiced', false)
    .eq('disregarded', false) // Only include non-disregarded sales
    .is('market_event_id', null) // Exclude market sales (Square handles payment)
    .order('date', { ascending: true })

  if (error) throw error

  // Filter out sales where all items have zero price (samples/gifts)
  const filteredData = data.filter(sale => {
    return sale.sale_items.some(item => parseFloat(item.price) > 0)
  })

  // Group by customer and calculate branch totals
  const customerGroups = {}

  filteredData.forEach(sale => {
    const customerId = sale.customer.id
    const customerName = sale.customer.name

    // Skip sales with invalid non-null branch_id (e.g., string "main" instead of UUID)
    if (sale.branch_id && !UUID_REGEX.test(sale.branch_id)) {
      console.warn('[fetchCustomersWithUninvoicedSales] Skipping sale with invalid branch_id:', {
        sale_id: sale.id,
        branch_id: sale.branch_id,
        customer: sale.customer.name
      })
      return
    }

    // Use 'main' as synthetic key for sales without a branch
    const branchId = sale.branch_id || 'main'
    const branchName = sale.branch?.name || 'Main'
    const branchAddress = sale.branch?.address || ''

    // Calculate sale total
    const saleTotal = sale.sale_items.reduce((sum, item) => sum + parseFloat(item.sub_total), 0)

    // Initialize customer group if not exists
    if (!customerGroups[customerId]) {
      customerGroups[customerId] = {
        customer: {
          id: customerId,
          name: customerName,
          email: sale.customer.email,
          phone: sale.customer.phone,
          xero_contact_id: sale.customer.xero_contact_id || null
        },
        branches: {}
      }
    }

    // Initialize branch if not exists
    if (!customerGroups[customerId].branches[branchId]) {
      customerGroups[customerId].branches[branchId] = {
        id: branchId,
        name: branchName,
        address: branchAddress,
        customer_type: sale.branch?.customer_type || null,
        total: 0,
        salesCount: 0
      }
    }

    // Add to branch total
    customerGroups[customerId].branches[branchId].total += saleTotal
    customerGroups[customerId].branches[branchId].salesCount += 1
  })
  
  // Convert to array format
  return Object.values(customerGroups).map(group => ({
    customer: group.customer,
    branches: Object.values(group.branches)
  }))
}

export const fetchLastInvoiceDateByCustomer = async () => {
  const { data, error } = await supabase
    .from('invoices')
    .select('customer_id, date')
    .order('date', { ascending: false })
  
  if (error) throw error
  
  // Get the most recent invoice date per customer
  const lastDates = {}
  data.forEach(invoice => {
    if (!lastDates[invoice.customer_id]) {
      lastDates[invoice.customer_id] = invoice.date
    }
  })
  
  return lastDates
}

export const fetchUninvoicedSalesByCustomerBranch = async (customerId, branchId = null) => {
  let query = supabase
    .from('sales')
    .select(`
      *,
      customer:customers(id, name, email, phone, transmission, payment_terms),
      branch:branches(id, name, address, customer_type:customer_types(id, name)),
      sale_items:sale_items(
        *,
        product:products(strain, reference_code),
        product_type:product_types(name, reference_code),
        block_format:block_formats(id, name, weight_kg, reference_code),
        growing_material:growing_materials(id, name, category, unit_label, reference_code),
        price_tier:price_tiers!sale_items_applied_price_tier_id_fkey(id, name, display_name, multiplier)
      )
    `)
    .eq('customer_id', customerId)
    .eq('invoiced', false)
    .or('status.eq.delivered,status.eq.collected,status.eq.sent,status.is.null')
    .or('disregarded.is.null,disregarded.eq.false') // Exclude disregarded sales
    .order('date', { ascending: true })

  // Filter by branch if specified
  if (branchId && branchId !== 'main') {
    query = query.eq('branch_id', branchId)
  } else if (branchId === 'main') {
    query = query.is('branch_id', null)
  }

  const { data, error } = await query
  if (error) throw error

  // Filter out sales where all items have zero price (samples/gifts)
  const filteredData = data.filter(sale => {
    // Keep sales that have at least one item with a price > 0
    return sale.sale_items.some(item => parseFloat(item.price) > 0)
  })

  return filteredData
}

export const fetchAllInvoices = async () => {
  const { data, error } = await supabase
    .from('invoices')
    .select(`
      *,
      customer:customers(name, email, payment_terms, xero_contact_id),
      branch:branches(name, address, customer_type:customer_types(id, name)),
      xero_mapping:xero_invoice_mappings!odin_invoice_id(xero_invoice_number, xero_invoice_id)
    `)
    .order('date', { ascending: false })

  if (error) throw error
  return data
}

/**
 * Fetch all invoices with balance data from invoice_balances view
 * Used by Invoice Dashboard to display amount_due and calculated_status
 * US-REC-001: Invoice Reconciliation
 */
/**
 * Fetch latest GoCardless payment status per invoice
 * Returns a Map of invoice_id → { gc_payment_status, gc_charge_date, gc_failure_reason }
 */
export const fetchGoCardlessPaymentStatuses = async () => {
  const { data, error } = await supabase
    .from('payments')
    .select('invoice_id, gocardless_payment_status, gocardless_charge_date, gocardless_failure_reason')
    .eq('source', 'gocardless')
    .not('invoice_id', 'is', null)
    .order('date', { ascending: false })

  if (error) throw error

  // Keep only the latest payment per invoice (first seen due to desc order)
  const statusMap = new Map()
  for (const row of data || []) {
    if (!statusMap.has(row.invoice_id)) {
      statusMap.set(row.invoice_id, {
        gc_payment_status: row.gocardless_payment_status,
        gc_charge_date: row.gocardless_charge_date,
        gc_failure_reason: row.gocardless_failure_reason
      })
    }
  }
  return statusMap
}

export const fetchInvoicesWithBalances = async () => {
  // Fetch invoices with related data
  const { data: invoices, error: invoicesError } = await supabase
    .from('invoices')
    .select(`
      *,
      customer:customers(id, name, email, payment_terms, xero_contact_id),
      branch:branches(name, address, customer_type:customer_types(id, name)),
      xero_mapping:xero_invoice_mappings!odin_invoice_id(xero_invoice_number, xero_invoice_id)
    `)
    .order('date', { ascending: false })

  if (invoicesError) throw invoicesError

  // Fetch balance data and GC payment statuses in parallel
  // GC statuses are non-critical — fail gracefully to avoid breaking invoice list
  const [balancesResult, gcStatusMap] = await Promise.all([
    supabase
      .from('invoice_balances')
      .select('invoice_id, amount_paid, amount_credited, amount_due, calculated_status, is_legacy_paid'),
    fetchGoCardlessPaymentStatuses().catch(err => {
      console.warn('[fetchInvoicesWithBalances] GoCardless status fetch failed, continuing without:', err)
      return new Map()
    })
  ])

  if (balancesResult.error) throw balancesResult.error

  // Create a lookup map for balances
  const balanceMap = new Map(
    balancesResult.data.map(b => [b.invoice_id, b])
  )

  // Merge balance data and GC payment statuses into invoices
  return invoices.map(invoice => {
    const balance = balanceMap.get(invoice.id)
    const gcStatus = gcStatusMap.get(invoice.id)
    return {
      ...invoice,
      // Balance fields from view
      amount_paid: balance?.amount_paid ?? 0,
      amount_credited: balance?.amount_credited ?? 0,
      amount_due: balance?.amount_due ?? invoice.total,
      calculated_status: balance?.calculated_status ?? (invoice.paid ? 'paid' : 'unpaid'),
      is_legacy_paid: balance?.is_legacy_paid ?? false,
      // GoCardless payment status fields
      gc_payment_status: gcStatus?.gc_payment_status ?? null,
      gc_charge_date: gcStatus?.gc_charge_date ?? null,
      gc_failure_reason: gcStatus?.gc_failure_reason ?? null
    }
  })
}

// Fetch draft invoices (is_draft = true, xero_invoice_id exists)
// Includes email_logs for failure indicators (Task 14)
export const fetchDraftInvoices = async () => {
  const { data, error } = await supabase
    .from('invoices')
    .select(`
      *,
      customer:customers(id, name, email, xero_contact_id),
      branch:branches(name, address, customer_type:customer_types(id, name)),
      xero_mapping:xero_invoice_mappings!odin_invoice_id(xero_invoice_number),
      email_logs(id, status, error_message, sent_at, created_at)
    `)
    .eq('is_draft', true)
    .not('xero_invoice_id', 'is', null)
    .order('date', { ascending: false})

  if (error) throw error

  // Enrich with email status metadata
  const enrichedData = data.map(invoice => {
    const failedLogs = invoice.email_logs?.filter(log => log.status === 'failed') || []
    const sentLogs = invoice.email_logs?.filter(log => log.status === 'sent_via_xero') || []
    const attemptCount = invoice.email_logs?.length || 0

    return {
      ...invoice,
      emailFailed: failedLogs.length > 0 && sentLogs.length === 0,
      emailAttemptCount: attemptCount,
      lastEmailError: failedLogs[0]?.error_message || null,
      canRetryEmail: attemptCount < 3 && failedLogs.length > 0
    }
  })

  return enrichedData
}

// Delete a draft invoice (returns sales to accruing state)
export const deleteDraftInvoice = async (invoiceId) => {
  // First, unlink sales from this invoice
  const { error: salesError } = await supabase
    .from('sales')
    .update({
      invoiced: false,
      invoice_id: null,
      status: 'delivered'
    })
    .eq('invoice_id', invoiceId)

  if (salesError) throw salesError

  // Then delete the invoice
  const { error: invoiceError } = await supabase
    .from('invoices')
    .delete()
    .eq('id', invoiceId)

  if (invoiceError) throw invoiceError

  return { success: true }
}

// Sync draft invoice status from Xero
export const syncDraftStatus = async (draftIds = []) => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('Not authenticated')
    }

    // Call Edge Function to sync status from Xero
    const { data, error } = await supabase.functions.invoke('xero-invoices', {
      body: {
        action: 'sync_draft_status',
        draft_ids: draftIds // If empty, syncs all drafts
      },
      headers: {
        Authorization: `Bearer ${session.access_token}`
      }
    })

    if (error) throw error
    return data
  } catch (err) {
    console.error('Error syncing draft status:', err)
    throw err
  }
}

export const fetchUninvoicedSalesByCustomer = async (customerId, includeDisregarded = false) => {
  let query = supabase
    .from('sales')
    .select(`
      *,
      customer:customers(id, name),
      branch:branches(id, name, address, customer_type:customer_types(id, name)),
      sale_items:sale_items(
        *,
        product:products(strain, reference_code),
        product_type:product_types(name, reference_code),
        block_format:block_formats(id, name, weight_kg, reference_code),
        growing_material:growing_materials(id, name, category, unit_label, reference_code),
        price_tier:price_tiers!sale_items_applied_price_tier_id_fkey(id, name, display_name, multiplier)
      )
    `)
    .eq('customer_id', customerId)
    .eq('invoiced', false)
    .or('status.eq.delivered,status.eq.collected,status.is.null')
    .order('date', { ascending: true })

  // Only exclude disregarded sales if includeDisregarded is false
  if (!includeDisregarded) {
    query = query.or('disregarded.is.null,disregarded.eq.false')
  }

  const { data, error } = await query
  if (error) throw error
  return data
}

export const fetchInvoicesByCustomer = async (customerId) => {
  const { data, error } = await supabase
    .from('invoices')
    .select(`
      *,
      customer:customers(name),
      branch:branches(name, address, customer_type:customer_types(id, name)),
      xero_mapping:xero_invoice_mappings!odin_invoice_id(xero_invoice_number)
    `)
    .eq('customer_id', customerId)
    .order('date', { ascending: false })

  if (error) throw error
  return data
}

// ==================== REPORTING DATA FUNCTIONS ====================

// Get the earliest sales date from the database
export const getEarliestSalesDate = async () => {
  try {
    const { data, error } = await supabase
      .from('sales')
      .select('date')
      .order('date', { ascending: true })
      .limit(1)
      .single()
    
    if (error) {
      console.warn('No sales data found, using default start date')
      return parseDateString('2020-01-01') // Fallback date
    }

    return parseDateString(data.date)
  } catch (error) {
    console.warn('Error fetching earliest sales date:', error)
    return parseDateString('2020-01-01') // Fallback date
  }
}

export const getSalesVolumeData = async (dateRange = '6months') => {
  try {
    let startDate = new Date()
    
    // Calculate date range
    switch (dateRange) {
      case '1month':
        startDate.setMonth(startDate.getMonth() - 1)
        break
      case '3months':
        startDate.setMonth(startDate.getMonth() - 3)
        break
      case '6months':
        startDate.setMonth(startDate.getMonth() - 6)
        break
      case '1year':
        startDate.setFullYear(startDate.getFullYear() - 1)
        break
      case 'ytd': {
        const currentYear = getCurrentYear()
        startDate = createDate(currentYear, 0, 1) // January 1st of current year
        break
      }
      case 'all': {
        startDate = await getEarliestSalesDate() // Get actual earliest date from database
        const startDateStr = dateToISOString(startDate)
        console.log('All Time - Start Date:', startDateStr)
        break
      }
      default:
        startDate.setMonth(startDate.getMonth() - 6)
    }

    const fetchStartDate = dateToISOString(startDate)
    console.log(`Fetching sales data from ${fetchStartDate} for range: ${dateRange}`)

    // Use pagination to get all records (Supabase has a 1000 record limit)
    const allSales = []
    let currentDate = fetchStartDate
    let hasMore = true
    let pageCount = 0

    while (hasMore && pageCount < 20) { // Safety limit
      pageCount++

      const { data, error } = await supabase
        .from('sales')
        .select(`
          id,
          date,
          status,
          disregarded,
          sale_items:sale_items(quantity, sub_total, product_type_id, block_format_id, growing_material_id)
        `)
        .gte('date', currentDate)
        .order('date', { ascending: true })
        .limit(1000)

      if (error) throw error

      if (data.length === 0) {
        hasMore = false
        break
      }

      // Add to our collection (skip first record if it's the same as last record from previous page)
      const startIndex = pageCount === 1 ? 0 : 1
      allSales.push(...data.slice(startIndex))

      if (data.length < 1000) {
        hasMore = false
      } else {
        currentDate = data[data.length - 1].date
      }
    }

    const data = allSales
    console.log(`Found ${data.length} sales records`)
    if (data.length > 0) {
      console.log('Date range of fetched data:', data[0].date, 'to', data[data.length - 1].date)
    }

    // Group by month and calculate totals (confirmed vs all-inclusive)
    const CONFIRMED_STATUSES = new Set(['packed', 'delivered', 'collected', 'sent', 'invoiced', 'paid'])
    const monthlyData = {}

    data.forEach(sale => {
      if (sale.disregarded) return

      const saleDate = parseDateString(sale.date)
      const monthYear = toLocalDateString(saleDate).slice(0, 7) // YYYY-MM format

      if (!monthlyData[monthYear]) {
        monthlyData[monthYear] = {
          month: monthYear,
          volume: 0, value: 0, salesCount: 0,
          allVolume: 0, allValue: 0, allSalesCount: 0
        }
      }

      // Mushroom-only totals: exclude fruiting blocks and growing materials whose
      // quantity is in units (logs, blocks) rather than kg. Mushroom line items
      // are identified by product_type_id.
      const mushroomItems = sale.sale_items.filter(item => item.product_type_id)
      if (mushroomItems.length === 0) return
      const saleVolume = mushroomItems.reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0)
      const saleValue = mushroomItems.reduce((sum, item) => sum + parseFloat(item.sub_total || 0), 0)

      // All-inclusive totals (for tooltips)
      monthlyData[monthYear].allVolume += saleVolume
      monthlyData[monthYear].allValue += saleValue
      monthlyData[monthYear].allSalesCount += 1

      // Confirmed-only totals (for display)
      if (CONFIRMED_STATUSES.has(sale.status)) {
        monthlyData[monthYear].volume += saleVolume
        monthlyData[monthYear].value += saleValue
        monthlyData[monthYear].salesCount += 1
      }
    })

    // Convert to array and format for chart
    const result = Object.values(monthlyData).map(item => {
      const [y, m] = item.month.split('-').map(Number)
      const date = new Date(y, m - 1, 1)
      const formattedMonth = date.toLocaleDateString('en-GB', {
        year: 'numeric',
        month: 'short'
      })

      return {
        month: formattedMonth,
        isoMonth: item.month,
        volume: Math.round(item.volume * 100) / 100,
        value: Math.round(item.value * 100) / 100,
        salesCount: item.salesCount,
        asp: item.volume > 0 ? Math.round((item.value / item.volume) * 100) / 100 : 0,
        allVolume: Math.round(item.allVolume * 100) / 100,
        allValue: Math.round(item.allValue * 100) / 100,
        allSalesCount: item.allSalesCount,
        allAsp: item.allVolume > 0 ? Math.round((item.allValue / item.allVolume) * 100) / 100 : 0
      }
    })

    return result
    
  } catch (error) {
    console.error('Error fetching sales volume data:', error)
    return []
  }
}

export const getSalesVolumeDataWeekly = async (dateRange = '6months') => {
  try {
    let startDate = new Date()
    
    // Calculate date range
    switch (dateRange) {
      case '1month':
        startDate.setMonth(startDate.getMonth() - 1)
        break
      case '3months':
        startDate.setMonth(startDate.getMonth() - 3)
        break
      case '6months':
        startDate.setMonth(startDate.getMonth() - 6)
        break
      case '1year':
        startDate.setFullYear(startDate.getFullYear() - 1)
        break
      case 'ytd': {
        const currentYear = getCurrentYear()
        startDate = createDate(currentYear, 0, 1) // January 1st of current year
        break
      }
      case 'all':
        startDate = await getEarliestSalesDate() // Get actual earliest date from database
        break
      default:
        startDate.setMonth(startDate.getMonth() - 6)
    }

    const weeklyFetchStartDate = dateToISOString(startDate)
    console.log(`Fetching weekly sales volume data from ${weeklyFetchStartDate} for range: ${dateRange}`)

    // Use pagination to get all records (Supabase has a 1000 record limit)
    const allSales = []
    const currentDateStr = dateToISOString(startDate)
    let currentDate = currentDateStr
    let hasMore = true
    let pageCount = 0

    while (hasMore && pageCount < 20) { // Safety limit
      pageCount++
      
      const { data, error } = await supabase
        .from('sales')
        .select(`
          id,
          date,
          sale_items:sale_items(quantity, sub_total)
        `)
        .gte('date', currentDate)
        .order('date', { ascending: true })
        .limit(1000)

      if (error) throw error

      if (data.length === 0) {
        hasMore = false
        break
      }

      // Add to our collection (skip first record if it's the same as last record from previous page)
      const startIndex = pageCount === 1 ? 0 : 1
      allSales.push(...data.slice(startIndex))

      if (data.length < 1000) {
        hasMore = false
      } else {
        currentDate = data[data.length - 1].date
      }
    }

    const data = allSales
    console.log(`Found ${data.length} weekly sales volume records`)

    // Group by week and calculate totals
    const weeklyData = {}
    
    data.forEach(sale => {
      const saleDate = parseDateString(sale.date)
      
      // Get the start of the week (Sunday)
      const weekStart = new Date(saleDate)
      weekStart.setDate(saleDate.getDate() - saleDate.getDay())
      
      // Format the week start date as YYYY-MM-DD for consistent keys
      const weekKey = toLocalDateString(weekStart)
      
      if (!weeklyData[weekKey]) {
        // Get week number of the year
        const yearStart = getYearStartFromDate(weekStart)
        const weekNumber = Math.ceil(((weekStart - yearStart) / 86400000 + 1) / 7)
        
        weeklyData[weekKey] = {
          weekStart: weekStart,
          weekNumber: weekNumber,
          volume: 0,
          value: 0,
          salesCount: 0
        }
      }
      
      // Calculate sale totals
      const saleVolume = sale.sale_items.reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0)
      const saleValue = sale.sale_items.reduce((sum, item) => sum + parseFloat(item.sub_total || 0), 0)
      
      weeklyData[weekKey].volume += saleVolume
      weeklyData[weekKey].value += saleValue
      weeklyData[weekKey].salesCount += 1
    })

    // Convert to array and format for chart
    const result = Object.values(weeklyData)
      .sort((a, b) => new Date(a.weekStart) - new Date(b.weekStart))
      .map(item => {
        const monthShort = item.weekStart.toLocaleDateString('en-GB', { 
          month: 'short' 
        })
        
        return {
          week: `W${item.weekNumber} ${monthShort}`,
          volume: Math.round(item.volume * 100) / 100,
          value: Math.round(item.value * 100) / 100,
          salesCount: item.salesCount,
          asp: item.volume > 0 ? Math.round((item.value / item.volume) * 100) / 100 : 0
        }
      })
      
    return result
    
  } catch (error) {
    console.error('Error fetching weekly sales volume data:', error)
    return []
  }
}

export const getWeeklyOutputData = async (dateRange = '6months') => {
  try {
    let startDate = new Date()
    
    // Calculate date range
    switch (dateRange) {
      case '1month':
        startDate.setMonth(startDate.getMonth() - 1)
        break
      case '3months':
        startDate.setMonth(startDate.getMonth() - 3)
        break
      case '6months':
        startDate.setMonth(startDate.getMonth() - 6)
        break
      case '1year':
        startDate.setFullYear(startDate.getFullYear() - 1)
        break
      case 'ytd': {
        const currentYear = getCurrentYear()
        startDate = createDate(currentYear, 0, 1) // January 1st of current year
        break
      }
      case 'all':
        startDate = await getEarliestSalesDate() // Get actual earliest date from database
        break
      default:
        startDate.setMonth(startDate.getMonth() - 6)
    }

    const { data, error } = await supabase
      .from('sales')
      .select(`
        id,
        date,
        sale_items:sale_items(
          quantity, 
          sub_total,
          product:products(strain),
        product_type:product_types(name),
        block_format:block_formats(id, name, weight_kg),
        growing_material:growing_materials(id, name, category, unit_label, reference_code)
        )
      `)
      .gte('date', (() => dateToISOString(startDate))())
      .order('date', { ascending: true })

    if (error) throw error

    // Group by week
    const weeklyData = {}
    
    data.forEach(sale => {
      const saleDate = parseDateString(sale.date)
      const weekStart = new Date(saleDate)
      weekStart.setDate(saleDate.getDate() - saleDate.getDay()) // Start of week (Sunday)
      const weekKey = toLocalDateString(weekStart)
      
      if (!weeklyData[weekKey]) {
        const weekLabelDate = weekStart.toLocaleDateString('en-GB', { 
          month: 'short', 
          day: 'numeric'
        })
        
        weeklyData[weekKey] = {
          week: weekKey,
          weekLabel: `Week of ${weekLabelDate}`,
          mass: 0,
          value: 0,
          salesCount: 0
        }
      }
      
      // Calculate weekly totals
      const saleVolume = sale.sale_items.reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0)
      const saleValue = sale.sale_items.reduce((sum, item) => sum + parseFloat(item.sub_total || 0), 0)
      
      weeklyData[weekKey].mass += saleVolume
      weeklyData[weekKey].value += saleValue
      weeklyData[weekKey].salesCount += 1
    })

    // Convert to array and add target (you may want to make this configurable)
    const target = 90 // Weekly target from your screenshots
    
    return Object.values(weeklyData).map(item => ({
      week: item.weekLabel,
      mass: Math.round(item.mass * 10) / 10,
      value: Math.round(item.value * 100) / 100,
      target: target,
      salesCount: item.salesCount,
      asp: item.mass > 0 ? Math.round((item.value / item.mass) * 100) / 100 : 0
    }))
    
  } catch (error) {
    console.error('Error fetching weekly output data:', error)
    return []
  }
}

export const getWeeklySalesPerformanceData = async () => {
  try {
    // Get sales data for the past 6 months (26 weeks)
    const startDate = new Date()
    startDate.setMonth(startDate.getMonth() - 6)

    const { data, error } = await supabase
      .from('sales')
      .select(`
        id,
        date,
        sale_items:sale_items(
          quantity, 
          sub_total
        )
      `)
      .gte('date', (() => dateToISOString(startDate))())
      .order('date', { ascending: true })

    if (error) throw error

    // Group by week (starting Sunday like in the screenshot)
    const weeklyData = {}

    // Pre-seed all weeks in the rolling window so weeks without sales still appear
    const today = new Date()
    const currentWeekStart = new Date(today)
    currentWeekStart.setHours(0, 0, 0, 0)
    currentWeekStart.setDate(today.getDate() - today.getDay())
    for (let i = 0; i < 26; i++) {
      const weekStart = new Date(currentWeekStart)
      weekStart.setDate(currentWeekStart.getDate() - i * 7)
      const weekKey = toLocalDateString(weekStart)
      const yearStart = getYearStartFromDate(weekStart)
      const weekNumber = Math.ceil(((weekStart - yearStart) / 86400000 + 1) / 7)
      const monthShort = weekStart.toLocaleDateString('en-GB', { month: 'short' })
      weeklyData[weekKey] = {
        weekStart,
        weekNumber,
        year: weekStart.getFullYear(),
        month: monthShort,
        mass: 0,
        value: 0,
        salesCount: 0
      }
    }

    data.forEach(sale => {
      const saleDate = parseDateString(sale.date)

      // Get the start of the week (Sunday)
      const weekStart = new Date(saleDate)
      weekStart.setDate(saleDate.getDate() - saleDate.getDay())

      // Skip future-dated sales — don't materialise weeks past the current one
      if (weekStart > currentWeekStart) return

      // Format the week start date as YYYY-MM-DD for consistent keys
      const weekKey = toLocalDateString(weekStart)

      if (!weeklyData[weekKey]) {
        // Get week number of the year
        const yearStart = getYearStartFromDate(weekStart)
        const weekNumber = Math.ceil(((weekStart - yearStart) / 86400000 + 1) / 7)

        const monthShort = weekStart.toLocaleDateString('en-GB', { month: 'short' })

        weeklyData[weekKey] = {
          weekStart: weekStart,
          weekNumber: weekNumber,
          year: weekStart.getFullYear(),
          month: monthShort,
          mass: 0,
          value: 0,
          salesCount: 0
        }
      }

      // Calculate sale totals
      const saleVolume = sale.sale_items.reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0)
      const saleValue = sale.sale_items.reduce((sum, item) => sum + parseFloat(item.sub_total || 0), 0)

      weeklyData[weekKey].mass += saleVolume
      weeklyData[weekKey].value += saleValue
      weeklyData[weekKey].salesCount += 1
    })

    // Convert to array and format like the screenshot
    const target = 90 // Weekly target as shown in screenshot
    
    const result = Object.values(weeklyData)
      .sort((a, b) => new Date(a.weekStart) - new Date(b.weekStart))
      .map(item => {
        const formattedDate = item.weekStart.toLocaleDateString('en-GB', { 
          day: '2-digit', 
          month: '2-digit', 
          year: 'numeric' 
        })
        
        return {
          date: formattedDate,
          year: item.year,
          month: item.month,
          week: item.weekNumber,
          mass: Math.round(item.mass * 10) / 10,
          value: Math.round(item.value * 100) / 100,
          target: target,
          asp: item.mass > 0 ? Math.round((item.value / item.mass) * 100) / 100 : 0,
          salesCount: item.salesCount
        }
      })
      
    return result

  } catch (error) {
    console.error('Error fetching weekly sales performance data:', error)
    return []
  }
}

export const getWeeklyStrainBreakdownData = async () => {
  try {
    const startDate = new Date()
    startDate.setMonth(startDate.getMonth() - 6)

    const { data, error } = await supabase
      .from('sales')
      .select(`
        date,
        sale_items:sale_items(
          quantity,
          product:products(
            strain,
            strain_family:strain_families(name)
          )
        )
      `)
      .gte('date', (() => dateToISOString(startDate))())
      .order('date', { ascending: true })

    if (error) throw error

    const today = new Date()
    const currentWeekStart = new Date(today)
    currentWeekStart.setHours(0, 0, 0, 0)
    currentWeekStart.setDate(today.getDate() - today.getDay())

    // Pre-seed 26 weeks (oldest to newest)
    const weeks = []
    const weekMap = {}
    for (let i = 25; i >= 0; i--) {
      const weekStart = new Date(currentWeekStart)
      weekStart.setDate(currentWeekStart.getDate() - i * 7)
      const weekKey = toLocalDateString(weekStart)
      const yearStart = getYearStartFromDate(weekStart)
      const weekNumber = Math.ceil(((weekStart - yearStart) / 86400000 + 1) / 7)
      const bucket = {
        weekKey,
        weekStart,
        weekNumber,
        year: weekStart.getFullYear(),
        strains: {} // { strainName: kg }
      }
      weeks.push(bucket)
      weekMap[weekKey] = bucket
    }

    const strainTotals = {}

    data.forEach(sale => {
      const saleDate = parseDateString(sale.date)
      const weekStart = new Date(saleDate)
      weekStart.setDate(saleDate.getDate() - saleDate.getDay())
      if (weekStart > currentWeekStart) return
      const weekKey = toLocalDateString(weekStart)
      const bucket = weekMap[weekKey]
      if (!bucket) return

      sale.sale_items.forEach(item => {
        const strainName = item.product?.strain_family?.name || item.product?.strain || 'Unknown'
        const qty = parseFloat(item.quantity || 0)
        bucket.strains[strainName] = (bucket.strains[strainName] || 0) + qty
        strainTotals[strainName] = (strainTotals[strainName] || 0) + qty
      })
    })

    // Only include strains that had any sales in the window, sorted by total desc
    const strains = Object.entries(strainTotals)
      .filter(([, total]) => total > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([name]) => name)

    // Newest week first for display
    const rows = weeks.slice().reverse().map(w => ({
      weekKey: w.weekKey,
      weekStart: w.weekStart,
      weekNumber: w.weekNumber,
      year: w.year,
      strains: w.strains
    }))

    return { strains, rows, totals: strainTotals }
  } catch (error) {
    console.error('Error fetching weekly strain breakdown data:', error)
    return { strains: [], rows: [], totals: {} }
  }
}

export const getProductPerformanceData = async (dateRange = '6months') => {
  try {
    let startDate = new Date()
    
    // Calculate date range
    switch (dateRange) {
      case '1month':
        startDate.setMonth(startDate.getMonth() - 1)
        break
      case '3months':
        startDate.setMonth(startDate.getMonth() - 3)
        break
      case '6months':
        startDate.setMonth(startDate.getMonth() - 6)
        break
      case '1year':
        startDate.setFullYear(startDate.getFullYear() - 1)
        break
      case 'ytd': {
        const currentYear = getCurrentYear()
        startDate = createDate(currentYear, 0, 1) // January 1st of current year
        break
      }
      case 'all':
        startDate = await getEarliestSalesDate() // Get actual earliest date from database
        break
      default:
        startDate.setMonth(startDate.getMonth() - 6)
    }

    const { data, error } = await supabase
      .from('sales')
      .select(`
        id,
        date,
        sale_items:sale_items(
          quantity, 
          sub_total,
          product:products(strain),
        product_type:product_types(name),
        block_format:block_formats(id, name, weight_kg),
        growing_material:growing_materials(id, name, category, unit_label, reference_code)
        )
      `)
      .gte('date', (() => dateToISOString(startDate))())
      .order('date', { ascending: true })

    if (error) throw error

    // Group by product strain and week
    const productWeeklyData = {}
    
    data.forEach(sale => {
      const saleDate = parseDateString(sale.date)
      const weekStart = new Date(saleDate)
      weekStart.setDate(saleDate.getDate() - saleDate.getDay())
      const weekKey = toLocalDateString(weekStart)
      
      sale.sale_items.forEach(item => {
        const strain = item.product?.strain || 'Unknown'
        const key = `${weekKey}-${strain}`

        if (!productWeeklyData[key]) {
          // Calculate ISO week number
          const tempDate = new Date(weekStart)
          tempDate.setHours(0, 0, 0, 0)
          tempDate.setDate(tempDate.getDate() + 3 - ((tempDate.getDay() + 6) % 7))
          const week1 = new Date(tempDate.getFullYear(), 0, 4)
          const weekNum = 1 + Math.round(((tempDate - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7)
          const monthShort = weekStart.toLocaleDateString('en-GB', { month: 'short' })

          productWeeklyData[key] = {
            week: weekKey,
            weekLabel: `W${weekNum} ${monthShort}`,
            strain: strain,
            quantity: 0,
            value: 0
          }
        }
        
        productWeeklyData[key].quantity += parseFloat(item.quantity || 0)
        productWeeklyData[key].value += parseFloat(item.sub_total || 0)
      })
    })

    // Group by week for stacked chart format — produce both value and mass datasets
    const weeklyValueData = {}
    const weeklyMassData = {}
    Object.values(productWeeklyData).forEach(item => {
      if (!weeklyValueData[item.week]) {
        weeklyValueData[item.week] = { week: item.weekLabel, total: 0 }
        weeklyMassData[item.week] = { week: item.weekLabel, total: 0 }
      }

      weeklyValueData[item.week][item.strain] = Math.round(item.value * 100) / 100
      weeklyValueData[item.week].total += item.value

      weeklyMassData[item.week][item.strain] = Math.round(item.quantity * 100) / 100
      weeklyMassData[item.week].total += item.quantity
    })

    const formatOutput = (obj) => Object.values(obj).map(item => ({
      ...item,
      total: Math.round(item.total * 100) / 100
    }))

    return {
      value: formatOutput(weeklyValueData),
      mass: formatOutput(weeklyMassData)
    }
    
  } catch (error) {
    console.error('Error fetching product performance data:', error)
    return []
  }
}

export const getCustomerPerformanceData = async (dateRange = '6months') => {
  try {
    let startDate = new Date()

    switch (dateRange) {
      case '1month':
        startDate.setMonth(startDate.getMonth() - 1)
        break
      case '3months':
        startDate.setMonth(startDate.getMonth() - 3)
        break
      case '6months':
        startDate.setMonth(startDate.getMonth() - 6)
        break
      case '1year':
        startDate.setFullYear(startDate.getFullYear() - 1)
        break
      case 'ytd': {
        const currentYear = getCurrentYear()
        startDate = createDate(currentYear, 0, 1)
        break
      }
      case 'all':
        startDate = await getEarliestSalesDate()
        break
      default:
        startDate.setMonth(startDate.getMonth() - 6)
    }

    const { data, error } = await supabase
      .from('sales')
      .select(`
        id,
        date,
        customer:customers(id, name),
        sale_items:sale_items(
          quantity,
          sub_total
        )
      `)
      .gte('date', (() => dateToISOString(startDate))())
      .order('date', { ascending: true })

    if (error) throw error

    const customerWeeklyData = {}

    data.forEach(sale => {
      const customerName = sale.customer?.name || 'Unknown'
      const saleDate = parseDateString(sale.date)
      const weekStart = new Date(saleDate)
      weekStart.setDate(saleDate.getDate() - saleDate.getDay())
      const weekKey = toLocalDateString(weekStart)

      const key = `${weekKey}-${customerName}`

      if (!customerWeeklyData[key]) {
        const tempDate = new Date(weekStart)
        tempDate.setHours(0, 0, 0, 0)
        tempDate.setDate(tempDate.getDate() + 3 - ((tempDate.getDay() + 6) % 7))
        const week1 = new Date(tempDate.getFullYear(), 0, 4)
        const weekNum = 1 + Math.round(((tempDate - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7)
        const monthShort = weekStart.toLocaleDateString('en-GB', { month: 'short' })

        customerWeeklyData[key] = {
          week: weekKey,
          weekLabel: `W${weekNum} ${monthShort}`,
          customer: customerName,
          quantity: 0,
          value: 0
        }
      }

      sale.sale_items.forEach(item => {
        customerWeeklyData[key].quantity += parseFloat(item.quantity || 0)
        customerWeeklyData[key].value += parseFloat(item.sub_total || 0)
      })
    })

    const weeklyValueData = {}
    const weeklyMassData = {}
    Object.values(customerWeeklyData).forEach(item => {
      if (!weeklyValueData[item.week]) {
        weeklyValueData[item.week] = { week: item.weekLabel, total: 0 }
        weeklyMassData[item.week] = { week: item.weekLabel, total: 0 }
      }

      weeklyValueData[item.week][item.customer] = Math.round(item.value * 100) / 100
      weeklyValueData[item.week].total += item.value

      weeklyMassData[item.week][item.customer] = Math.round(item.quantity * 100) / 100
      weeklyMassData[item.week].total += item.quantity
    })

    const formatOutput = (obj) => Object.values(obj).map(item => ({
      ...item,
      total: Math.round(item.total * 100) / 100
    }))

    return {
      value: formatOutput(weeklyValueData),
      mass: formatOutput(weeklyMassData)
    }

  } catch (error) {
    console.error('Error fetching customer performance data:', error)
    return []
  }
}

export const getDeliveryFrequencyData = async () => {
  try {
    // Look at last 6 weeks of sales to identify active customers and products
    const sixWeeksAgo = new Date()
    sixWeeksAgo.setDate(sixWeeksAgo.getDate() - 42)
    
    const { data, error } = await supabase
      .from('sales')
      .select(`
        id,
        date,
        customer_id,
        branch_id,
        status,
        customer:customers(id, name),
        branch:branches(id, name, customer_type:customer_types(id, name)),
        sale_items:sale_items(
          quantity,
          sub_total,
          product:products(strain),
        product_type:product_types(name),
        block_format:block_formats(id, name, weight_kg),
        growing_material:growing_materials(id, name, category, unit_label, reference_code)
        )
      `)
      .gte('date', (() => dateToISOString(sixWeeksAgo))())
      .eq('status', 'delivered') // Only delivered sales count as actual deliveries
      .order('date', { ascending: false })

    if (error) throw error

    // Build customer-product delivery matrix
    const deliveryMatrix = {}
    const today = new Date()
    
    data.forEach(sale => {
      const customerKey = sale.branch_id 
        ? `${sale.customer.name} - ${sale.branch.name}`
        : sale.customer.name
        
      if (!deliveryMatrix[customerKey]) {
        deliveryMatrix[customerKey] = {
          customerId: sale.customer_id,
          customerName: sale.customer.name,
          branchId: sale.branch_id,
          branchName: sale.branch?.name,
          customerType: sale.branch?.customer_type?.name || 'Unknown',
          products: {}
        }
      }
      
      // Track latest delivery for each product strain
      sale.sale_items.forEach(item => {
        const strain = item.product?.strain || 'Unknown'
        const saleDate = parseDateString(sale.date)
        
        // Only keep the most recent delivery date for each product-customer combination
        if (!deliveryMatrix[customerKey].products[strain] || 
            saleDate > parseDateString(deliveryMatrix[customerKey].products[strain].lastDeliveryDate)) {
          
          const daysSince = Math.floor((today - saleDate) / (1000 * 60 * 60 * 24))
          
          deliveryMatrix[customerKey].products[strain] = {
            lastDeliveryDate: sale.date,
            daysSince: daysSince,
            lastQuantity: parseFloat(item.quantity || 0),
            lastValue: parseFloat(item.sub_total || 0)
          }
        }
      })
    })

    // Filter out customers/products with minimal activity (less than 2 deliveries in period)
    const activeMatrix = {}
    
    Object.entries(deliveryMatrix).forEach(([customerKey, customerData]) => {
      const activeProducts = {}
      
      // Only include products that have been delivered at least once recently
      Object.entries(customerData.products).forEach(([strain, productData]) => {
        // Include if delivered within last 6 weeks
        if (productData.daysSince <= 42) {
          activeProducts[strain] = productData
        }
      })
      
      // Only include customers with at least one active product
      if (Object.keys(activeProducts).length > 0) {
        activeMatrix[customerKey] = {
          ...customerData,
          products: activeProducts
        }
      }
    })

    return activeMatrix
    
  } catch (error) {
    console.error('Error fetching delivery frequency data:', error)
    return {}
  }
}

export const updateInvoicePaidStatus = async (invoiceId, paidStatus) => {
  const { data, error } = await supabase
    .from('invoices')
    .update({ paid: paidStatus })
    .eq('id', invoiceId)
    .select()
    .single()
  
  if (error) throw error
  return data
}

export const fetchInvoiceWithSales = async (invoiceId) => {
  // First get the invoice details
  const { data: invoice, error: invoiceError } = await supabase
    .from('invoices')
    .select(`
      *,
      customer:customers(id, name),
      branch:branches(id, name, address, customer_type:customer_types(id, name))
    `)
    .eq('id', invoiceId)
    .single()

  if (invoiceError) throw invoiceError

  // Then get all sales associated with this invoice
  const { data: sales, error: salesError } = await supabase
    .from('sales')
    .select(`
      *,
      customer:customers(id, name),
      branch:branches(id, name, address, customer_type:customer_types(id, name)),
      sale_items:sale_items(
        *,
        product:products(strain),
        product_type:product_types(name),
        block_format:block_formats(id, name, weight_kg),
        growing_material:growing_materials(id, name, category, unit_label, reference_code),
        price_tier:price_tiers!sale_items_applied_price_tier_id_fkey(id, name, display_name, multiplier)
      )
    `)
    .eq('invoice_id', invoiceId)
    .order('date', { ascending: true })
    
  if (salesError) throw salesError
  
  // Group sales by branch for the invoice structure
  const branches = {}
  const processedSales = []
  
  sales.forEach(sale => {
    // Handle main company (no branch)
    const branchKey = sale.branch_id || 'main'
    const branchName = sale.branch?.name || 'Main Company'
    
    if (!branches[branchKey]) {
      branches[branchKey] = {
        id: branchKey,
        name: branchName,
        address: sale.branch?.address || null
      }
    }
    
    processedSales.push(sale)
  })
  
  // Create the invoice data structure expected by the PDF generator
  const invoiceData = {
    invoiceNumber: invoice.invoice_no,
    customer: invoice.customer,
    date: invoice.date,
    sales: processedSales,
    branches: Object.values(branches),
    balance: 0.00 // TODO: Calculate actual balance if needed
  }
  
  return { invoice, invoiceData }
}

// Customer Balance Calculation Functions
// US-REC-001: Updated to use customer_balances view which includes credit notes
export const fetchCustomerBalances = async () => {
  try {
    // Use the customer_balances view which properly calculates:
    // - total_credits (sum of payments)
    // - total_outstanding (sum of amount_due from invoice_balances)
    // - current_balance (total_credits - total_outstanding)
    const { data: balances, error } = await supabase
      .from('customer_balances')
      .select('customer_id, total_credits, total_outstanding, current_balance')

    if (error) throw error

    // Transform to object keyed by customer_id for backwards compatibility
    // Note: positive balance = customer owes money, negative = credit available
    const balanceMap = {}
    balances?.forEach(row => {
      // Invert current_balance: view shows credits - outstanding
      // UI expects positive = owes money, so use total_outstanding
      balanceMap[row.customer_id] = parseFloat(row.total_outstanding) || 0
    })

    return balanceMap
  } catch (error) {
    console.error('Error fetching customer balances:', error)
    throw error
  }
}

/**
 * Fetch detailed customer balance information using customer_balances view
 * US-REC-001: Provides complete balance breakdown for display
 * @param {string} customerId - Customer ID to fetch balance for
 * @returns {Object} { total_credits, total_outstanding, current_balance }
 */
export const fetchCustomerBalanceDetail = async (customerId) => {
  try {
    const { data, error } = await supabase
      .from('customer_balances')
      .select('total_credits, total_outstanding, current_balance')
      .eq('customer_id', customerId)
      .single()

    if (error) {
      // If no balance record found (no invoices), return zeros
      if (error.code === 'PGRST116') {
        return {
          total_credits: 0,
          total_outstanding: 0,
          current_balance: 0
        }
      }
      throw error
    }

    return {
      total_credits: parseFloat(data.total_credits) || 0,
      total_outstanding: parseFloat(data.total_outstanding) || 0,
      current_balance: parseFloat(data.current_balance) || 0
    }
  } catch (error) {
    console.error('Error fetching customer balance detail:', error)
    throw error
  }
}

// US-P4-001 Task 4: Send invoice email via Edge Function
/**
 * Send invoice email via Xero API
 * Xero handles email delivery, templates, payment links, and reminders
 * @param {string} invoiceId - Odin invoice ID
 * @returns {Promise<object>} Response with success status and details
 */
export const sendInvoiceViaXero = async (invoiceId) => {
  try {
    // Get user session for authentication
    const { data: { session } } = await supabase.auth.getSession()

    if (!session) {
      throw new Error('User not authenticated')
    }

    // Call xero-send-invoice Edge Function
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/xero-send-invoice`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ invoiceId }),
      }
    )

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Failed to send invoice via Xero')
    }

    return data
  } catch (error) {
    console.error('Error sending invoice via Xero:', error)
    throw error
  }
}

// Deprecated: Use sendInvoiceViaXero instead (Xero-first approach)
export const sendInvoiceEmail = sendInvoiceViaXero

// Import Session Management Functions
export const createImportSession = async (sessionData) => {
  const { data, error } = await supabase
    .from('import_sessions')
    .insert([sessionData])
    .select()
    .single()

  if (error) throw error
  return data
}

export const updateImportSession = async (sessionId, updates) => {
  const { data, error } = await supabase
    .from('import_sessions')
    .update(updates)
    .eq('id', sessionId)
    .select()
    .single()

  if (error) throw error
  return data
}

export const fetchImportSession = async (sessionId) => {
  const { data, error } = await supabase
    .from('import_sessions')
    .select('*')
    .eq('id', sessionId)
    .single()

  if (error) throw error
  return data
}

export const fetchUserImportSessions = async (userId, options = {}) => {
  const {
    limit = 50,
    timeout = 10000,
    selectAll = false,
    includeSessionState = false
  } = options

  // Create abort controller for timeout
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeout)

  try {
    // Select only necessary columns for performance
    // Can override with selectAll: true if full data needed
    // session_state excluded by default as it can be very large
    let selectColumns = selectAll
      ? '*'
      : 'id,status,file_name,total_rows,created_at,entity_type'

    if (includeSessionState && !selectAll) {
      selectColumns += ',session_state'
    }

    const { data, error } = await supabase
      .from('import_sessions')
      .select(selectColumns)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit)
      .abortSignal(controller.signal)

    clearTimeout(timeoutId)

    if (error) throw error
    return data
  } catch (error) {
    clearTimeout(timeoutId)

    // Handle timeout errors specifically
    // Supabase wraps AbortError with code "20" and includes details
    const isAbortError =
      error.name === 'AbortError' ||
      error.code === '20' ||
      (error.details && error.details.includes('AbortError'))

    if (isAbortError) {
      const timeoutError = new Error('Query timeout: Failed to fetch import sessions')
      timeoutError.code = 'TIMEOUT'
      throw timeoutError
    }

    throw error
  }
}

// Re-Import Functions
/**
 * Fetch import sessions with staging data available for re-import
 * Only returns sessions that have completed and have associated staging records
 */
export const fetchImportSessionsForReimport = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('import_sessions')
      .select(`
        id,
        file_name,
        total_rows,
        valid_rows,
        created_at,
        completed_at,
        entity_type,
        template_id,
        parent_session_id,
        status
      `)
      .eq('user_id', userId)
      .eq('entity_type', 'sales') // Currently only supporting sales re-import
      .in('status', ['completed', 'failed']) // Only completed or failed sessions
      .order('created_at', { ascending: false })

    if (error) throw error

    // Filter to only sessions with staging data
    // We'll verify staging data exists by checking staging table
    const sessionsWithStaging = []
    for (const session of data || []) {
      const { count, error: countError } = await supabase
        .from('import_staging_sales')
        .select('id', { count: 'exact', head: true })
        .eq('session_id', session.id)

      if (!countError && count > 0) {
        sessionsWithStaging.push({
          ...session,
          stagingRecordCount: count
        })
      }
    }

    return sessionsWithStaging
  } catch (error) {
    console.error('[fetchImportSessionsForReimport] Error:', error)
    throw error
  }
}

/**
 * Re-import from a previous import session with duplicate detection
 * Creates a new session linked to the original via parent_session_id
 */
export const reImportFromSession = async (originalSessionId, userId, skipDuplicates = true) => {
  try {
    // Fetch original session
    const originalSession = await fetchImportSession(originalSessionId)
    if (!originalSession) {
      throw new Error('Original import session not found')
    }

    // Fetch staging records from original session
    const { data: stagingRecords, error: stagingError } = await supabase
      .from('import_staging_sales')
      .select('*')
      .eq('session_id', originalSessionId)
      .order('row_number')

    if (stagingError) throw stagingError
    if (!stagingRecords || stagingRecords.length === 0) {
      throw new Error('No staging data found for this session')
    }

    // Generate fingerprints and check for duplicates (using mapped entity IDs)
    const { getDuplicateDetectionSummary } = await import('./utils/recordFingerprintManager.js')
    const mappedDataRecords = stagingRecords.map(r => r.mapped_data)
    const duplicateSummary = await getDuplicateDetectionSummary(
      mappedDataRecords,
      userId,
      originalSession.template_id,
      { useEntityIds: true }
    )

    // Determine which records to import
    let recordsToImport = stagingRecords
    if (skipDuplicates && duplicateSummary.fingerprintMap) {
      recordsToImport = stagingRecords.filter((record, index) => {
        const fingerprintData = duplicateSummary.fingerprintMap.get(index)
        // Only import if it's a new record (not already imported)
        return !fingerprintData?.existing || fingerprintData.action !== 'imported'
      })
    }

    // Create new import session linked to original
    const newSessionData = {
      user_id: userId,
      file_name: `${originalSession.file_name} (re-import)`,
      file_size: originalSession.file_size,
      total_rows: recordsToImport.length,
      entity_type: 'sales',
      status: 'ready',
      parent_session_id: originalSessionId,
      template_id: originalSession.template_id,
      column_mappings: originalSession.column_mappings,
      import_settings: originalSession.import_settings
    }

    const newSession = await createImportSession(newSessionData)

    // Create new staging records for the re-import
    const newStagingRecords = recordsToImport.map((record, index) => ({
      session_id: newSession.id,
      row_number: index + 1,
      original_data: record.original_data,
      mapped_data: record.mapped_data,
      validation_status: record.validation_status,
      validation_errors: record.validation_errors,
      created_entities: record.created_entities,
      imported: false
    }))

    await createStagingSalesRecords(newStagingRecords)

    return {
      newSession,
      duplicateSummary,
      totalRecords: stagingRecords.length,
      recordsToImport: recordsToImport.length,
      skippedDuplicates: stagingRecords.length - recordsToImport.length
    }
  } catch (error) {
    console.error('[reImportFromSession] Error:', error)
    throw error
  }
}

// Import Staging Functions
export const createStagingSalesRecords = async (stagingData) => {
  // Handle edge cases
  if (!stagingData || stagingData.length === 0) {
    return []
  }

  const BATCH_SIZE = 1000
  const batches = []

  // Split into chunks
  for (let i = 0; i < stagingData.length; i += BATCH_SIZE) {
    batches.push(stagingData.slice(i, i + BATCH_SIZE))
  }

  // Process batches sequentially
  const allInsertedRecords = []

  for (let i = 0; i < batches.length; i++) {
    try {
      const { data, error } = await supabase
        .from('import_staging_sales')
        .insert(batches[i])
        .select()

      if (error) throw error

      allInsertedRecords.push(...data)

      // Log progress for debugging when processing multiple batches
      if (batches.length > 1) {
        console.log(`[createStagingSalesRecords] Inserted batch ${i + 1}/${batches.length} (${data.length} records)`)
      }
    } catch (error) {
      // Enhance error message with batch context
      const batchStart = i * BATCH_SIZE + 1
      const batchEnd = Math.min((i + 1) * BATCH_SIZE, stagingData.length)
      throw new Error(`Failed to insert batch ${i + 1}/${batches.length} (records ${batchStart}-${batchEnd}): ${error.message}`)
    }
  }

  return allInsertedRecords
}

export const fetchStagingSalesRecords = async (sessionId) => {
  const query = supabase
    .from('import_staging_sales')
    .select('*')
    .eq('session_id', sessionId)

  return await paginateQuery(query, { orderBy: 'row_number' })
}

export const updateStagingSalesRecord = async (recordId, updates) => {
  const { data, error } = await supabase
    .from('import_staging_sales')
    .update(updates)
    .eq('id', recordId)
    .select()
    .single()

  if (error) throw error
  return data
}

export const batchUpdateStagingSalesRecords = async (updates) => {
  const { data, error } = await supabase
    .from('import_staging_sales')
    .upsert(updates)
    .select()

  if (error) throw error
  return data
}

// Commit staged records to actual sales
export const commitStagingImportToSales = async (sessionId) => {
  try {
    // Fetch import session to get template_id
    const session = await fetchImportSession(sessionId)
    const templateId = session.template_id

    // Fetch all valid staging records for this session with pagination
    // CRITICAL: Use pagination to handle large imports (>1000 records)
    const query = supabase
      .from('import_staging_sales')
      .select('*')
      .eq('session_id', sessionId)
      .eq('validation_status', 'valid')
      .eq('imported', false)

    const stagingRecords = await paginateQuery(query, { orderBy: 'row_number' })
    console.log(`[commitStagingImportToSales] Fetched ${stagingRecords.length} staging records for session ${sessionId}`)

    const importedSales = []
    const errors = []
    const auditRecords = [] // Collect audit trail data

    // CRITICAL FIX: Group staging records by (date, customer_id, branch_id) before processing
    // This ensures multiple CSV rows for the same sale create ONE sale with MULTIPLE items
    const groupKey = (record) => {
      const mapped = record.mapped_data
      return `${mapped.date}|${mapped.customer_id}|${mapped.branch_id || 'null'}`
    }

    const groupedRecords = new Map()
    for (const record of stagingRecords) {
      const key = groupKey(record)
      if (!groupedRecords.has(key)) {
        groupedRecords.set(key, [])
      }
      groupedRecords.get(key).push(record)
    }

    console.log(`[commitStagingImportToSales] Grouped ${stagingRecords.length} records into ${groupedRecords.size} sales`)

    // Process each group as a single sale with multiple items
    for (const [key, groupRecords] of groupedRecords.entries()) {
      try {
        // Use first record for sale-level data (date, customer, branch, consignment_no)
        const firstRecord = groupRecords[0]
        const mappedData = firstRecord.mapped_data

        // Collect ALL items from this group
        const items = groupRecords.map(record => {
          const entities = record.created_entities || {}
          const mapped = record.mapped_data
          return {
            product_id: entities.product_id || mapped.product_id,
            product_type_id: entities.product_type_id || mapped.product_type_id,
            quantity: parseFloat(mapped.quantity || 0),
            price: parseFloat(mapped.price || 0)
          }
        })

        // Create ONE sale with ALL items from the group
        const saleData = {
          date: mappedData.date,
          customer_id: mappedData.customer_id,
          branch_id: mappedData.branch_id === null ? null : mappedData.branch_id,
          consignment_no: mappedData.consignment_no || null,
          status: 'delivered', // Import as delivered since they're being imported
          items: items
        }

        const sale = await createSaleWithItems(saleData)
        importedSales.push(sale)

        // Mark ALL staging records in this group as imported
        for (const record of groupRecords) {
          await supabase
            .from('import_staging_sales')
            .update({
              imported: true,
              imported_id: sale.id
            })
            .eq('id', record.id)

          // Generate fingerprint for each record for audit trail
          try {
            const mapped = record.mapped_data
            const entities = record.created_entities || {}
            const fingerprintData = {
              date: mapped.date,
              customer_id: mapped.customer_id,
              branch_id: mapped.branch_id || null,
              product_id: entities.product_id || mapped.product_id,
              quantity: mapped.quantity,
              price: mapped.price
            }
            const fingerprint = await generateRecordFingerprint(fingerprintData, { useEntityIds: true })
            auditRecords.push({
              sessionId,
              templateId,
              fingerprint,
              action: 'imported',
              mappedData: {
                customer_id: mapped.customer_id,
                branch_id: mapped.branch_id,
                product_id: entities.product_id || mapped.product_id
              }
            })
          } catch (fingerprintError) {
            console.error(`Failed to generate fingerprint for record ${record.id}:`, fingerprintError)
            // Continue with import even if fingerprint generation fails
          }
        }

      } catch (groupError) {
        console.error(`Failed to import grouped sale for key ${key}:`, groupError)
        // Mark all records in this group as failed
        for (const record of groupRecords) {
          errors.push({
            recordId: record.id,
            rowNumber: record.row_number,
            error: groupError.message
          })
        }
      }
    }

    // Save audit trail records in batch
    if (auditRecords.length > 0) {
      try {
        await saveImportHistory(auditRecords)
        console.log(`[commitStagingImportToSales] Saved ${auditRecords.length} audit records for session ${sessionId}`)
      } catch (auditError) {
        console.error('[commitStagingImportToSales] Failed to save audit records:', auditError)
        // Log error but don't fail the import
      }
    }

    // Update session as completed or failed
    const sessionStatus = errors.length === 0 ? 'completed' : 'failed'
    const sessionUpdate = {
      status: sessionStatus,
      completed_at: new Date().toISOString(),
      error_message: errors.length > 0 ? `Import completed with ${errors.length} errors` : null
    }

    await updateImportSession(sessionId, sessionUpdate)

    return {
      success: true,
      importedCount: importedSales.length,
      errorCount: errors.length,
      errors: errors
    }

  } catch (error) {
    console.error('Failed to commit staging import:', error)

    // Mark session as failed
    await updateImportSession(sessionId, {
      status: 'failed',
      error_message: error.message
    })

    throw error
  }
}

// Import Validation Rules
export const fetchValidationRules = async (entityType) => {
  const { data, error } = await supabase
    .from('import_validation_rules')
    .select('*')
    .eq('entity_type', entityType)
    .eq('active', true)

  if (error) throw error
  return data
}

// Fuzzy Matching Utility Functions
export const findSimilarCustomers = async (searchTerm, threshold = 0.9) => {
  // Use Supabase's similarity search if available, or fetch all and filter client-side
  const { data, error } = await supabase
    .from('customers')
    .select('id, name, email')
    .eq('active', true)

  if (error) throw error

  // Client-side fuzzy matching (we'll enhance this with a proper fuzzy search library)
  const filtered = data.filter(customer => {
    const similarity = calculateStringSimilarity(searchTerm.toLowerCase(), customer.name.toLowerCase())
    return similarity >= threshold
  })

  return filtered.sort((a, b) => {
    const aScore = calculateStringSimilarity(searchTerm.toLowerCase(), a.name.toLowerCase())
    const bScore = calculateStringSimilarity(searchTerm.toLowerCase(), b.name.toLowerCase())
    return bScore - aScore
  })
}

export const findSimilarProducts = async (searchTerm, threshold = 0.9) => {
  const { data, error } = await supabase
    .from('products')
    .select('id, strain, type, base_price_per_kg')
    .eq('active', true)

  if (error) throw error

  const filtered = data.filter(product => {
    const similarity = calculateStringSimilarity(searchTerm.toLowerCase(), product.strain.toLowerCase())
    return similarity >= threshold
  })

  return filtered.sort((a, b) => {
    const aScore = calculateStringSimilarity(searchTerm.toLowerCase(), a.strain.toLowerCase())
    const bScore = calculateStringSimilarity(searchTerm.toLowerCase(), b.strain.toLowerCase())
    return bScore - aScore
  })
}

export const findSimilarBranches = async (customerId, searchTerm, threshold = 0.9) => {
  const { data, error } = await supabase
    .from('branches')
    .select('id, name, customer_id')
    .eq('customer_id', customerId)
    .eq('active', true)

  if (error) throw error

  const filtered = data.filter(branch => {
    const similarity = calculateStringSimilarity(searchTerm.toLowerCase(), branch.name.toLowerCase())
    return similarity >= threshold
  })

  return filtered.sort((a, b) => {
    const aScore = calculateStringSimilarity(searchTerm.toLowerCase(), a.name.toLowerCase())
    const bScore = calculateStringSimilarity(searchTerm.toLowerCase(), b.name.toLowerCase())
    return bScore - aScore
  })
}

// Simple string similarity calculation (Dice coefficient)
const calculateStringSimilarity = (str1, str2) => {
  if (str1 === str2) return 1.0
  if (str1.length < 2 || str2.length < 2) return 0.0

  const bigrams1 = new Set()
  const bigrams2 = new Set()

  for (let i = 0; i < str1.length - 1; i++) {
    bigrams1.add(str1.substring(i, i + 2))
  }

  for (let i = 0; i < str2.length - 1; i++) {
    bigrams2.add(str2.substring(i, i + 2))
  }

  const intersection = new Set([...bigrams1].filter(x => bigrams2.has(x)))
  return (2.0 * intersection.size) / (bigrams1.size + bigrams2.size)
}

// Import History Functions
export const createImportHistory = async (historyData) => {
  const { data, error } = await supabase
    .from('import_history')
    .insert([historyData])
    .select()
    .single()

  if (error) throw error
  return data
}

export const fetchUserImportHistory = async (userId) => {
  const { data, error } = await supabase
    .from('import_history')
    .select(`
      *,
      import_sessions(file_name, total_rows, status)
    `)
    .eq('user_id', userId)
    .order('completed_at', { ascending: false })

  if (error) throw error
  return data
}

// Test Data Cleanup Functions
export const cleanupTestSales = async () => {
  // Delete sale items for test sales first (foreign key constraint)
  const { error: saleItemsError } = await supabase
    .from('sale_items')
    .delete()
    .in('sale_id', 
      supabase
        .from('sales')
        .select('id')
        .or('consignment_no.is.null,consignment_no.like.%Test%')
    )
  
  if (saleItemsError) throw saleItemsError

  // Delete sales with null consignment_no or containing 'Test'
  const { error: salesError } = await supabase
    .from('sales')
    .delete()
    .or('consignment_no.is.null,consignment_no.like.%Test%')
  
  if (salesError) throw salesError
}

export const cleanupTestCustomers = async () => {
  // Delete customers with 'Test' in name or email containing 'test'
  const { error } = await supabase
    .from('customers')
    .delete()
    .or('name.like.%Test%,email.like.%test%')
  
  if (error) throw error
}

export const cleanupTestProducts = async () => {
  // Delete products with 'Test' in strain
  const { error } = await supabase
    .from('products')
    .delete()
    .like('strain', '%Test%')

  if (error) throw error
}

export const cleanupTestInvoices = async () => {
  // Delete test invoices
  const { error } = await supabase
    .from('invoices')
    .delete()
    .or('invoice_no.like.%Test%,invoice_no.like.INV-%')
  
  if (error) throw error
}

export const cleanupAllTestData = async () => {
  console.log('🧹 Starting test data cleanup...')
  
  try {
    await cleanupTestInvoices()
    console.log('✅ Cleaned up test invoices')
    
    await cleanupTestSales()
    console.log('✅ Cleaned up test sales and sale items')
    
    await cleanupTestCustomers()
    console.log('✅ Cleaned up test customers')
    
    await cleanupTestProducts()
    console.log('✅ Cleaned up test products')
    
    console.log('🎉 Test data cleanup completed successfully!')
  } catch (error) {
    console.error('❌ Test data cleanup failed:', error)
    throw error
  }
}

// Development utility to remove sales with 0 items (data corruption)
export const cleanupOrphanedSales = async () => {
  console.log('🧹 Cleaning up orphaned sales (0 items)...')
  
  // Get sales with 0 items
  const { data: orphanedSales, error: fetchError } = await supabase
    .from('sales')
    .select(`
      id,
      consignment_no,
      sale_items:sale_items(id)
    `)
    .not('sale_items', 'is', null)
    .having('count(sale_items)', 'eq', 0)
  
  if (fetchError) throw fetchError
  
  if (orphanedSales && orphanedSales.length > 0) {
    const orphanedIds = orphanedSales.map(sale => sale.id)
    
    const { error: deleteError } = await supabase
      .from('sales')
      .delete()
      .in('id', orphanedIds)
    
    if (deleteError) throw deleteError
    
    console.log(`✅ Removed ${orphanedSales.length} orphaned sales`)
  } else {
    console.log('✅ No orphaned sales found')
  }
}

export const getTestDataStats = async () => {
  const stats = {}
  
  // Count test sales
  const { count: testSales } = await supabase
    .from('sales')
    .select('*', { count: 'exact', head: true })
    .or('consignment_no.is.null,consignment_no.like.%Test%')
  
  stats.testSales = testSales || 0
  
  // Count test customers
  const { count: testCustomers } = await supabase
    .from('customers')
    .select('*', { count: 'exact', head: true })
    .or('name.like.%Test%,email.like.%test%')
  
  stats.testCustomers = testCustomers || 0
  
  // Count test products
  const { count: testProducts } = await supabase
    .from('products')
    .select('*', { count: 'exact', head: true })
    .like('name', '%Test%')
  
  stats.testProducts = testProducts || 0
  
  // Count test invoices
  const { count: testInvoices } = await supabase
    .from('invoices')
    .select('*', { count: 'exact', head: true })
    .or('invoice_no.like.%Test%,invoice_no.like.INV-%')
  
  stats.testInvoices = testInvoices || 0
  
  return stats
}

export const getCumulativeSalesData = async (dateRange = '6months') => {
  try {
    let startDate = new Date()
    
    // Calculate date range
    switch (dateRange) {
      case '1month':
        startDate.setMonth(startDate.getMonth() - 1)
        break
      case '3months':
        startDate.setMonth(startDate.getMonth() - 3)
        break
      case '6months':
        startDate.setMonth(startDate.getMonth() - 6)
        break
      case '1year':
        startDate.setFullYear(startDate.getFullYear() - 1)
        break
      case 'ytd': {
        const currentYear = getCurrentYear()
        startDate = createDate(currentYear, 0, 1) // January 1st of current year
        break
      }
      case 'all':
        startDate = await getEarliestSalesDate() // Get actual earliest date from database
        break
      default:
        startDate.setMonth(startDate.getMonth() - 6)
    }

    const { data, error } = await supabase
      .from('sales')
      .select(`
        id,
        date,
        sale_items:sale_items(sub_total),
        customers(name),
        branches(name)
      `)
      .gte('date', (() => dateToISOString(startDate))())
      .order('date', { ascending: true })

    if (error) throw error

    // Group by month and customer, calculating cumulative totals
    const monthlyCustomerData = {}
    const customerCumulatives = {}

    data.forEach(sale => {
      const saleDate = parseDateString(sale.date)
      const monthYear = toLocalDateString(saleDate).slice(0, 7) // YYYY-MM format
      const customerName = sale.customers?.name || sale.branches?.name || 'Unknown'
      
      // Initialize month if it doesn't exist
      if (!monthlyCustomerData[monthYear]) {
        monthlyCustomerData[monthYear] = {}
      }
      
      // Calculate sale value
      const saleValue = sale.sale_items.reduce((sum, item) => sum + parseFloat(item.sub_total || 0), 0)
      
      // Add to customer total for this month
      if (!monthlyCustomerData[monthYear][customerName]) {
        monthlyCustomerData[monthYear][customerName] = 0
      }
      monthlyCustomerData[monthYear][customerName] += saleValue
    })

    // Convert to cumulative data
    const sortedMonths = Object.keys(monthlyCustomerData).sort()
    const cumulativeData = []

    // Initialize cumulative totals for all customers
    const allCustomers = new Set()
    Object.values(monthlyCustomerData).forEach(monthData => {
      Object.keys(monthData).forEach(customer => allCustomers.add(customer))
    })

    allCustomers.forEach(customer => {
      customerCumulatives[customer] = 0
    })

    // Build cumulative data for each month
    sortedMonths.forEach(month => {
      const [y2, m2] = month.split('-').map(Number)
      const date = new Date(y2, m2 - 1, 1)
      const formattedMonth = date.toLocaleDateString('en-GB', {
        year: '2-digit',
        month: 'short'
      }).replace(' ', '-') // Format as "Jan-24"
      
      const monthData = { 
        month: formattedMonth
      }

      // Add this month's sales to each customer's cumulative total
      allCustomers.forEach(customer => {
        const monthlyAmount = monthlyCustomerData[month][customer] || 0
        customerCumulatives[customer] += monthlyAmount
        monthData[customer] = Math.round(customerCumulatives[customer] * 100) / 100
      })

      cumulativeData.push(monthData)
    })

    return cumulativeData

  } catch (error) {
    console.error('Error fetching cumulative sales data:', error)
    return []
  }
}

export const getCumulativeTotalSoldData = async (dateRange = '6months') => {
  try {
    let startDate = new Date()
    
    // Calculate date range
    switch (dateRange) {
      case '1month':
        startDate.setMonth(startDate.getMonth() - 1)
        break
      case '3months':
        startDate.setMonth(startDate.getMonth() - 3)
        break
      case '6months':
        startDate.setMonth(startDate.getMonth() - 6)
        break
      case '1year':
        startDate.setFullYear(startDate.getFullYear() - 1)
        break
      case 'ytd': {
        const currentYear = getCurrentYear()
        startDate = createDate(currentYear, 0, 1) // January 1st of current year
        break
      }
      case 'all':
        startDate = await getEarliestSalesDate() // Get actual earliest date from database
        break
      default:
        startDate.setMonth(startDate.getMonth() - 6)
    }

    const cumulativeFetchStartDate = dateToISOString(startDate)
    console.log(`Fetching cumulative total sold data from ${cumulativeFetchStartDate} for range: ${dateRange}`)

    // Use pagination to get all records (Supabase has a 1000 record limit)
    const allSales = []
    const currentDateStr = dateToISOString(startDate)
    let currentDate = currentDateStr
    let hasMore = true
    let pageCount = 0

    while (hasMore && pageCount < 20) { // Safety limit
      pageCount++
      
      const { data, error } = await supabase
        .from('sales')
        .select(`
          id,
          date,
          sale_items:sale_items(quantity)
        `)
        .gte('date', currentDate)
        .order('date', { ascending: true })
        .limit(1000)

      if (error) throw error

      if (data.length === 0) {
        hasMore = false
        break
      }

      // Add to our collection (skip first record if it's the same as last record from previous page)
      const startIndex = pageCount === 1 ? 0 : 1
      allSales.push(...data.slice(startIndex))

      if (data.length < 1000) {
        hasMore = false
      } else {
        currentDate = data[data.length - 1].date
      }
    }

    const data = allSales
    console.log(`Found ${data.length} sales records for cumulative total sold`)

    // Group by 6-month periods for more appropriate granularity for long-term view
    const periodData = {}
    
    data.forEach(sale => {
      const saleDate = parseDateString(sale.date)
      const year = saleDate.getFullYear()
      const month = saleDate.getMonth() + 1 // 1-based month
      
      // Create 6-month periods: H1 (Jan-Jun) and H2 (Jul-Dec)
      const halfYear = month <= 6 ? 'H1' : 'H2'
      const periodKey = `${year}-${halfYear}`
      
      if (!periodData[periodKey]) {
        periodData[periodKey] = {
          year: year,
          half: halfYear,
          totalVolume: 0,
          // Create date for sorting - use Jan 1 for H1, Jul 1 for H2
          sortDate: new Date(year, halfYear === 'H1' ? 0 : 6, 1)
        }
      }
      
      // Calculate sale volume
      const saleVolume = sale.sale_items.reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0)
      periodData[periodKey].totalVolume += saleVolume
    })

    // Convert to cumulative data
    const sortedPeriods = Object.values(periodData).sort((a, b) => a.sortDate - b.sortDate)
    const cumulativeData = []
    let cumulativeTotal = 0

    sortedPeriods.forEach(period => {
      cumulativeTotal += period.totalVolume
      cumulativeData.push({
        period: `${period.year}${period.half}`, // Format as "2023H1", "2023H2"
        displayYear: period.year.toString(),
        isH1: period.half === 'H1',
        cumulativeTotal: Math.round(cumulativeTotal * 10) / 10,
        sortDate: period.sortDate
      })
    })

    return cumulativeData
    
  } catch (error) {
    console.error('Error fetching cumulative total sold data:', error)
    return []
  }
}

export const getYearlyRevenueData = async () => {
  try {
    // Get all sales data from the beginning using pagination
    const startDate = await getEarliestSalesDate()
    
    // Use pagination to get all records
    let allSales = []
    let from = 0
    const batchSize = 1000
    let hasMore = true
    
    while (hasMore) {
      const { data, error } = await supabase
        .from('sales')
        .select(`
          id,
          date,
          sale_items:sale_items(sub_total)
        `)
        .gte('date', (() => dateToISOString(startDate))())
        .order('date', { ascending: true })
        .range(from, from + batchSize - 1)

      if (error) throw error
      
      if (data.length === 0) {
        hasMore = false
      } else {
        allSales = [...allSales, ...data]
        from += batchSize
        
        // Safety check to prevent infinite loops
        if (from > 50000) {
          console.warn('Reached safety limit for pagination')
          hasMore = false
        }
      }
    }
    
    // Debug: Log what we got
    console.log('getYearlyRevenueData - Found', allSales.length, 'sales records (paginated)')
    if (allSales.length > 0) {
      console.log('getYearlyRevenueData - Date range:', allSales[0].date, 'to', allSales[allSales.length - 1].date)
    }

    const data = allSales

    // Group by year and calculate revenue per week
    const yearlyData = {}
    
    data.forEach(sale => {
      const saleDate = parseDateString(sale.date)
      const year = saleDate.getFullYear()
      
      // Calculate total revenue for this sale
      const saleItems = sale.sale_items
      const saleTotal = saleItems ? saleItems.reduce((sum, item) => {
        return sum + (item.sub_total || 0)
      }, 0) : 0

      if (!yearlyData[year]) {
        yearlyData[year] = {
          totalRevenue: 0,
          weekCount: 0
        }
      }

      yearlyData[year].totalRevenue += saleTotal
    })

    // Calculate weeks per year (accounting for partial years)
    const currentYear = getCurrentYear()
    const now = new Date()
    const currentWeek = getWeekOfYear(now)
    
    Object.keys(yearlyData).forEach(year => {
      const yearNum = parseInt(year)
      if (yearNum === currentYear) {
        // For current year, use weeks elapsed so far
        yearlyData[year].weekCount = currentWeek
      } else {
        // For past years, use 52 weeks (standard year)
        yearlyData[year].weekCount = 52
      }
    })

    // Ensure all years from 2021-2025 are included, even with no sales
    const minYear = 2021
    const maxYear = 2025
    
    for (let year = minYear; year <= maxYear; year++) {
      if (!yearlyData[year]) {
        yearlyData[year] = {
          totalRevenue: 0,
          weekCount: year === currentYear ? currentWeek : 52
        }
      }
    }

    // Convert to chart format
    const chartData = Object.entries(yearlyData).map(([year, data]) => ({
      year: year,
      revenuePerWeek: data.weekCount > 0 ? Math.round((data.totalRevenue / data.weekCount) * 100) / 100 : 0,
      totalRevenue: Math.round(data.totalRevenue * 100) / 100
    })).sort((a, b) => parseInt(a.year) - parseInt(b.year))

    // Debug: Log final chart data
    console.log('getYearlyRevenueData - Final chart data:', chartData)

    return chartData

  } catch (error) {
    console.error('Error fetching yearly revenue data:', error)
    return []
  }
}

// Helper function to get week number of the year
function getWeekOfYear(date) {
  const start = getYearStartFromDate(date)
  const diff = date - start + (start.getTimezoneOffset() - date.getTimezoneOffset()) * 60 * 1000
  const oneWeek = 1000 * 60 * 60 * 24 * 7
  return Math.ceil(diff / oneWeek)
}

// Get seasonal sales data (aggregated by calendar month across all years)
export const getSeasonalSalesData = async () => {
  try {
    // Get all sales data from the beginning
    const startDate = await getEarliestSalesDate()
    
    const seasonalFetchStartDate = dateToISOString(startDate)
    console.log(`Fetching seasonal sales data from ${seasonalFetchStartDate}`)

    // Use pagination to get all records
    const allSales = []
    const currentDateStr = dateToISOString(startDate)
    let currentDate = currentDateStr
    let hasMore = true
    let pageCount = 0

    while (hasMore && pageCount < 20) { // Safety limit
      pageCount++
      
      const { data, error } = await supabase
        .from('sales')
        .select(`
          id,
          date,
          sale_items:sale_items(quantity)
        `)
        .gte('date', currentDate)
        .order('date', { ascending: true })
        .limit(1000)

      if (error) throw error

      if (data.length === 0) {
        hasMore = false
        break
      }

      // Add to our collection (skip first record if it's the same as last record from previous page)
      const startIndex = pageCount === 1 ? 0 : 1
      allSales.push(...data.slice(startIndex))

      if (data.length < 1000) {
        hasMore = false
      } else {
        currentDate = data[data.length - 1].date
      }
    }

    console.log(`Found ${allSales.length} sales records for seasonal analysis`)

    // Group by calendar month (across all years)
    const seasonalData = {}
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ]
    
    // Initialize all months
    monthNames.forEach(month => {
      seasonalData[month] = {
        month: month,
        volume: 0,
        salesCount: 0
      }
    })
    
    allSales.forEach(sale => {
      const saleDate = parseDateString(sale.date)
      const monthName = monthNames[saleDate.getMonth()]
      
      // Calculate sale volume
      const saleVolume = sale.sale_items.reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0)
      
      seasonalData[monthName].volume += saleVolume
      seasonalData[monthName].salesCount += 1
    })

    // Convert to array format for radar chart
    return monthNames.map(month => ({
      month: month,
      volume: Math.round(seasonalData[month].volume * 100) / 100,
      salesCount: seasonalData[month].salesCount
    }))
    
  } catch (error) {
    console.error('Error fetching seasonal sales data:', error)
    return []
  }
}

export const getCustomerStockingData = async (customerType = 'grocer') => {
  try {
    // Use last 6 months for volume calculations
    const sixMonthsAgo = new Date()
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6)
    
    // 30 days ago for staleness check
    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
    
    // Fetch sales data for selected customer type
    const { data, error } = await supabase
      .from('sales')
      .select(`
        id,
        date,
        customer_id,
        branch_id,
        status,
        customer:customers!inner(id, name),
        branch:branches(id, name, customer_type:customer_types(name)),
        sale_items:sale_items(
          quantity,
          sub_total,
          product:products(strain),
        product_type:product_types(name),
        block_format:block_formats(id, name, weight_kg),
        growing_material:growing_materials(id, name, category, unit_label, reference_code)
        )
      `)
      .gte('date', (() => dateToISOString(sixMonthsAgo))())
      .eq('status', 'delivered')
      // Note: Cannot filter on branch.customer_type.name in Supabase - will filter client-side below
      .order('date', { ascending: false })

    if (error) throw error

    // Filter sales by customer type (client-side since we can't filter nested joins in Supabase)
    const filteredData = data.filter(sale => {
      const branchType = sale.branch?.customer_type?.name
      return branchType?.toLowerCase() === customerType.toLowerCase()
    })

    // Calculate customer and product volumes
    const customerVolumes = {}
    const productVolumes = {}
    const deliveryMatrix = {}
    const today = new Date()

    filteredData.forEach(sale => {
      const customerKey = sale.branch_id 
        ? `${sale.customer.name} - ${sale.branch.name}`
        : sale.customer.name
      
      const saleDate = parseDateString(sale.date)
      const daysSinceSale = Math.floor((today - saleDate) / (1000 * 60 * 60 * 24))
      
      // Initialize customer in delivery matrix
      if (!deliveryMatrix[customerKey]) {
        deliveryMatrix[customerKey] = {
          customerId: sale.customer_id,
          customerName: sale.customer.name,
          branchId: sale.branch_id,
          branchName: sale.branch?.name,
          customerType: sale.branch?.customer_type?.name || 'Unknown',
          totalVolume: 0,
          lastSaleDate: null,
          isStale: true,
          products: {}
        }
        customerVolumes[customerKey] = 0
      }

      // Track customer's latest sale date and staleness
      if (!deliveryMatrix[customerKey].lastSaleDate || saleDate > new Date(deliveryMatrix[customerKey].lastSaleDate)) {
        deliveryMatrix[customerKey].lastSaleDate = sale.date
        deliveryMatrix[customerKey].isStale = daysSinceSale > 30
      }

      // Process each sale item
      sale.sale_items.forEach(item => {
        const strain = item.product?.strain || 'Unknown'
        const quantity = parseFloat(item.quantity || 0)
        const value = parseFloat(item.sub_total || 0)

        // Add to customer total volume
        customerVolumes[customerKey] += quantity
        deliveryMatrix[customerKey].totalVolume += quantity

        // Add to product total volume
        if (!productVolumes[strain]) {
          productVolumes[strain] = {
            totalVolume: 0,
            lastSaleDate: null,
            isStale: true
          }
        }
        productVolumes[strain].totalVolume += quantity
        
        // Track product's latest sale date and staleness
        if (!productVolumes[strain].lastSaleDate || saleDate > new Date(productVolumes[strain].lastSaleDate)) {
          productVolumes[strain].lastSaleDate = sale.date
          productVolumes[strain].isStale = daysSinceSale > 30
        }

        // Track latest delivery for each customer-product combination
        if (!deliveryMatrix[customerKey].products[strain] || 
            saleDate > parseDateString(deliveryMatrix[customerKey].products[strain].lastDeliveryDate)) {
          
          const daysSinceDelivery = Math.floor((today - saleDate) / (1000 * 60 * 60 * 24))
          
          deliveryMatrix[customerKey].products[strain] = {
            lastDeliveryDate: sale.date,
            daysSince: daysSinceDelivery,
            lastQuantity: quantity,
            lastValue: value
          }
        }
      })
    })

    // Sort customers: active customers by volume (desc), then stale customers by volume (desc)
    const sortedCustomerKeys = Object.keys(deliveryMatrix).sort((a, b) => {
      const customerA = deliveryMatrix[a]
      const customerB = deliveryMatrix[b]
      
      // Fresh customers first, then stale customers
      if (customerA.isStale !== customerB.isStale) {
        return customerA.isStale ? 1 : -1
      }
      
      // Within each group, sort by volume descending
      return customerB.totalVolume - customerA.totalVolume
    })

    // Sort products: active products by volume (desc), then stale products by volume (desc)
    const sortedProductStrains = Object.keys(productVolumes).sort((a, b) => {
      const productA = productVolumes[a]
      const productB = productVolumes[b]
      
      // Fresh products first, then stale products
      if (productA.isStale !== productB.isStale) {
        return productA.isStale ? 1 : -1
      }
      
      // Within each group, sort by volume descending
      return productB.totalVolume - productA.totalVolume
    })

    return {
      deliveryMatrix,
      sortedCustomerKeys,
      sortedProductStrains,
      customerVolumes,
      productVolumes
    }

  } catch (error) {
    console.error('Error fetching customer stocking data:', error)
    return {
      deliveryMatrix: {},
      sortedCustomerKeys: [],
      sortedProductStrains: [],
      customerVolumes: {},
      productVolumes: {}
    }
  }
}

// ============================================================================
// LAST DELIVERED MATRIX (for popover)
// ============================================================================

/**
 * Fetch a lightweight "days since last delivery" matrix.
 * Returns the top 10 customer-branches (by sale count) and top 8 products (by appearance count).
 */
export const getLastDeliveredMatrix = async () => {
  // Fetch active products to filter rows
  const { data: activeProducts, error: prodErr } = await supabase
    .from('products')
    .select('strain')
    .eq('active', true)

  if (prodErr) throw prodErr
  const activeStrains = new Set(activeProducts.map(p => p.strain))

  // Excluded customer types
  const excludedTypes = new Set(['distributor', 'restaurant', 'direct'])

  const { data, error } = await supabase
    .from('sales')
    .select('id, date, customer_id, branch_id, customer:customers(id, name), branch:branches(id, name, customer_type:customer_types(name)), sale_items:sale_items(product:products(strain, active))')
    .in('status', ['delivered', 'invoiced', 'paid'])
    .order('date', { ascending: false })
    .limit(5000)

  if (error) throw error

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  // Count sales per customer-branch and per strain
  const customerCounts = {}
  const customerWeeks = {} // Set of ISO week keys per customer-branch within the 42-day window
  const productCounts = {}
  // Track most recent sale date per (strain, customerKey)
  const latestDates = {}

  const cutoff = new Date(today)
  cutoff.setDate(cutoff.getDate() - 42)

  // Helper: ISO week key from a date (e.g. "2026-W10")
  const isoWeekKey = (d) => {
    const tmp = new Date(d.getTime())
    tmp.setHours(0, 0, 0, 0)
    tmp.setDate(tmp.getDate() + 3 - ((tmp.getDay() + 6) % 7))
    const week1 = new Date(tmp.getFullYear(), 0, 4)
    const weekNum = 1 + Math.round(((tmp - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7)
    return `${tmp.getFullYear()}-W${weekNum}`
  }

  ;(data || []).forEach(sale => {
    // Skip excluded customer types
    const typeName = sale.branch?.customer_type?.name?.toLowerCase()
    if (typeName && excludedTypes.has(typeName)) return

    const custName = sale.customer?.name || 'Unknown'
    const branchName = sale.branch?.name || 'Main'
    const key = branchName.toLowerCase() === 'main' ? custName : `${custName}|${branchName}`

    customerCounts[key] = (customerCounts[key] || 0) + 1

    const saleDate = parseDateString(sale.date)

    // Track distinct weeks within the 42-day window
    if (saleDate >= cutoff) {
      if (!customerWeeks[key]) customerWeeks[key] = new Set()
      customerWeeks[key].add(isoWeekKey(saleDate))
    }

    ;(sale.sale_items || []).forEach(si => {
      const strain = si.product?.strain
      if (!strain || !activeStrains.has(strain)) return
      productCounts[strain] = (productCounts[strain] || 0) + 1

      const mk = `${strain}::${key}`
      if (!latestDates[mk] || saleDate > latestDates[mk]) {
        latestDates[mk] = saleDate
      }
    })
  })

  // Only include customers who ordered in at least 2 of the last 6 weeks
  const MIN_WEEKS = 2

  const eligibleCustomers = Object.entries(customerCounts)
    .filter(([k]) => (customerWeeks[k]?.size || 0) >= MIN_WEEKS)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([k]) => k)

  // Group branches from the same customer together.
  // Sort by the best (highest count) branch of each customer group, then by branch count within.
  const customerName = (key) => key.split('|')[0]
  const grouped = {}
  eligibleCustomers.forEach(k => {
    const name = customerName(k)
    if (!grouped[name]) grouped[name] = []
    grouped[name].push(k)
  })
  // Order groups by their best branch's original rank (preserves eligibleCustomers order)
  const customers = []
  const seen = new Set()
  eligibleCustomers.forEach(k => {
    const name = customerName(k)
    if (seen.has(name)) return
    seen.add(name)
    customers.push(...grouped[name])
  })

  // Top 8 products by appearance count (only active products)
  const products = Object.entries(productCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([k]) => k)

  // Build matrix: { [strain]: { [customerKey]: daysSince | null } }
  const matrix = {}
  products.forEach(strain => {
    matrix[strain] = {}
    customers.forEach(custKey => {
      const d = latestDates[`${strain}::${custKey}`]
      matrix[strain][custKey] = d ? Math.floor((today - d) / (1000 * 60 * 60 * 24)) : null
    })
  })

  return { customers, products, matrix }
}

// ============================================================================
// IMPORT HISTORY FUNCTIONS
// ============================================================================

/**
 * Fetch detailed information about a specific import session
 * Includes session metadata, staging data counts, and duplicate detection stats
 */
export const fetchImportSessionDetail = async (sessionId) => {
  try {
    // Fetch session metadata
    const { data: session, error: sessionError } = await supabase
      .from('import_sessions')
      .select(`
        id,
        user_id,
        file_name,
        file_size,
        total_rows,
        processed_rows,
        valid_rows,
        error_rows,
        entity_type,
        status,
        created_at,
        completed_at,
        updated_at,
        column_mappings,
        import_settings,
        session_state,
        template_id,
        parent_session_id
      `)
      .eq('id', sessionId)
      .single()

    if (sessionError) throw sessionError
    if (!session) return null

    // Fetch staging data counts
    const { count: totalStagingCount, error: stagingCountError } = await supabase
      .from('import_staging_sales')
      .select('id', { count: 'exact', head: true })
      .eq('session_id', sessionId)

    if (stagingCountError) throw stagingCountError

    const { count: importedCount, error: importedCountError } = await supabase
      .from('import_staging_sales')
      .select('id', { count: 'exact', head: true })
      .eq('session_id', sessionId)
      .eq('imported', true)

    if (importedCountError) throw importedCountError

    // Fetch duplicate detection stats
    const { count: duplicateImportedCount, error: dupImportedError } = await supabase
      .from('import_record_history')
      .select('id', { count: 'exact', head: true })
      .eq('session_id', sessionId)
      .eq('action', 'imported')

    if (dupImportedError) throw dupImportedError

    const { count: duplicateDisregardedCount, error: dupDisregardedError } = await supabase
      .from('import_record_history')
      .select('id', { count: 'exact', head: true })
      .eq('session_id', sessionId)
      .eq('action', 'disregarded')

    if (dupDisregardedError) throw dupDisregardedError

    return {
      ...session,
      stagingRecordCount: totalStagingCount || 0,
      importedRecordCount: importedCount || 0,
      duplicateStats: {
        imported: duplicateImportedCount || 0,
        disregarded: duplicateDisregardedCount || 0,
        total: (duplicateImportedCount || 0) + (duplicateDisregardedCount || 0)
      }
    }
  } catch (error) {
    console.error('[fetchImportSessionDetail] Error:', error)
    throw error
  }
}

/**
 * Fetch CSV preview data for an import session
 * Returns original CSV data with import status indicators
 */
export const fetchImportSessionCSVData = async (sessionId, limit = 100, offset = 0) => {
  try {
    // Fetch staging records with original and mapped data
    const { data: stagingRecords, error: stagingError, count } = await supabase
      .from('import_staging_sales')
      .select(`
        id,
        row_number,
        original_data,
        mapped_data,
        validation_status,
        validation_errors,
        imported,
        imported_id,
        created_at
      `, { count: 'exact' })
      .eq('session_id', sessionId)
      .order('row_number')
      .range(offset, offset + limit - 1)

    if (stagingError) throw stagingError

    // Fetch record history to identify duplicates
    const { data: recordHistory, error: historyError } = await supabase
      .from('import_record_history')
      .select('record_fingerprint, action, mapped_data')
      .eq('session_id', sessionId)

    if (historyError) throw historyError

    // Create a map of fingerprints for quick lookup
    const fingerprintMap = new Map()
    if (recordHistory) {
      recordHistory.forEach(record => {
        fingerprintMap.set(record.record_fingerprint, record.action)
      })
    }

    // Enhance records with duplicate detection info
    const enhancedRecords = stagingRecords?.map(record => ({
      ...record,
      isDuplicate: fingerprintMap.size > 0 && record.imported === false,
      duplicateAction: fingerprintMap.get(record.record_fingerprint) || null
    })) || []

    return {
      records: enhancedRecords,
      totalCount: count || 0,
      hasMore: (offset + limit) < (count || 0)
    }
  } catch (error) {
    console.error('[fetchImportSessionCSVData] Error:', error)
    throw error
  }
}

/**
 * Fetch imported sales records for an import session
 * Returns the actual sales records created from this import
 */
export const fetchImportSessionSalesRecords = async (sessionId, limit = 100, offset = 0) => {
  try {
    // First, get the imported IDs from staging table
    const { data: stagingRecords, error: stagingError } = await supabase
      .from('import_staging_sales')
      .select('imported_id')
      .eq('session_id', sessionId)
      .eq('imported', true)
      .not('imported_id', 'is', null)

    if (stagingError) throw stagingError

    const importedIds = stagingRecords?.map(r => r.imported_id).filter(Boolean) || []

    if (importedIds.length === 0) {
      return {
        records: [],
        totalCount: 0,
        hasMore: false
      }
    }

    // Fetch actual sales records with related data
    const { data: salesRecords, error: salesError, count } = await supabase
      .from('sales')
      .select(`
        id,
        date,
        consignment_no,
        status,
        invoiced,
        invoice_id,
        customer_id,
        branch_id,
        customers:customer_id (
          id,
          name,
          type
        ),
        branches:branch_id (
          id,
          name
        ),
        sale_items:sale_items (
          id,
          quantity,
          price,
          discount_percent,
          discount_note,
          sub_total,
          packed,
          products:product_id (
            id,
            strain,
            type
          )
        )
      `, { count: 'exact' })
      .in('id', importedIds)
      .order('date', { ascending: false })
      .range(offset, offset + limit - 1)

    if (salesError) throw salesError

    return {
      records: salesRecords || [],
      totalCount: count || 0,
      hasMore: (offset + limit) < (count || 0)
    }
  } catch (error) {
    console.error('[fetchImportSessionSalesRecords] Error:', error)
    throw error
  }
}

/**
 * Fetch import statistics summary
 * Returns aggregated statistics for display in the import history list
 */
export const fetchImportSessionStatistics = async (sessionId) => {
  try {
    const sessionDetail = await fetchImportSessionDetail(sessionId)

    if (!sessionDetail) {
      return null
    }

    // Calculate derived statistics
    const totalRecords = sessionDetail.stagingRecordCount
    const importedRecords = sessionDetail.importedRecordCount
    const skippedRecords = totalRecords - importedRecords
    const duplicatesDetected = sessionDetail.duplicateStats.disregarded

    return {
      sessionId: sessionDetail.id,
      fileName: sessionDetail.file_name,
      completedAt: sessionDetail.completed_at,
      createdAt: sessionDetail.created_at,
      status: sessionDetail.status,
      totalRecords,
      importedRecords,
      skippedRecords,
      duplicatesDetected,
      validationErrors: sessionDetail.error_rows || 0,
      isReimport: !!sessionDetail.parent_session_id
    }
  } catch (error) {
    console.error('[fetchImportSessionStatistics] Error:', error)
    throw error
  }
}

/**
 * Fetch Xero integration configuration (tenant ID for constructing Xero links)
 * @returns {Promise<Object>} Xero config with tenant_id
 */
export const fetchXeroConfig = async () => {
  const { data, error } = await supabase
    .from('xero_integration_config')
    .select('tenant_id')
    .order('created_at', { ascending: false })
    .limit(1)
    .single()

  if (error) throw error
  return data
}

/**
 * Construct Xero invoice URL for direct link to Xero
 * @param {string} tenantId - Xero tenant/organisation ID
 * @param {string} xeroInvoiceId - Xero invoice ID (GUID)
 * @returns {string} Full URL to invoice in Xero
 */
export const buildXeroInvoiceUrl = (tenantId, xeroInvoiceId) => {
  if (!tenantId || !xeroInvoiceId) return null
  return `https://go.xero.com/organisationlogin/default.aspx?shortcode=${tenantId}&redirecturl=/AccountsReceivable/View.aspx?InvoiceID=${xeroInvoiceId}`
}

/**
 * Check if customer is mapped to a Xero contact
 * @param {string} customerID - Odin customer ID
 * @returns {Promise<boolean>} True if mapped, false otherwise
 */
export const isCustomerMappedToXero = async (customerID) => {
  try {
    // Check if customer has xero_contact_id (customer-level mapping)
    const { data, error } = await supabase
      .from('customers')
      .select('xero_contact_id')
      .eq('id', customerID)
      .single()

    if (error && error.code !== 'PGRST116') {
      throw error
    }

    return !!data?.xero_contact_id
  } catch (error) {
    console.error('[isCustomerMappedToXero] Error:', error)
    return false
  }
}

/**
 * Check if a customer has been mapped to GoCardless
 * A customer is mapped to GoCardless if they have a gocardless_customer_id
 * @param {string} customerID - Customer ID
 * @returns {Promise<boolean>} True if customer is mapped to GoCardless
 */
export const isCustomerMappedToGoCardless = async (customerID) => {
  try {
    const { data, error } = await supabase
      .from('customers')
      .select('gocardless_customer_id')
      .eq('id', customerID)
      .single()

    if (error && error.code !== 'PGRST116') {
      throw error
    }

    return !!data?.gocardless_customer_id
  } catch (error) {
    console.error('[isCustomerMappedToGoCardless] Error:', error)
    return false
  }
}

/**
 * Fetch all Xero contacts for selection with pagination
 * @param {number} page - Page number (1-based)
 * @param {number} pageSize - Number of contacts per page (max 100)
 * @returns {Promise<{contacts: Array, count: number, page: number}>} Paginated contacts
 */
export const fetchXeroContacts = async (page = 1, pageSize = 100) => {
  try {
    const { data, error } = await supabase.functions.invoke('xero-contacts', {
      body: { action: 'list', page, pageSize }
    })

    if (error) {
      console.error('[fetchXeroContacts] Full error object:', JSON.stringify(error, null, 2))
      console.error('[fetchXeroContacts] Error context:', error.context)

      // Try to read the response body
      if (error.context && error.context.json) {
        try {
          const responseBody = await error.context.json()
          console.error('[fetchXeroContacts] Response body:', responseBody)
        } catch (e) {
          console.error('[fetchXeroContacts] Could not parse response body:', e)
        }
      }

      throw error
    }
    return {
      contacts: data.contacts || [],
      count: data.count || 0,
      page: data.page || page
    }
  } catch (error) {
    console.error('[fetchXeroContacts] Caught error:', error)
    console.error('[fetchXeroContacts] Error details:', JSON.stringify(error, Object.getOwnPropertyNames(error), 2))
    throw error
  }
}

/**
 * Search Xero contacts by name or email (server-side search)
 * @param {string} query - Search query string
 * @param {number} page - Page number (1-based)
 * @returns {Promise<{contacts: Array, count: number, query: string}>} Search results
 */
export const searchXeroContacts = async (query, page = 1) => {
  try {
    const { data, error } = await supabase.functions.invoke('xero-contacts', {
      body: { action: 'search', query, page }
    })

    if (error) throw error
    return {
      contacts: data.contacts || [],
      count: data.count || 0,
      query: data.query || query
    }
  } catch (error) {
    console.error('[searchXeroContacts] Error:', error)
    throw error
  }
}

/**
 * Link customer to existing Xero contact
 * @param {string} customerID - Odin customer ID
 * @param {string} xeroContactID - Xero contact ID (GUID)
 * @param {string} contactName - Xero contact name (for reference)
 * @returns {Promise<Object>} Created mapping record
 */
export const linkCustomerToXeroContact = async (customerID, xeroContactID, contactName) => {
  try {
    // Check if mapping already exists (handle .single() errors gracefully)
    const { data: existing, error: checkError } = await supabase
      .from('xero_customer_mappings')
      .select('id')
      .eq('odin_customer_id', customerID)
      .single()

    // .single() returns 406 when no rows found or 400+ on other errors - treat as "not found"
    const mappingExists = !checkError && existing

    let mappingData
    if (mappingExists) {
      // Update existing mapping
      const { data, error } = await supabase
        .from('xero_customer_mappings')
        .update({
          xero_contact_id: xeroContactID,
          xero_contact_name: contactName,
          mapping_type: 'manual',
          is_manual_override: true
        })
        .eq('id', existing.id)
        .select()
        .single()

      if (error) throw error
      mappingData = data
    } else {
      // Create new mapping
      const { data, error } = await supabase
        .from('xero_customer_mappings')
        .insert({
          odin_customer_id: customerID,
          xero_contact_id: xeroContactID,
          xero_contact_name: contactName,
          mapping_type: 'manual',
          is_manual_override: true
        })
        .select()
        .single()

      if (error) throw error
      mappingData = data
    }

    // CRITICAL: Update the denormalized column in customers table for quick lookups
    // This ensures sync functions can quickly check if customer is mapped to Xero
    const { error: updateError } = await supabase
      .from('customers')
      .update({ xero_contact_id: xeroContactID })
      .eq('id', customerID)

    if (updateError) {
      console.error('[linkCustomerToXeroContact] Failed to update customers.xero_contact_id:', updateError)
      // Don't throw - the mapping was created successfully, just log the denormalization issue
    }

    return mappingData
  } catch (error) {
    console.error('[linkCustomerToXeroContact] Error:', error)
    throw error
  }
}

/**
 * Create new contact in Xero and link to customer
 * Edge Function fetches customer details from database and creates both contact and mapping
 * @param {string} customerID - Odin customer ID
 * @param {Object} contactData - Contact information (ignored - Edge Function uses database values)
 * @returns {Promise<Object>} Created mapping record from Edge Function
 */
export const createXeroContactAndLink = async (customerID, contactData) => {
  try {
    // Edge Function creates contact in Xero AND mapping in database automatically
    const { data: xeroData, error: xeroError } = await supabase.functions.invoke('xero-contacts', {
      body: {
        action: 'create',
        odinCustomerId: customerID
      }
    })

    if (xeroError) throw xeroError
    if (!xeroData?.success) {
      throw new Error('Failed to create contact in Xero')
    }

    // Return the mapping created by Edge Function
    return xeroData.mapping
  } catch (error) {
    console.error('[createXeroContactAndLink] Error:', error)
    throw error
  }
}

/**
 * GoCardless Functions
 * Functions for interacting with GoCardless payment system
 */

/**
 * Fetch the next pending GoCardless payout
 * @returns {Promise<Object>} { amount, arrival_date, currency } or { payout: null }
 */
export const fetchNextGoCardlessPayout = async () => {
  const { data, error } = await supabase.functions.invoke('gocardless-next-payout')
  if (error) throw error
  return data
}

/**
 * Retry a failed GoCardless payment
 *
 * @param {string} paymentId - The ID of the failed payment in the payments table
 * @returns {Promise<Object>} Result with success status and new payment ID
 * @throws {Error} If payment retry fails
 */
export const retryGoCardlessPayment = async (paymentId) => {
  try {
    console.log('[retryGoCardlessPayment] Retrying payment:', paymentId)

    // Fetch the failed payment with invoice details
    const { data: payment, error: fetchError } = await supabase
      .from('payments')
      .select('*, invoice:invoices(invoice_no)')
      .eq('id', paymentId)
      .single()

    if (fetchError) throw fetchError
    if (!payment) {
      throw new Error('Payment not found')
    }

    // Validate payment is eligible for retry
    if (payment.source !== 'gocardless') {
      throw new Error('Only GoCardless payments can be retried through this function')
    }

    if (payment.gocardless_payment_status !== 'failed') {
      throw new Error('Only failed payments can be retried')
    }

    // Call the gocardless-payments Edge Function to create a new payment
    const { data: retryData, error: retryError } = await supabase.functions.invoke('gocardless-payments', {
      body: {
        action: 'create_payment',
        invoiceId: payment.invoice_id,
        customerId: payment.customer_id,
        amount: parseFloat(payment.amount),
        invoiceNumber: payment.invoice?.invoice_no || 'Unknown'
      }
    })

    if (retryError) throw retryError
    if (!retryData?.success) {
      throw new Error(retryData?.error || 'Failed to retry payment')
    }

    console.log('[retryGoCardlessPayment] Payment retried successfully:', retryData.paymentId)
    return retryData
  } catch (error) {
    console.error('[retryGoCardlessPayment] Error:', error)
    throw error
  }
}

/**
 * Enhanced Invoice Review Functions (Task 12)
 * Functions for creating draft invoices, authorising, and payment marking
 */

/**
 * Create draft invoice in Xero with advanced controls
 *
 * @param {Object} invoiceData - Invoice creation data
 * @param {string} invoiceData.customer_id - Odin customer ID
 * @param {string} [invoiceData.branch_id] - Odin branch ID (optional)
 * @param {string} invoiceData.payment_method - Payment method (cash, gocardless, etc.)
 * @param {Object} [invoiceData.discount] - Discount configuration
 * @param {string} invoiceData.discount.type - Discount type ('percentage' or 'fixed')
 * @param {number} invoiceData.discount.amount - Discount amount
 * @param {number} [invoiceData.credit_note_applied] - Credit note amount to apply
 * @param {string[]} [invoiceData.excluded_transactions] - Sale IDs to exclude
 * @param {Array} [invoiceData.line_item_adjustments] - Line item price adjustments
 * @returns {Promise<Object>} Result with invoice details
 * @throws {Error} If invoice creation fails
 */
export const createDraftInvoice = async (invoiceData) => {
  try {
    console.log('[createDraftInvoice] Creating draft invoice:', {
      customer_id: invoiceData.customer_id,
      branch_id: invoiceData.branch_id,
      payment_method: invoiceData.payment_method
    })

    const { data, error } = await supabase.functions.invoke('xero-invoices', {
      body: {
        action: 'create-draft',
        ...invoiceData
      }
    })

    if (error) throw error
    if (!data?.success) {
      throw new Error(data?.error || 'Failed to create draft invoice')
    }

    console.log('[createDraftInvoice] Draft invoice created:', data.invoice_id)
    return data
  } catch (error) {
    console.error('[createDraftInvoice] Error:', error)
    throw error
  }
}

/**
 * Authorise draft invoice and send via email in Xero
 *
 * @param {string} invoiceId - Xero invoice ID
 * @param {string} emailTo - Recipient email address
 * @returns {Promise<Object>} Result with success status
 * @throws {Error} If authorisation or sending fails
 */
export const authoriseAndSendInvoice = async (invoiceId, emailTo) => {
  try {
    console.log('[authoriseAndSendInvoice] Authorising and sending invoice:', {
      invoice_id: invoiceId,
      email_to: emailTo
    })

    const { data, error } = await supabase.functions.invoke('xero-invoices', {
      body: {
        action: 'authorise-and-send',
        invoice_id: invoiceId,
        email_to: emailTo
      }
    })

    if (error) throw error
    if (!data?.success) {
      throw new Error(data?.error || 'Failed to authorise and send invoice')
    }

    console.log('[authoriseAndSendInvoice] Invoice authorised and sent')
    return data
  } catch (error) {
    console.error('[authoriseAndSendInvoice] Error:', error)
    throw error
  }
}

/**
 * Authorise and send multiple draft invoices in batch
 * @param {string[]} invoiceIds - Array of invoice IDs (max 20)
 * @returns {Promise<Object>} Batch results with success/failure breakdown
 */
export const authoriseAndSendBatch = async (invoiceIds) => {
  try {
    console.log('[authoriseAndSendBatch] Authorising and sending batch:', {
      count: invoiceIds.length,
      invoice_ids: invoiceIds
    })

    const { data, error } = await supabase.functions.invoke('xero-invoices', {
      body: {
        action: 'authorise_and_send_batch',
        invoiceIds: invoiceIds
      }
    })

    if (error) throw error
    if (!data?.success) {
      throw new Error(data?.error || 'Failed to authorise and send batch')
    }

    console.log('[authoriseAndSendBatch] Batch processed:', data.summary)
    return data
  } catch (error) {
    console.error('[authoriseAndSendBatch] Error:', error)
    throw error
  }
}

/**
 * Revert a draft invoice (undo draft creation)
 * Returns sales to uninvoiced status and deletes invoice from Xero and Odin
 * @param {string} invoiceId - Invoice ID to revert
 * @returns {Promise<Object>} Result with sales reverted count
 */
export const revertDraftInvoice = async (invoiceId) => {
  try {
    console.log('[revertDraftInvoice] Reverting draft invoice:', invoiceId)

    const { data, error } = await supabase.functions.invoke('xero-invoices', {
      body: {
        action: 'revert_draft_invoice',
        invoiceId: invoiceId
      }
    })

    if (error) throw error
    if (!data?.success) {
      throw new Error(data?.message || data?.error || 'Failed to revert draft invoice')
    }

    console.log('[revertDraftInvoice] Draft invoice reverted:', data)
    return data
  } catch (error) {
    console.error('[revertDraftInvoice] Error:', error)
    throw error
  }
}

/**
 * Retry sending a failed invoice email
 * Enforces 3-attempt limit per invoice
 * @param {string} invoiceId - Invoice ID to retry
 * @returns {Promise<Object>} Result with attempt count
 */
export const retryInvoiceEmail = async (invoiceId) => {
  try {
    console.log('[retryInvoiceEmail] Retrying email send:', invoiceId)

    const { data, error } = await supabase.functions.invoke('xero-invoices', {
      body: {
        action: 'retry_email_send',
        invoiceId: invoiceId
      }
    })

    if (error) throw error
    if (!data?.success) {
      throw new Error(data?.message || data?.error || 'Failed to retry email send')
    }

    console.log('[retryInvoiceEmail] Email retry completed:', data)
    return data
  } catch (error) {
    console.error('[retryInvoiceEmail] Error:', error)
    throw error
  }
}

/**
 * List unmatched GoCardless customers
 * Returns customers in GoCardless who are not yet matched to an Odin customer
 *
 * @returns {Promise<Array>} Array of GoCardless customers with mandate details
 * @throws {Error} If fetch fails
 */
export const listUnmatchedGoCardlessCustomers = async () => {
  try {
    console.log('[listUnmatchedGoCardlessCustomers] Fetching unmatched customers')

    const { data, error } = await supabase.functions.invoke('gocardless-customers', {
      body: {
        action: 'list_unmatched'
      }
    })

    if (error) throw error
    if (!data?.success) {
      throw new Error(data?.error || 'Failed to fetch unmatched customers')
    }

    console.log('[listUnmatchedGoCardlessCustomers] Found customers:', data.customers?.length || 0)
    return data.customers || []
  } catch (error) {
    console.error('[listUnmatchedGoCardlessCustomers] Error:', error)
    throw error
  }
}

/**
 * Match Odin customer to GoCardless customer
 *
 * @param {string} odinCustomerId - Odin customer ID
 * @param {string} gocardlessCustomerId - GoCardless customer ID
 * @returns {Promise<Object>} Result with customer and mandate details
 * @throws {Error} If matching fails
 */
export const matchGoCardlessCustomer = async (odinCustomerId, gocardlessCustomerId) => {
  try {
    console.log('[matchGoCardlessCustomer] Matching customer:', {
      odin_customer_id: odinCustomerId,
      gocardless_customer_id: gocardlessCustomerId
    })

    const { data, error } = await supabase.functions.invoke('gocardless-customers', {
      body: {
        action: 'match_customer',
        odin_customer_id: odinCustomerId,
        gocardless_customer_id: gocardlessCustomerId
      }
    })

    if (error) throw error
    if (!data?.success) {
      throw new Error(data?.error || 'Failed to match customer')
    }

    console.log('[matchGoCardlessCustomer] Customer matched successfully')
    return data
  } catch (error) {
    console.error('[matchGoCardlessCustomer] Error:', error)
    throw error
  }
}

/**
 * Record payment against an invoice
 *
 * Creates a payment record in the payments table and updates invoice status.
 * Supports full and partial payments for local (non-Xero) invoices.
 *
 * @param {string} invoiceId - Odin invoice ID
 * @param {Object} paymentData - Payment details
 * @param {string} paymentData.payment_date - Payment date (YYYY-MM-DD)
 * @param {number} paymentData.amount - Payment amount
 * @param {string} [paymentData.reference] - Payment reference (optional)
 * @param {string} [paymentData.method] - Payment method: 'Bank Transfer', 'Card', 'Cash', 'GoCardless'
 * @param {boolean} [paymentData.is_legacy_paid] - Whether this is a legacy paid invoice
 * @returns {Promise<Object>} Result with payment details including invoice status
 * @throws {Error} If payment recording fails
 */
export const markInvoiceAsPaid = async (invoiceId, paymentData) => {
  try {
    console.log('[markInvoiceAsPaid] Recording payment:', {
      invoice_id: invoiceId,
      amount: paymentData.amount,
      date: paymentData.payment_date,
      method: paymentData.method || 'Bank Transfer'
    })

    const { data, error } = await supabase.functions.invoke('xero-payments', {
      body: {
        action: 'mark_paid',
        invoice_id: invoiceId,
        payment_date: paymentData.payment_date,
        amount: paymentData.amount,
        reference: paymentData.reference || '',
        method: paymentData.method || 'Bank Transfer',
        is_legacy_paid: paymentData.is_legacy_paid || false,
        account_code: '970' // Cash account (for Xero if needed)
      }
    })

    if (error) throw error
    if (!data?.success) {
      throw new Error(data?.message || data?.error || 'Failed to record payment')
    }

    console.log('[markInvoiceAsPaid] Payment recorded:', {
      payment_id: data.payment_id,
      invoice_paid: data.invoice_paid,
      status: data.invoice_status
    })
    return data
  } catch (error) {
    console.error('[markInvoiceAsPaid] Error:', error)
    throw error
  }
}

// ===================================================================
// GoCardless Mandate Mapping Functions
// ===================================================================

/**
 * Get all GoCardless customer mappings with fuzzy suggestions
 *
 * @param {boolean} includeCustomerDetails - Whether to include customer details
 * @param {boolean} includeSuggestions - Whether to include fuzzy match suggestions
 * @returns {Promise<Object>} Mappings, unmapped customers, suggestions, and summary
 * @throws {Error} If fetch fails
 */
export const getGoCardlessMappings = async (includeCustomerDetails = true, includeSuggestions = true) => {
  try {
    console.log('[getGoCardlessMappings] Fetching mappings')

    const { data, error } = await supabase.functions.invoke('gocardless-customers', {
      body: {
        action: 'get-mappings',
        includeCustomerDetails,
        includeSuggestions
      }
    })

    if (error) throw error
    if (!data?.success) {
      throw new Error(data?.error || 'Failed to fetch mappings')
    }

    console.log('[getGoCardlessMappings] Fetched:', {
      mappings: data.mappings?.length || 0,
      unmapped: data.unmapped?.length || 0,
      suggestions: Object.keys(data.suggestions || {}).length
    })

    return data
  } catch (error) {
    console.error('[getGoCardlessMappings] Error:', error)
    throw error
  }
}

/**
 * Link Odin customer to GoCardless customer/mandate
 *
 * @param {string} odinCustomerId - Odin customer ID
 * @param {string} gcCustomerId - GoCardless customer ID
 * @param {string} mandateId - GoCardless mandate ID
 * @param {string} mappingType - 'manual' or 'auto_matched'
 * @param {number} confidenceScore - Confidence score (0-1) for auto matches
 * @returns {Promise<Object>} Created mapping object
 * @throws {Error} If link fails
 */
export const linkGoCardlessMandate = async (
  odinCustomerId,
  gcCustomerId,
  mandateId,
  mappingType = 'manual',
  confidenceScore = null
) => {
  try {
    console.log('[linkGoCardlessMandate] Linking customer:', {
      odinCustomerId,
      gcCustomerId,
      mandateId,
      mappingType
    })

    const { data: { session } } = await supabase.auth.getSession()
    const userId = session?.user?.id

    const { data, error } = await supabase.functions.invoke('gocardless-customers', {
      body: {
        action: 'link-customer',
        odinCustomerId,
        gcCustomerId,
        mandateId,
        mappingType,
        confidenceScore,
        userId
      }
    })

    if (error) throw error
    if (!data?.success) {
      throw new Error(data?.error || 'Failed to link customer')
    }

    console.log('[linkGoCardlessMandate] Customer linked successfully')
    return data.mapping
  } catch (error) {
    console.error('[linkGoCardlessMandate] Error:', error)
    throw error
  }
}

/**
 * Unlink Odin customer from GoCardless mandate
 *
 * @param {string} odinCustomerId - Odin customer ID
 * @returns {Promise<Object>} Result with deleted mapping ID
 * @throws {Error} If unlink fails
 */
export const unlinkGoCardlessMandate = async (odinCustomerId) => {
  try {
    console.log('[unlinkGoCardlessMandate] Unlinking customer:', odinCustomerId)

    const { data, error } = await supabase.functions.invoke('gocardless-customers', {
      body: {
        action: 'unlink-customer',
        odinCustomerId
      }
    })

    if (error) throw error
    if (!data?.success) {
      throw new Error(data?.error || 'Failed to unlink customer')
    }

    console.log('[unlinkGoCardlessMandate] Customer unlinked successfully')
    return data
  } catch (error) {
    console.error('[unlinkGoCardlessMandate] Error:', error)
    throw error
  }
}

/**
 * Search GoCardless customers by name or email
 *
 * @param {string} query - Search query (name or email)
 * @param {string[]} mandateStatusFilter - Optional array of mandate statuses to filter by
 * @returns {Promise<Object[]>} Array of GoCardless customers with mandate details
 * @throws {Error} If search fails
 */
export const searchGoCardlessCustomers = async (query, mandateStatusFilter = null) => {
  try {
    if (!query || query.length < 2) {
      throw new Error('Query must be at least 2 characters')
    }

    console.log('[searchGoCardlessCustomers] Searching:', query)

    const { data, error} = await supabase.functions.invoke('gocardless-customers', {
      body: {
        action: 'search-customers',
        query,
        mandateStatusFilter
      }
    })

    if (error) throw error
    if (!data?.success) {
      throw new Error(data?.error || 'Search failed')
    }

    console.log('[searchGoCardlessCustomers] Found:', data.customers?.length || 0, 'customers')
    return data.customers || []
  } catch (error) {
    console.error('[searchGoCardlessCustomers] Error:', error)
    throw error
  }
}

// ============================================================================
// LOCATION MANAGEMENT
// ============================================================================

/**
 * Fetch all locations from the database
 * @param {Object} options - Filter options
 * @param {string} [options.locationType] - Filter by type ('room', 'area', 'zone', 'equipment')
 * @param {boolean} [options.includeInactive=false] - Include inactive locations
 * @returns {Promise<Object[]>} Array of location objects
 */
export const fetchLocations = async (options = {}) => {
  const { locationType, includeInactive = false } = options

  let query = supabase
    .from('locations')
    .select('*')
    .order('sort_order', { ascending: true })

  if (!includeInactive) {
    query = query.eq('is_active', true)
  }

  if (locationType) {
    query = query.eq('location_type', locationType)
  }

  const { data, error } = await query

  if (error) {
    console.error('[fetchLocations] Error:', error)
    throw error
  }

  return data || []
}

/**
 * Create a new location
 * @param {Object} locationData - Location data
 * @param {string} locationData.code - Unique location code (e.g., 'fruiting_3')
 * @param {string} locationData.name - Display name (e.g., 'Fruiting Room 3')
 * @param {string} [locationData.location_type='room'] - Type: 'room', 'area', 'zone', 'equipment'
 * @param {string} [locationData.expected_status] - Expected container status in this location
 * @param {string} [locationData.description] - Optional description
 * @param {number} [locationData.capacity] - Optional max containers
 * @param {number} [locationData.sort_order=0] - Display order
 * @returns {Promise<Object>} Created location
 */
export const createLocation = async (locationData) => {
  const { data, error } = await supabase
    .from('locations')
    .insert({
      code: locationData.code,
      name: locationData.name,
      location_type: locationData.location_type || 'room',
      expected_status: locationData.expected_status || null,
      description: locationData.description || null,
      capacity: locationData.capacity || null,
      sort_order: locationData.sort_order || 0,
      is_active: true
    })
    .select()
    .single()

  if (error) {
    console.error('[createLocation] Error:', error)
    throw error
  }

  return data
}

/**
 * Update an existing location
 * @param {string} id - Location UUID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated location
 */
export const updateLocation = async (id, updates) => {
  const { data, error } = await supabase
    .from('locations')
    .update({
      ...updates,
      updated_at: new Date().toISOString()
    })
    .eq('id', id)
    .select()
    .single()

  if (error) {
    console.error('[updateLocation] Error:', error)
    throw error
  }

  return data
}

/**
 * Delete a location (hard delete)
 * @param {string} id - Location UUID
 * @returns {Promise<void>}
 */
export const deleteLocation = async (id) => {
  const { error } = await supabase
    .from('locations')
    .delete()
    .eq('id', id)

  if (error) {
    console.error('[deleteLocation] Error:', error)
    throw error
  }
}

/**
 * Toggle location active status (soft delete/restore)
 * @param {string} id - Location UUID
 * @param {boolean} isActive - New active status
 * @returns {Promise<Object>} Updated location
 */
export const toggleLocationActive = async (id, isActive) => {
  return updateLocation(id, { is_active: isActive })
}

/**
 * Update sort order for multiple locations (for drag-to-reorder)
 * @param {Array<{id: string, sort_order: number}>} updates - Array of id/sort_order pairs
 * @returns {Promise<void>}
 */
export const updateLocationSortOrder = async (updates) => {
  // Use a transaction-like approach with individual updates
  const promises = updates.map(({ id, sort_order }) =>
    supabase
      .from('locations')
      .update({ sort_order, updated_at: new Date().toISOString() })
      .eq('id', id)
  )

  const results = await Promise.all(promises)

  const errors = results.filter(r => r.error)
  if (errors.length > 0) {
    console.error('[updateLocationSortOrder] Errors:', errors)
    throw new Error('Failed to update sort order for some locations')
  }
}

// Customer Detail View Functions

/**
 * Fetch aggregated statistics for a customer
 * @param {string} customerId - Customer UUID
 * @returns {Promise<Object>} { totalSales, orderCount, avgOrderValue, lastOrderDate, balance }
 */
export const fetchCustomerStats = async (customerId) => {
  try {
    // Fetch sales with items to calculate totals (sales table has no total_amount column)
    const { data: salesData, error: salesError } = await supabase
      .from('sales')
      .select('id, date, sale_items(sub_total)')
      .eq('customer_id', customerId)
      .not('status', 'eq', 'cancelled')

    if (salesError) throw salesError

    // Calculate aggregates - total comes from sum of sale_items.sub_total
    const orderCount = salesData?.length || 0
    const totalSales = salesData?.reduce((sum, sale) => {
      const saleTotal = sale.sale_items?.reduce((itemSum, item) => itemSum + (parseFloat(item.sub_total) || 0), 0) || 0
      return sum + saleTotal
    }, 0) || 0
    const avgOrderValue = orderCount > 0 ? totalSales / orderCount : 0
    const lastOrderDate = salesData?.length > 0
      ? salesData.reduce((latest, sale) => {
          const saleDate = parseDateString(sale.date)
          return saleDate > latest ? saleDate : latest
        }, parseDateString(salesData[0].date))
      : null

    // Fetch balance from invoice_balances view
    const { data: balances, error: balanceError } = await supabase
      .from('invoice_balances')
      .select('amount_due')
      .eq('customer_id', customerId)

    if (balanceError) throw balanceError

    const balance = balances?.reduce((sum, inv) => sum + (parseFloat(inv.amount_due) || 0), 0) || 0

    return {
      totalSales,
      orderCount,
      avgOrderValue,
      lastOrderDate: lastOrderDate ? lastOrderDate.toISOString() : null,
      balance
    }
  } catch (error) {
    console.error('[fetchCustomerStats] Error:', error)
    throw error
  }
}

/**
 * Fetch active recurring orders for a customer
 * @param {string} customerId - Customer UUID
 * @returns {Promise<Array>} Recurring orders with items and next delivery date
 */
export const fetchCustomerRecurringOrders = async (customerId) => {
  try {
    const { data, error } = await supabase
      .from('recurring_orders')
      .select(`
        *,
        recurring_order_items (
          id,
          product_id,
          product_type_id,
          quantity,
          products (
            id,
            strain
          ),
          product_types (
            id,
            name
          )
        ),
        recurring_order_instances!inner (
          id,
          scheduled_date,
          status
        )
      `)
      .eq('customer_id', customerId)
      .eq('active', true)
      .order('created_at', { ascending: false })

    if (error) throw error

    // Process each recurring order to find next delivery date
    const ordersWithNextDate = data?.map(order => {
      // Find next pending instance
      const nextInstance = order.recurring_order_instances
        ?.filter(inst => inst.status === 'pending' && parseDateString(inst.scheduled_date) >= new Date(new Date().setHours(0,0,0,0)))
        .sort((a, b) => parseDateString(a.scheduled_date) - parseDateString(b.scheduled_date))[0]

      return {
        ...order,
        next_delivery_date: nextInstance?.scheduled_date || null
      }
    }) || []

    return ordersWithNextDate
  } catch (error) {
    console.error('[fetchCustomerRecurringOrders] Error:', error)
    throw error
  }
}

/**
 * Fetch paginated sales history for a customer
 * @param {string} customerId - Customer UUID
 * @param {number} limit - Number of sales to fetch (default: 10)
 * @param {number} offset - Offset for pagination (default: 0)
 * @returns {Promise<Object>} { sales, hasMore, total }
 */
export const fetchCustomerSalesHistory = async (customerId, limit = 10, offset = 0) => {
  try {
    // Get total count
    const { count, error: countError } = await supabase
      .from('sales')
      .select('*', { count: 'exact', head: true })
      .eq('customer_id', customerId)
      .not('status', 'eq', 'cancelled')

    if (countError) throw countError

    // Fetch paginated sales with items (total calculated from sale_items.sub_total)
    const { data: sales, error: salesError } = await supabase
      .from('sales')
      .select(`
        id,
        date,
        status,
        consignment_no,
        invoice_id,
        legacy_invoice_no,
        branch_id,
        branches (
          id,
          name
        ),
        invoices (
          invoice_no
        ),
        sale_items (
          id,
          item_number,
          quantity,
          price,
          discount_percent,
          discount_note,
          sub_total,
          product_id,
          product_type_id,
          block_format_id,
          products (
            strain
          ),
          product_type:product_types (
            name
          ),
          block_format:block_formats (
            id, name, weight_kg
          ),
          growing_material:growing_materials (
            id, name, category, unit_label, reference_code
          )
        )
      `)
      .eq('customer_id', customerId)
      .not('status', 'eq', 'cancelled')
      .order('date', { ascending: false })
      .range(offset, offset + limit - 1)

    if (salesError) throw salesError

    return {
      sales: sales || [],
      hasMore: (offset + limit) < count,
      total: count
    }
  } catch (error) {
    console.error('[fetchCustomerSalesHistory] Error:', error)
    throw error
  }
}

export const fetchCustomerPaymentHistory = async (customerId, limit = 100, offset = 0) => {
  try {
    // Count total invoices for this customer
    const { count, error: countError } = await supabase
      .from('invoice_balances')
      .select('*', { count: 'exact', head: true })
      .eq('customer_id', customerId)

    if (countError) throw countError

    // Fetch invoice balances with branch info
    const { data: invoices, error } = await supabase
      .from('invoice_balances')
      .select(`
        invoice_id,
        invoice_no,
        customer_id,
        branch_id,
        date,
        invoice_total,
        amount_paid,
        amount_credited,
        amount_due,
        calculated_status,
        is_legacy_paid,
        status,
        paid,
        xero_invoice_id
      `)
      .eq('customer_id', customerId)
      .order('date', { ascending: false })
      .range(offset, offset + limit - 1)

    if (error) throw error

    // Fetch branch names for display
    const branchIds = [...new Set(invoices?.map(i => i.branch_id).filter(Boolean))]
    let branchMap = {}
    if (branchIds.length > 0) {
      const { data: branches } = await supabase
        .from('branches')
        .select('id, name')
        .in('id', branchIds)
      branchMap = Object.fromEntries((branches || []).map(b => [b.id, b.name]))
    }

    const enriched = (invoices || []).map(inv => ({
      ...inv,
      branch_name: branchMap[inv.branch_id] || null
    }))

    return {
      invoices: enriched,
      hasMore: (offset + limit) < count,
      total: count
    }
  } catch (error) {
    console.error('[fetchCustomerPaymentHistory] Error:', error)
    throw error
  }
}

// =============================================================================
// TIMELINE QUERY FUNCTIONS (Sales Record Timeline Evolution)
// =============================================================================

/**
 * Fetch future timeline items (unconfirmed recurring instances and AI suggestions)
 *
 * @param {string} fromDate - Start date (YYYY-MM-DD format)
 * @param {number} days - Number of days to fetch forward
 * @returns {Promise<Array>} Array of TimelineItem objects sorted ascending by date
 */
export const fetchTimelineFuture = async (fromDate, days) => {
  const toDate = parseDateString(fromDate)
  toDate.setDate(toDate.getDate() + days)
  const toDateStr = toLocalDateString(toDate)

  // Fetch pending recurring order instances
  const { data: instances, error: instancesError } = await supabase
    .from('recurring_order_instances')
    .select(`
      *,
      recurring_order:recurring_orders(
        id,
        customer_id,
        branch_id,
        days_of_week,
        end_type,
        end_date,
        max_occurrences,
        customers(id, name),
        branches(id, name),
        items:recurring_order_items(
          id,
          product_id,
          product_type_id,
          quantity,
          products(id, strain, base_price_per_kg),
          product_types(id, name, price_multiplier)
        )
      )
    `)
    .eq('status', 'pending')
    .gte('scheduled_date', fromDate)
    .lt('scheduled_date', toDateStr)
    .order('scheduled_date', { ascending: true })

  if (instancesError) {
    console.error('[fetchTimelineFuture] Error fetching recurring instances:', instancesError)
    throw instancesError
  }

  // Fetch pending AI suggestions (no product_type - suggestions only store product_id)
  const { data: suggestions, error: suggestionsError } = await supabase
    .from('sale_suggestions')
    .select(`
      *,
      customer:customers(id, name),
      branch:branches(id, name),
      product:products(id, strain)
    `)
    .eq('status', 'pending')
    .gte('suggested_date', fromDate)
    .lt('suggested_date', toDateStr)
    .order('suggested_date', { ascending: true })

  if (suggestionsError) {
    console.error('[fetchTimelineFuture] Error fetching suggestions:', suggestionsError)
    throw suggestionsError
  }

  // Fetch confirmed/pending sales in the future window (lightweight)
  const futureSales = await fetchSalesLightweight(fromDate, toDateStr)

  // Transform future sales to TimelineItems (lightweight)
  const futureSaleItems = (futureSales || []).map(transformLightweightToTimelineItem)

  // Build a set of date-customer-branch keys from confirmed sales
  const saleKeys = new Set()
  futureSaleItems.forEach(item => {
    saleKeys.add(`${item.date}-${item.customer?.id}-${item.branch?.id || 'null'}`)
  })

  // Filter out recurring instances where a sale already exists for that customer+date+branch,
  // and merge duplicate instances for the same customer+date+branch into a single row
  const allInstanceItems = (instances || []).map(instance =>
    transformToTimelineItem(instance, 'unconfirmed-recurring')
  )
  const instancesByKey = new Map()
  allInstanceItems.forEach(item => {
    const key = `${item.date}-${item.customer?.id}-${item.branch?.id || 'null'}`
    if (saleKeys.has(key)) return // sale already exists, skip
    if (instancesByKey.has(key)) {
      // Merge items from duplicate instance into the first one
      const existing = instancesByKey.get(key)
      existing.items = [...existing.items, ...item.items]
      existing.total = Math.round((existing.total + item.total) * 100) / 100
    } else {
      instancesByKey.set(key, { ...item })
    }
  })
  const instanceItems = Array.from(instancesByKey.values())

  // Build combined covered keys (sales + remaining instances) for suggestion filtering
  const coveredKeys = new Set(saleKeys)
  instanceItems.forEach(item => {
    coveredKeys.add(`${item.date}-${item.customer?.id}-${item.branch?.id || 'null'}`)
  })

  // Group suggestions by date+customer+branch, filter out those already covered, then transform
  const suggestionGroups = groupSuggestionsByDateCustomerBranch(suggestions || [])
  const filteredGroups = suggestionGroups.filter(group => {
    const key = `${group.date}-${group.customerId}-${group.branchId || 'null'}`
    return !coveredKeys.has(key)
  })
  const suggestionItems = filteredGroups.map(group =>
    transformToTimelineItem(group, 'unconfirmed-suggestion')
  )

  // Merge and sort ascending by date
  return mergeTimelineItems([...instanceItems, ...suggestionItems, ...futureSaleItems])
}

/**
 * Fetch past timeline items (confirmed sales)
 *
 * @param {string} toDate - End date (YYYY-MM-DD format, exclusive)
 * @param {number} days - Number of days to fetch backward from toDate
 * @returns {Promise<Array>} Array of TimelineItem objects sorted descending by date
 */
export const fetchTimelinePast = async (toDate, days) => {
  const fromDate = parseDateString(toDate)
  fromDate.setDate(fromDate.getDate() - days)
  const fromDateStr = toLocalDateString(fromDate)

  // Fetch sales using existing function
  const sales = await fetchSalesWithDateRange(fromDateStr, toDate)

  // Transform to TimelineItems (checking for recurring_order_id)
  const timelineItems = (sales || []).map(sale => {
    // Determine type based on whether sale has recurring_order_id
    const type = sale.recurring_order_id ? 'committed-recurring' : 'confirmed'
    return transformToTimelineItem(sale, type)
  })

  // Sort descending by date (most recent first)
  return timelineItems.sort((a, b) => b.date.localeCompare(a.date))
}

/**
 * Transform a source record (sale, recurring instance, or suggestion group) to TimelineItem format
 *
 * @param {Object} source - Source record from database
 * @param {string} type - Timeline item type: 'confirmed', 'unconfirmed-recurring', 'unconfirmed-suggestion', 'committed-recurring'
 * @returns {Object} Unified TimelineItem object
 */
export const transformToTimelineItem = (source, type) => {
  switch (type) {
    case 'unconfirmed-recurring': {
      // Source is a recurring_order_instance with nested recurring_order
      const order = source.recurring_order
      // Calculate items with estimated prices from base_price * multiplier
      const itemsWithPrices = (order.items || []).map(item => {
        const basePrice = item.products?.base_price_per_kg || 0
        const multiplier = item.product_types?.price_multiplier || 1.0
        const price = Math.round(basePrice * multiplier * 100) / 100
        const quantity = parseFloat(item.quantity)
        const subtotal = Math.round(price * quantity * 100) / 100
        return {
          id: item.id,
          productId: item.product_id,
          productTypeId: item.product_type_id,
          strain: item.products?.strain || 'Unknown',
          productTypeName: item.product_types?.name || 'Unknown',
          quantity,
          price,
          subtotal
        }
      })
      // Sum up total from all item subtotals
      const total = itemsWithPrices.reduce((sum, item) => sum + item.subtotal, 0)
      return {
        id: `recurring-${source.id}`,
        type: 'unconfirmed-recurring',
        sourceId: source.id,
        date: source.scheduled_date,
        customer: {
          id: order.customers?.id || order.customer_id,
          name: order.customers?.name || 'Unknown Customer'
        },
        branch: {
          id: order.branches?.id || order.branch_id,
          name: order.branches?.name || 'Unknown Branch'
        },
        items: itemsWithPrices,
        total: Math.round(total * 100) / 100,
        status: null,
        consignmentNo: null,
        invoiceNo: null,
        recurringOrderId: order.id,
        // Scheduling fields for editing
        daysOfWeek: order.days_of_week || [],
        endType: order.end_type || 'indefinite',
        endDate: order.end_date || null,
        maxOccurrences: order.max_occurrences || null
      }
    }

    case 'unconfirmed-suggestion': {
      // Source is a grouped set of suggestions for same date+customer+branch
      return {
        id: `suggestion-${source.groupKey}`,
        type: 'unconfirmed-suggestion',
        sourceId: source.groupKey,
        date: source.date,
        customer: {
          id: source.customerId,
          name: source.customerName
        },
        branch: {
          id: source.branchId,
          name: source.branchName
        },
        items: source.suggestions.map(s => ({
          id: s.id,
          productId: s.product_id,
          productTypeId: null, // Not stored in suggestions
          strain: s.product?.strain || 'Unknown',
          productTypeName: null, // Not stored in suggestions
          quantity: parseFloat(s.suggested_quantity),
          price: s.suggested_price ? parseFloat(s.suggested_price) : 0,
          subtotal: s.suggested_price ? parseFloat(s.suggested_quantity) * parseFloat(s.suggested_price) : 0
        })),
        total: source.suggestions.reduce((sum, s) =>
          sum + (s.suggested_price ? parseFloat(s.suggested_quantity) * parseFloat(s.suggested_price) : 0),
          0
        ),
        status: null,
        consignmentNo: null,
        invoiceNo: null,
        recurringOrderId: null
      }
    }

    case 'confirmed':
    case 'committed-recurring': {
      // Source is a sale record from fetchSalesWithDateRange
      return {
        id: `sale-${source.id}`,
        type,
        sourceId: source.id,
        date: source.date,
        customer: {
          id: source.customer_id,
          name: source.customer?.name || 'Unknown Customer'
        },
        branch: {
          id: source.branch_id,
          name: source.branch?.name || 'Unknown Branch'
        },
        items: (source.sale_items || []).map(item => {
          const isGm = !!item.growing_material_id
          return {
            id: item.id,
            productId: item.product_id,
            productTypeId: item.product_type_id,
            blockFormatId: item.block_format_id || null,
            blockFormat: item.block_format || null,
            growingMaterialId: item.growing_material_id || null,
            growingMaterial: item.growing_material || null,
            strain: isGm
              ? (item.growing_material?.product?.strain || item.growing_material?.name || 'Growing Material')
              : (item.product?.strain || 'Unknown'),
            productTypeName: isGm
              ? (item.growing_material?.unit_label || '')
              : (item.block_format_id
                ? `Block (${item.block_format?.name || 'Unknown'})`
                : (item.product_type?.name || 'Unknown')),
            quantity: parseFloat(item.quantity),
            price: parseFloat(item.price),
            discountPercent: parseFloat(item.discount_percent || 0),
            discountNote: item.discount_note || null,
            subtotal: parseFloat(item.sub_total),
            itemNumber: item.item_number || null
          }
        }),
        total: (source.sale_items || []).reduce((sum, item) => sum + parseFloat(item.sub_total), 0),
        status: source.status,
        consignmentNo: source.consignment_no,
        invoiceNo: source.invoices?.[0]?.invoice_no || null,
        recurringOrderId: source.recurring_order_id || null
      }
    }

    default:
      throw new Error(`Unknown timeline item type: ${type}`)
  }
}

/**
 * Group sale suggestions by date + customer + branch
 * This allows multiple product suggestions for the same delivery to be shown as one timeline row
 *
 * @param {Array} suggestions - Array of sale_suggestions records
 * @returns {Array} Grouped suggestions with group metadata
 */
const groupSuggestionsByDateCustomerBranch = (suggestions) => {
  const groups = {}

  suggestions.forEach(suggestion => {
    const groupKey = `${suggestion.suggested_date}-${suggestion.customer_id}-${suggestion.branch_id || 'null'}`

    if (!groups[groupKey]) {
      groups[groupKey] = {
        groupKey,
        date: suggestion.suggested_date,
        customerId: suggestion.customer_id,
        customerName: suggestion.customer?.name || 'Unknown',
        branchId: suggestion.branch_id,
        branchName: suggestion.branch?.name || 'Unknown',
        suggestions: []
      }
    }

    groups[groupKey].suggestions.push(suggestion)
  })

  return Object.values(groups)
}

/**
 * Merge and sort timeline items
 * Removes duplicates and sorts by date
 *
 * @param {Array} items - Array of TimelineItem objects
 * @returns {Array} Sorted and deduped TimelineItem array
 */
export const mergeTimelineItems = (items) => {
  // Deduplicate by ID (shouldn't have dupes but defensive)
  const uniqueItems = {}
  items.forEach(item => {
    uniqueItems[item.id] = item
  })

  // Convert back to array and sort ascending by date (for future items)
  return Object.values(uniqueItems).sort((a, b) => a.date.localeCompare(b.date))
}

// ============================================================
// Portal Registration & Invitation Functions
// ============================================================

/**
 * Fetch all portal registration requests
 */
export const fetchRegistrationRequests = async () => {
  const { data, error } = await supabase
    .from('portal_registration_requests')
    .select('*')
    .order('created_at', { ascending: false })

  if (error) throw error
  return data || []
}

/**
 * Send invitation to a new customer from registration request
 */
export const inviteRegistrationRequest = async (requestId, customerName, priceTierId = null, deliveryDays = []) => {
  const { data, error } = await supabase.functions.invoke('portal-registration', {
    body: {
      action: 'invite',
      request_id: requestId,
      customer_name: customerName,
      price_tier_id: priceTierId,
      delivery_days: deliveryDays,
    },
  })

  if (error) throw error
  return data
}

/**
 * Reject a registration request
 */
export const rejectRegistrationRequest = async (requestId) => {
  const { data, error } = await supabase.functions.invoke('portal-registration', {
    body: { action: 'reject', request_id: requestId },
  })

  if (error) throw error
  return data
}

/**
 * Invite an existing customer to the portal
 */
export const inviteExistingCustomerToPortal = async (customerId, email, displayName) => {
  const { data, error } = await supabase.functions.invoke('portal-registration', {
    body: {
      action: 'invite_existing',
      customer_id: customerId,
      email: email,
      display_name: displayName,
    },
  })

  if (error) throw error
  return data
}

// ============================================================
// Portal Registration v2 — onboarding contract with portal team
// Pending requests = status='interest_submitted'. Accept / reject
// both route through the portal's portal-registration edge function
// with reviewed_from='odin' so the audit trail records the surface
// that acted.
// ============================================================

export const fetchPendingRegistrations = async () => {
  const { data, error } = await supabase
    .from('portal_registration_requests')
    .select('id, business_name, contact_name, email, phone, message, created_at')
    .eq('status', 'interest_submitted')
    .order('created_at', { ascending: true })

  if (error) throw error
  return data || []
}

export const subscribeToPendingRegistrations = (onChange) => {
  const channel = supabase
    .channel('portal_registration_requests:pending')
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'portal_registration_requests' },
      () => onChange()
    )
    .subscribe()
  return () => supabase.removeChannel(channel)
}

export const approveRegistration = async (requestId) => {
  const { data, error } = await supabase.functions.invoke('portal-registration', {
    body: {
      action: 'approve_registration',
      request_id: requestId,
      reviewed_from: 'odin',
    },
  })
  if (error) throw error
  return data
}

export const rejectRegistration = async (requestId, reason) => {
  const { data, error } = await supabase.functions.invoke('portal-registration', {
    body: {
      action: 'reject_application',
      request_id: requestId,
      reason: reason || null,
      reviewed_from: 'odin',
    },
  })
  if (error) throw error
  return data
}

/**
 * Fetch portal users for a customer
 */
export const fetchPortalUsers = async (customerId) => {
  const { data, error } = await supabase
    .from('portal_users')
    .select('*')
    .eq('customer_id', customerId)
    .order('created_at', { ascending: false })

  if (error) throw error
  return data || []
}

export default supabase